// @ts-check
/* ── STATE + CONSTANTS + UTIL HELPERS ── moved to assets/state.js */



/* ── INIT ── */
async function init() {
  // First paint is served from a small precomputed summary (data/summary.json)
  // plus the lightweight index (data/offers-index.json). This avoids
  // downloading + parsing the ~21MB of raw per-city offers and aggregating
  // ~28k records on the main thread just to render the default view.
  //
  // The raw offers (loadOffersPayload) are loaded LAZILY via ensureRawOffers()
  // — only when the user does something the summary can't answer (filters,
  // bill changes, wallet/compare views, detail drill-ins). At default scope
  // the summary is byte-identical to computeRecommendations()/
  // computeRestaurantDeals(), so the swap to the compute path after the first
  // lazy load is seamless and never changes ranking output.
  let summaryLoaded = false;
  try {
    const loaded = await loadSummary();
    if (loaded && loaded.summary && loaded.index) {
      state.index = loaded.index;
      state.summary = loaded.summary;
      summaryLoaded = true;
    }
  } catch (err) {
    console.warn("[summary] unavailable, falling back to raw offers", err);
  }

  // Requirements context is small and needed by the compute/eligibility paths.
  // Load it alongside whichever data path we take.
  const requirementsPromise = loadRequirementsContext();
  // Category manifest (data/categories.json) is tiny and best-effort: without
  // it the app is exactly the food-only app it was before.
  const manifestPromise = loadCategoryManifest();

  if (!summaryLoaded) {
    // Fallback: no usable summary → behave exactly like before (load raw
    // offers up front, then render).
    await ensureRawOffers();
  }

  state.requirements = await requirementsPromise;
  state.categoryManifest = await manifestPromise;
  seedMonthlySpend();

  // Slug pre-filters (?restaurant=, ?bank=) resolve their canonical names
  // against state.data.offers inside restoreStateFromUrl(). If such params are
  // present we must have raw offers loaded BEFORE restore, or the slug lookup
  // silently no-ops and the deep link loses its filter.
  if (summaryLoaded && !state.data && hasPendingSlugParams()) {
    await ensureRawOffers();
  }

  // Order matters: defaults (from state.js) → localStorage → URL.
  // URL takes the last word so shared links keep working.
  restoreStateFromLocal();
  restoreStateFromUrl();
  // SEO deep links (?restaurant=, ?bank=) point into the food dataset.
  if (hasPendingSlugParams()) state.category = "food";
  activateRestoredCategory();

  // Any non-default state restored from localStorage/URL (filters, bill,
  // eligibility, wallet/compare view) can't be served from the summary —
  // pull raw offers in before the first render so nothing renders against
  // null data.
  if ((summaryLoaded || !isFoodCategory()) && !state.data && !isDefaultScope() && !isAggregateView()) {
    await ensureRawOffers();
  }

  bindEvents();
  renderCategoryBar();
  applyCategoryCopy();
  syncDomToState();
  renderDataFreshness();
  renderFavoritesAlert();
  render();
  // ~1 KB: which banks run 0% plans, for the BNPL badge on card rows.
  ensureBnplSummary()
    .then(() => { if (!isAggregateView()) renderRecommendations(); })
    .catch(() => {});
}

// Deep-link slug params that resolve against per-offer data (and so require
// raw offers loaded before restoreStateFromUrl runs).
function hasPendingSlugParams() {
  try {
    const params = new URLSearchParams(location.search);
    return params.has("restaurant") || params.has("bank");
  } catch (_) {
    return false;
  }
}

/* ── DATA FRESHNESS ──
   The offers payload carries a single dataset-level generatedAt timestamp
   (no per-offer timestamps in the schema today). We surface this in two
   places: a calm "verified Xd ago" line in the footer, and a stale-data
   banner at the top if the data is more than 60 days old. */
// Metadata source for the app shell/sidebar. Prefers the full raw payload
// once it's loaded, otherwise the lightweight index loaded at first paint.
// Both expose the same meta fields (cities, dayNames, stats, generatedAt,
// restaurantsByCity).
function getMeta() {
  return state.data || state.index || null;
}

function getDataFreshnessDaysAgo() {
  const ts = getMeta()?.generatedAt;
  if (!ts) return null;
  const then = new Date(ts);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, days);
}

function formatDaysAgo(d) {
  if (d === null || d === undefined) return "";
  if (d === 0) return "today";
  if (d === 1) return "1 day ago";
  if (d < 30) return `${d} days ago`;
  if (d < 60) return `~${Math.round(d / 7)} weeks ago`;
  return `~${Math.round(d / 30)} months ago`;
}

function buildReportMailto(bank, card) {
  const subject = `Offer correction: ${bank || ""} — ${card || ""}`.trim();
  const body = [
    `Bank: ${bank || ""}`,
    `Card: ${card || ""}`,
    `URL: ${typeof location !== "undefined" ? location.href : ""}`,
    "",
    "What's incorrect about this card or its offers? (e.g., discount %, cap, days, eligibility):",
    "",
  ].join("\n");
  return `mailto:faizan@konsacard.pk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/* ── FAVORITES + NEW-OFFER ALERTS ──
   Users can star restaurants. On every boot we snapshot which offers exist
   at each favorite restaurant, store that snapshot, then on the next boot
   diff current vs stored. New offers → in-app banner inviting them to
   open the restaurant detail. No backend, no push permission needed —
   just a localStorage diff at visit time. */
const FAVS_SNAPSHOT_KEY = "konsacard_fav_offers_v1";

function toggleFavoriteRestaurant(name) {
  if (state.favoriteRestaurants.has(name)) state.favoriteRestaurants.delete(name);
  else state.favoriteRestaurants.add(name);
  saveStateToLocal();
  trackEvent("favorite_toggle", { count: state.favoriteRestaurants.size });
}

function buildFavoritesSnapshot() {
  // Snapshot = { restaurantName: [list of "bank||card" offer keys] }
  const snap = {};
  if (!state.data?.offers) return snap;
  state.favoriteRestaurants.forEach((name) => snap[name] = []);
  state.data.offers.forEach((o) => {
    if (!state.favoriteRestaurants.has(o.restaurant)) return;
    snap[o.restaurant].push(`${o.bank}||${o.card}`);
  });
  Object.keys(snap).forEach((k) => { snap[k] = [...new Set(snap[k])].sort(); });
  return snap;
}

function detectNewOffersForFavorites() {
  let stored = {};
  try { stored = JSON.parse(localStorage.getItem(FAVS_SNAPSHOT_KEY) || "{}"); } catch (_) {}
  const current = buildFavoritesSnapshot();
  /** @type {{restaurant: string, added: string[]}[]} */
  const newOffers = [];
  for (const r of Object.keys(current)) {
    const before = new Set(stored[r] || []);
    const added = current[r].filter((k) => !before.has(k));
    if (added.length > 0) newOffers.push({ restaurant: r, added });
  }
  // Save the new snapshot for next visit
  try { localStorage.setItem(FAVS_SNAPSHOT_KEY, JSON.stringify(current)); } catch (_) {}
  return newOffers;
}

function renderFavoritesAlert() {
  if (state.favoriteRestaurants.size === 0) return;
  // The stored snapshot is of food offers; diffing it against another
  // category's payload would report every favorite as changed.
  if (!isFoodCategory()) return;
  // The new-offer diff is computed against per-offer data (state.data.offers).
  // If raw offers aren't loaded yet, lazy-load them in the background and run
  // the check once they arrive — never block first paint for this. Crucially,
  // we must NOT run buildFavoritesSnapshot against null data: it would seed an
  // empty snapshot and wipe the user's stored baseline.
  if (!state.data) {
    ensureRawOffers().then(() => renderFavoritesAlert()).catch(() => {});
    return;
  }
  // Only check after first visit (avoid alerting a brand-new user about
  // every offer on their first favorite). buildFavoritesSnapshot itself
  // doesn't gate this — we check whether stored has any keys.
  let stored = null;
  try { stored = localStorage.getItem(FAVS_SNAPSHOT_KEY); } catch (_) {}
  if (state.favoriteRestaurants.size === 0) return;
  const newOffers = detectNewOffersForFavorites();
  // Skip on first-ever check (nothing stored before) — we just seeded the snapshot.
  if (!stored) return;
  if (newOffers.length === 0) return;
  const banner = document.getElementById("favs-alert");
  if (!banner) return;
  const total = newOffers.reduce((sum, r) => sum + r.added.length, 0);
  const names = newOffers.slice(0, 3).map((r) => r.restaurant).join(", ");
  const more = newOffers.length > 3 ? ` and ${newOffers.length - 3} more` : "";
  banner.innerHTML = `
    <span class="favs-alert-icon">${phIcon("bell-ringing")}</span>
    <span><strong>${total} new offer${total === 1 ? "" : "s"}</strong> at your favorites: ${escapeHtml(names)}${escapeHtml(more)}.</span>
    <button class="favs-alert-close" id="favs-alert-close" type="button" aria-label="Dismiss">×</button>
  `;
  banner.classList.remove("hidden");
  document.getElementById("favs-alert-close")?.addEventListener("click", () => banner.classList.add("hidden"));
  trackEvent("favorites_new_offers_shown", { restaurants: newOffers.length, total });
}

function renderDataFreshness() {
  const days = getDataFreshnessDaysAgo();
  if (days === null) return;
  const label = formatDaysAgo(days);
  const footer = document.getElementById("footer-freshness");
  if (footer) footer.textContent = ` Data last verified ${label}.`;

  // Top-of-page always-visible chip. Hidden once data is stale enough
  // that the louder stale-banner shows instead (>60 days).
  const chip = document.getElementById("freshness-chip");
  if (chip) {
    if (days > 60) {
      chip.classList.add("hidden");
      chip.innerHTML = "";
    } else {
      chip.classList.remove("hidden");
      chip.classList.toggle("is-aging", days > 30);
      const prefix = days > 30 ? "Offers verified" : "Offers verified";
      chip.innerHTML = `
        <span class="freshness-chip-dot" aria-hidden="true"></span>
        <span>${escapeHtml(prefix)} <strong>${escapeHtml(label)}</strong></span>
      `;
    }
  }

  const banner = document.getElementById("stale-banner");
  if (banner) {
    if (days > 60) {
      banner.classList.remove("hidden");
      banner.innerHTML = `
        <span class="stale-banner-icon">⏳</span>
        <span>Our offers data was last verified <strong>${escapeHtml(label)}</strong>. Some deals may have changed — always confirm with your bank.</span>
      `;
    } else {
      banner.classList.add("hidden");
      banner.innerHTML = "";
    }
  }
}

async function loadOffersPayload() {
  let payload;
  try {
    const index = await fetchJson("./data/offers-index.json");
    if (!index || !index.cityFiles || !Array.isArray(index.cities)) {
      throw new Error("offers-index.json missing cityFiles or cities");
    }
    // Fetch all city files + the optional restaurants enrichment in
    // parallel. The enrichment file (descriptions, branches, social,
    // cuisine, opening hours) is treated as best-effort: if it's missing
    // we still render fine, just without the richer UI.
    const cityFetches = index.cities.map((city) => {
      const url = index.cityFiles[city];
      if (!url) return Promise.resolve({ offers: [] });
      return fetchJson(url).catch((err) => {
        console.warn(`[offers] failed to load ${url}:`, err);
        return { offers: [] };
      });
    });
    const restaurantsFetch = index.restaurantsFile
      ? fetchJson(index.restaurantsFile).catch((err) => {
          console.warn(`[offers] failed to load ${index.restaurantsFile}:`, err);
          return null;
        })
      : Promise.resolve(null);
    // Inferred cuisines: LLM-tagged taxonomy for the ~40% of restaurants
    // Peekaboo doesn't cover (NBP-only chains like KFC, Easypaisa merchants,
    // independent local restaurants). Treated as best-effort — silently
    // skipped if absent. Peekaboo enrichment always wins on conflicts since
    // it's the authoritative source.
    const inferredFetch = fetchJson("./data/inferred_cuisines.json").catch(() => null);
    const [cityPayloads, restaurantsPayload, inferredPayload] = await Promise.all([
      Promise.all(cityFetches),
      restaurantsFetch,
      inferredFetch,
    ]);
    const offers = cityPayloads.flatMap((p) => (Array.isArray(p?.offers) ? p.offers : []));
    payload = { ...index, offers };
    if (restaurantsPayload && restaurantsPayload.restaurants) {
      payload.restaurants = restaurantsPayload.restaurants;
    }
    // Merge inferred cuisine data: only fill gaps Peekaboo didn't cover, so
    // a restaurant present in both keeps its full Peekaboo record (description,
    // branches, photos, etc.) instead of being overwritten with a thin
    // {servesCuisine} stub.
    if (inferredPayload?.restaurants) {
      payload.restaurants = payload.restaurants || {};
      for (const [name, cuisines] of Object.entries(inferredPayload.restaurants)) {
        if (!Array.isArray(cuisines) || cuisines.length === 0) continue;
        if (!payload.restaurants[name]) {
          payload.restaurants[name] = { servesCuisine: cuisines };
        }
      }
    }
  } catch (err) {
    console.warn("[offers] split payload unavailable, falling back to offers.json", err);
    payload = await fetchJson("./data/offers.json");
  }
  validateOffersPayload(payload);
  return payload;
}

/* ── CATEGORIES ──
   data/categories.json lists every offer category (Food, Health, Shopping…)
   with its payload file, live stats, default bill/frequency and copy nouns.
   Food is the core dataset loaded via the index + per-city shards above; any
   other category is a single payload in the SAME shape, so ranking, wallet,
   the places list and the tag filter all run on it unchanged once it's
   installed as state.data. Categories with no offers yet are hidden. */
async function loadCategoryManifest() {
  try {
    const manifest = await fetchJson("./data/categories.json");
    if (!manifest || !Array.isArray(manifest.categories)) return null;
    return manifest;
  } catch (err) {
    console.warn("[categories] manifest unavailable, food only", err);
    return null;
  }
}

async function loadCategoryPayload(slug) {
  const meta = getCategoryMeta(slug);
  if (!meta || !meta.file) throw new Error(`category ${slug} has no data file`);
  const sep = meta.file.includes("?") ? "&" : "?";
  const version = meta.generatedAt ? `${sep}v=${encodeURIComponent(meta.generatedAt)}` : "";
  const payload = await fetchJson(`${meta.file}${version}`);
  validateOffersPayload(payload);
  return payload;
}

function isCategoryVisible(slug) {
  return getVisibleCategories().some((c) => c.slug === slug);
}

// Called once after local/URL restore. Falls back to food if the restored
// category no longer exists (renamed, or emptied by the pipeline), then
// applies that category's remembered or default bill + frequency.
function activateRestoredCategory() {
  if (!isCategoryVisible(state.category)) state.category = "food";
  if (isFoodCategory()) return;
  const meta = getActiveCategory();
  const prefs = state.categoryPrefs[state.category] || {};
  if (!state._urlCategoryBill) state.orderValue = Number.isFinite(prefs.orderValue) ? prefs.orderValue : getCategoryDefaultBill(meta);
  if (!state._urlCategoryFreq) state.outingsPerWeek = Number.isFinite(prefs.outingsPerWeek) ? prefs.outingsPerWeek : getCategoryDefaultFrequency(meta);
  delete state._urlCategoryBill;
  delete state._urlCategoryFreq;
  state.data = _categoryData[state.category] || null;
}

// Stash the active category's bill + frequency so switching away and back
// keeps what the user set.
function rememberCategoryPrefs() {
  state.categoryPrefs[state.category || "food"] = {
    orderValue: state.orderValue,
    outingsPerWeek: state.outingsPerWeek,
  };
}

function setCategory(slug) {
  const meta = getCategoryMeta(slug);
  if (!meta) return;
  if (isAggregateView()) {
    // Leaving Overall for a category: land on its ranked cards.
    state.viewMode = "cards";
    if (slug === (state.category || "food")) {
      renderCategoryBar();
      applyCategoryCopy();
      syncBillControls();
      renderForView();
      return;
    }
  }
  if (slug === (state.category || "food")) return;
  rememberCategoryPrefs();
  state.category = slug;
  const prefs = state.categoryPrefs[slug] || {};
  state.orderValue = Number.isFinite(prefs.orderValue) ? prefs.orderValue : getCategoryDefaultBill(meta);
  state.outingsPerWeek = Number.isFinite(prefs.outingsPerWeek) ? prefs.outingsPerWeek : getCategoryDefaultFrequency(meta);
  // Merchant names and tags belong to one category; card/bank/day filters
  // carry over because they mean the same thing everywhere.
  state.selectedRestaurants = new Set();
  state.selectedCuisines = new Set();
  state.restSearchTerm = "";
  state.cuisineSearchTerm = "";
  state.detailRestaurantKey = null;
  Object.keys(state.pagination).forEach((k) => { state.pagination[k] = 1; });
  const restSearch = document.getElementById("restaurant-search");
  if (restSearch) restSearch.value = "";
  state.data = _categoryData[slug] || null;
  trackEvent("category_change", { category: slug });
  renderCategoryBar();
  applyCategoryCopy();
  syncBillControls();
  render();
  window.scrollTo({ top: 0, behavior: "auto" });
}

function renderCategoryBar() {
  const bar = document.getElementById("category-bar");
  if (!bar) return;
  const cats = getVisibleCategories();
  if (cats.length < 2) { bar.hidden = true; bar.innerHTML = ""; return; }
  bar.hidden = false;
  const overall = state.viewMode === "overall";
  const active = isAggregateView() ? null : (state.category || "food");
  const overallChip = `<button type="button" role="tab" class="cat-tab cat-tab--overall${overall ? " active" : ""}" data-overall="1" aria-selected="${overall ? "true" : "false"}">`
    + `${categoryIconHtml("overall", "cat-icon")}Overall</button><span class="cat-bar-sep" aria-hidden="true"></span>`;
  bar.innerHTML = overallChip + cats.map((c) => {
    const isActive = c.slug === active;
    return `<button type="button" role="tab" class="cat-tab${isActive ? " active" : ""}" data-category="${escapeAttr(c.slug)}" aria-selected="${isActive ? "true" : "false"}">`
      + `${categoryIconHtml(c.slug, "cat-icon")}${escapeHtml(c.label)}</button>`;
  }).join("");
  bar.querySelectorAll(".cat-tab[data-category]").forEach((btn) => {
    btn.addEventListener("click", () => setCategory(btn.dataset.category));
  });
  bar.querySelector(".cat-tab[data-overall]")?.addEventListener("click", () => openOverallView());
  const activeBtn = bar.querySelector(".cat-tab.active");
  if (activeBtn && typeof activeBtn.scrollIntoView === "function" && bar.scrollWidth > bar.clientWidth) {
    activeBtn.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
}

// Static copy in index.html that names the venue type. Elements opt in with
// data-cat-copy="<key>"; render-time strings call venueNoun()/tagNoun()
// directly.
function applyCategoryCopy() {
  const food = isFoodCategory();
  const many = venueNoun(undefined, true);
  const tags = tagNoun(true, true);
  const copy = {
    "venues": many,
    "venues-search": `Search ${venueNoun()}…`,
    "venues-matched": `${venueNoun()} matched`,
    "tags": tags,
    "tags-search": `Search ${tagNoun(true)}…`,
    "bill": food ? "Typical Bill" : "Typical Spend",
    "frequency": food ? "Times / week" : "Times / month",
    "days": food ? "Going-Out Days" : "Shopping Days",
    "window-visit": `/${visitWord()}`,
  };
  document.querySelectorAll("[data-cat-copy]").forEach((el) => {
    const key = el.getAttribute("data-cat-copy");
    if (key && copy[key] !== undefined) el.textContent = copy[key];
  });
  document.querySelectorAll("[data-cat-placeholder]").forEach((el) => {
    const key = el.getAttribute("data-cat-placeholder");
    if (key && copy[key] !== undefined) el.setAttribute("placeholder", copy[key]);
  });
  // Bill presets are restaurant personas — only meaningful for food.
  const presets = document.getElementById("persona-presets");
  if (presets) presets.hidden = !food;
  document.body.setAttribute("data-category", state.category || "food");
}

// Slider range follows the category: a 1K–50K meal range is wrong for
// electronics or hotels.
function syncBillControls() {
  const slider = document.getElementById("order-value");
  if (!slider) return;
  const meta = getActiveCategory();
  const max = Math.max(50000, Math.ceil((getCategoryDefaultBill(meta) * 3) / 10000) * 10000);
  slider.max = String(max);
  slider.step = max > 50000 ? "1000" : "500";
  slider.value = String(state.orderValue);
  const ends = slider.parentElement?.querySelector(".range-ends");
  if (ends) ends.innerHTML = `<span>1K</span><span>${Math.round(max / 1000)}K</span>`;
}

/* ── OVERALL VIEW ──
   "Which card is best for everything I spend on?" Cards ranked across all
   categories by the user's monthly spend in each, from the precomputed
   data/overall-rates.json (34 KB gzipped). No raw offers are needed, so this
   view is fast even on a phone. Model: lib/overall-core.mjs. */
function sanitizeMonthlySpend(obj) {
  const out = {};
  for (const [slug, v] of Object.entries(obj || {})) {
    const n = Math.round(Number(v));
    if (slug && Number.isFinite(n) && n >= 0) out[slug] = Math.min(n, 10000000);
  }
  return out;
}

function seedMonthlySpend() {
  if (Object.keys(state.monthlySpend || {}).length > 0) return;
  const out = {};
  (state.categoryManifest?.categories || []).forEach((c) => {
    const v = Number(c.defaultMonthlySpend);
    if (Number.isFinite(v)) out[c.slug] = v;
  });
  state.monthlySpend = out;
}

let _overallRatesPromise = null;
function ensureOverallRates() {
  if (state.overallRates) return Promise.resolve(state.overallRates);
  if (!_overallRatesPromise) {
    const v = state.categoryManifest?.generatedAt ? `?v=${encodeURIComponent(state.categoryManifest.generatedAt)}` : "";
    _overallRatesPromise = fetchJson(`./data/overall-rates.json${v}`)
      .then((rates) => { state.overallRates = rates; return rates; })
      .catch((err) => { _overallRatesPromise = null; throw err; });
  }
  return _overallRatesPromise;
}

function openOverallView() {
  closeAllModals();
  rememberCategoryPrefs();
  state.viewMode = "overall";
  state.overallShown = 20;
  trackEvent("view_change", { view: "overall" });
  renderCategoryBar();
  render();
  window.scrollTo({ top: 0, behavior: "auto" });
}

/* ── BNPL badge: 0% instalment plans on listed products ──
   Bank Alfalah and MCB let credit card holders pay for listed products in
   monthly instalments at 0% markup. The products live on their own page,
   /bnpl/ (assets/bnpl.js); here we only badge the cards that qualify, from
   data/bnpl-summary.json (~1 KB, built by scripts/instalments/). */

// Views that replace the card list and don't use per-offer data.
function isAggregateView() {
  return state.viewMode === "overall";
}

let _bnplSummaryPromise = null;
function ensureBnplSummary() {
  if (state.bnplSummary) return Promise.resolve(state.bnplSummary);
  if (!_bnplSummaryPromise) {
    _bnplSummaryPromise = fetchJson("./data/bnpl-summary.json")
      .then((summary) => { state.bnplSummary = summary; return summary; })
      .catch((err) => { _bnplSummaryPromise = null; throw err; });
  }
  return _bnplSummaryPromise;
}

// The bank's 0% programme if this card can use it: a credit card from a bank
// that runs one, and not a card the bank excludes (e.g. Islamic, corporate).
function bnplPlanForCard(bank, card) {
  const plan = (state.bnplSummary?.banks || []).find((b) => b.bank === bank);
  if (!plan || !/credit/i.test(card)) return null;
  if (plan.exclude && new RegExp(plan.exclude, "i").test(card)) return null;
  return plan;
}

function renderBnplBadge(bank, card) {
  const plan = bnplPlanForCard(bank, card);
  if (!plan || !plan.longestMonths) return "";
  return `<a class="badge-bnpl" href="/bnpl/?bank=${encodeURIComponent(plan.id)}" title="Pay for ${plan.brands} brands' products in 0% instalments">BNPL 0% · up to ${plan.longestMonths} months</a>`;
}

// The badge sits inside a clickable card row; let the link navigate without
// also opening the card's details.
document.addEventListener("click", (e) => {
  const badge = e.target.closest?.(".badge-bnpl");
  if (!badge) return;
  e.stopPropagation();
  trackEvent("bnpl_badge_click", { href: badge.getAttribute("href") });
}, true);

// Categories the Overall view can price: visible in the manifest AND present
// in the rates file.
function getOverallCategories() {
  const inRates = state.overallRates?.categories || {};
  return getVisibleCategories().filter((c) => inRates[c.slug]);
}

function renderOverallSpendPanel(cats) {
  const total = cats.reduce((sum, c) => sum + (Number(state.monthlySpend[c.slug]) || 0), 0);
  // Only categories with spend get an input; the rest sit behind "+ Add"
  // chips so the list of results isn't pushed off-screen on a phone.
  const active = cats.filter((c) => (Number(state.monthlySpend[c.slug]) || 0) > 0);
  const inactive = cats.filter((c) => !((Number(state.monthlySpend[c.slug]) || 0) > 0));
  const addChips = inactive.length
    ? `<div class="ov-add"><span class="ov-add-l">Add:</span>${inactive.map((c) => `<button type="button" class="ov-add-chip" data-add-spend="${escapeAttr(c.slug)}">+ ${escapeHtml(c.label)}</button>`).join("")}</div>`
    : "";
  const rows = active.map((c) => {
    const v = Number(state.monthlySpend[c.slug]) || 0;
    return `<label class="ov-spend">
        <span class="ov-spend-name">${categoryIconHtml(c.slug)} ${escapeHtml(c.label)}</span>
        <span class="ov-spend-field"><span class="ov-spend-cur">PKR</span><input type="number" inputmode="numeric" min="0" step="1000" value="${v}" data-spend="${escapeAttr(c.slug)}" aria-label="${escapeAttr(c.label)} spend per month" /></span>
      </label>`;
  }).join("");
  // Collapsed by default everywhere so the ranked cards are the first thing
  // people see; the summary line shows the assumed mix so the ranking still
  // makes sense. The user's own toggle wins for the rest of the session.
  const open = state.overallPanelOpen ?? false;
  const shortK = (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v));
  const byAmount = [...active].sort((a, b) => (Number(state.monthlySpend[b.slug]) || 0) - (Number(state.monthlySpend[a.slug]) || 0));
  const mixText = byAmount.slice(0, 3).map((c) => `${c.label} ${shortK(Number(state.monthlySpend[c.slug]) || 0)}`).join(" · ")
    + (byAmount.length > 3 ? ` +${byAmount.length - 3} more` : "");
  return `
    <details class="ov-panel" id="ov-panel"${open ? " open" : ""}>
      <summary class="ov-panel-summary">
        <span class="ov-panel-title">Your monthly spending</span>
        <span class="ov-panel-total">${formatCurrency(total)}/mo</span>
        ${mixText ? `<span class="ov-panel-mix">${escapeHtml(mixText)}</span>` : ""}
        <span class="ov-panel-edit">${open ? "Done" : "Edit"}</span>
      </summary>
      <div class="ov-panel-head">
        <div>
          <div class="ov-panel-sub">Across ${active.length} ${active.length === 1 ? "category" : "categories"}. Set one to 0 to remove it.</div>
        </div>
        <button type="button" class="ov-reset" id="ov-reset">Reset to typical</button>
      </div>
      <div class="ov-spend-grid">${rows}</div>
      ${addChips}
      <p class="ov-how">How it's worked out: in each category we take the card's average discount at the places that accept it and the share of places in ${escapeHtml(state.selectedCity === "all" ? "your cities" : formatCityLabel(state.selectedCity))} that do, apply that to what you spend there, then subtract the annual fee. It's an estimate of what you'd save if you used this one card everywhere.</p>
    </details>`;
}

function renderOverallRow(result, rank, catsBySlug) {
  const cardKey = buildCardKey(result.bank, result.card);
  const showEligibility = isEligibilityContextActive();
  const feeText = result.annualFee === null ? "Fee unknown" : result.annualFee === 0 ? "No annual fee" : `${formatCurrency(result.annualFee)} annual fee`;
  const segs = result.byCategory.map((b, i) => {
    const pct = result.yearlySaving > 0 ? (b.yearlySaving / result.yearlySaving) * 100 : 0;
    return `<span class="ov-seg ov-seg-${i % 6}" style="width:${pct.toFixed(2)}%" title="${escapeAttr((catsBySlug[b.slug]?.label || b.slug) + ": " + formatCurrency(b.yearlySaving) + "/yr")}"></span>`;
  }).join("");
  const chips = result.byCategory.slice(0, 4).map((b, i) => {
    const c = catsBySlug[b.slug];
    return `<button type="button" class="ov-chip" data-ov-cat="${escapeAttr(b.slug)}" data-ov-card="${escapeAttr(result.card)}" title="See ${escapeAttr(result.card)} deals in ${escapeAttr(c?.label || b.slug)}">
        <span class="ov-dot ov-seg-${i % 6}" aria-hidden="true"></span>${escapeHtml(c?.label || b.slug)} <strong>${formatCurrencyShort(b.yearlySaving)}</strong>
      </button>`;
  }).join("");
  const more = result.byCategory.length > 4 ? `<span class="ov-chip-more">+${result.byCategory.length - 4} more</span>` : "";
  return `
    <article class="card-item ov-item" data-key="${escapeAttr(cardKey)}">
      <div class="card-row">
        <div class="card-rank${rank === 1 ? " rank-1" : ""}">${rank}</div>
        ${renderBankLogo(result.bank, "card-logo-box")}
        <div class="card-info">
          <div class="card-badges">${showEligibility ? renderEligibilityBadge(evaluateEligibility(result.bank, result.card)) : ""}${renderBnplBadge(result.bank, result.card)}</div>
          <div class="card-name">${escapeHtml(result.card)}</div>
          <div class="card-bank">${escapeHtml(result.bank)}</div>
        </div>
        <div class="ov-total">
          <div class="ov-net">${formatCurrency(Math.max(0, result.net))}</div>
          <div class="ov-net-l">${result.net < 0 ? "fee outweighs savings" : "net saving / year"}</div>
        </div>
      </div>
      <div class="ov-breakdown">
        <div class="ov-bar" aria-hidden="true">${segs}</div>
        <div class="ov-chips">${chips}${more}</div>
        <div class="ov-meta">Saves ${formatCurrency(result.yearlySaving)} a year · ${escapeHtml(feeText)}</div>
      </div>
    </article>`;
}

function renderOverallView() {
  const resultsGrid = document.getElementById("results-grid");
  const countEl = document.getElementById("result-count");
  const countLabel = document.getElementById("result-count-label");
  const rhSub = document.getElementById("rh-sub");
  const emptyState = document.getElementById("empty-state");
  const topPick = document.getElementById("top-pick");
  const summaryBest = document.getElementById("summary-best");
  if (emptyState) emptyState.classList.add("hidden");
  if (topPick) topPick.innerHTML = "";
  if (countLabel) countLabel.textContent = "cards ranked overall";
  if (rhSub) rhSub.textContent = "Yearly savings on your spending, after fees";
  if (!resultsGrid) return;

  if (!state.overallRates) {
    if (countEl) countEl.textContent = "…";
    resultsGrid.innerHTML = `<div class="cd-empty">Loading savings across all categories…</div>`;
    ensureOverallRates()
      .then(() => { if (state.viewMode === "overall") renderOverallView(); })
      .catch(() => {
        if (state.viewMode === "overall") resultsGrid.innerHTML = `<div class="cd-empty">Couldn't load the overall ranking. Check your connection and try again.</div>`;
      });
    return;
  }

  const cats = getOverallCategories();
  const catsBySlug = Object.fromEntries(cats.map((c) => [c.slug, c]));
  const results = computeOverallRecommendations();
  if (countEl) countEl.textContent = String(results.length);
  if (summaryBest) summaryBest.textContent = results[0] ? `${formatCurrency(results[0].net)} / year` : "—";
  const shown = results.slice(0, state.overallShown || 20);
  const list = shown.length
    ? shown.map((r, i) => renderOverallRow(r, i + 1, catsBySlug)).join("")
    : `<div class="cd-empty">No card saves anything on this spending mix with the current filters. Add some monthly spend above or clear the bank and card-type filters.</div>`;
  const moreBtn = results.length > shown.length
    ? `<button type="button" class="ov-more" id="ov-more">Show ${Math.min(20, results.length - shown.length)} more</button>`
    : "";
  resultsGrid.innerHTML = renderOverallSpendPanel(cats) + `<div class="ov-list">${list}</div>` + moreBtn;

  resultsGrid.querySelectorAll("input[data-spend]").forEach((input) => {
    input.addEventListener("change", () => {
      state.monthlySpend = sanitizeMonthlySpend({ ...state.monthlySpend, [input.dataset.spend]: input.value });
      trackEvent("overall_spend_change", { category: input.dataset.spend });
      renderOverallView();
      encodeStateToUrl();
    });
  });
  document.getElementById("ov-reset")?.addEventListener("click", () => {
    state.monthlySpend = {};
    seedMonthlySpend();
    renderOverallView();
    encodeStateToUrl();
  });
  document.getElementById("ov-panel")?.addEventListener("toggle", (e) => {
    state.overallPanelOpen = e.currentTarget.open;
    const edit = e.currentTarget.querySelector(".ov-panel-edit");
    if (edit) edit.textContent = e.currentTarget.open ? "Done" : "Edit";
  });
  resultsGrid.querySelectorAll("[data-add-spend]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const slug = btn.dataset.addSpend;
      const meta = getCategoryMeta(slug);
      // Start from one typical visit a month; the user adjusts from there.
      state.monthlySpend = sanitizeMonthlySpend({ ...state.monthlySpend, [slug]: getCategoryDefaultBill(meta || undefined) });
      renderOverallView();
      encodeStateToUrl();
      const input = resultsGrid.querySelector(`input[data-spend="${CSS.escape(slug)}"]`);
      if (input) { input.focus(); input.select(); }
    });
  });
  document.getElementById("ov-more")?.addEventListener("click", () => {
    state.overallShown = (state.overallShown || 20) + 20;
    renderOverallView();
  });
  resultsGrid.querySelectorAll(".ov-chip[data-ov-cat]").forEach((chip) => {
    chip.addEventListener("click", () => {
      // Drill into that category's ranked cards, filtered to this card.
      state.selectedCards = new Set([chip.dataset.ovCard]);
      trackEvent("overall_drilldown", { category: chip.dataset.ovCat });
      setCategory(chip.dataset.ovCat);
    });
  });
}

/* ── SPLIT LOADING: SUMMARY + LAZY RAW OFFERS ──
   loadSummary() fetches only the index meta + the small precomputed summary.
   It NEVER fetches the per-city raw offer files. That happens lazily via
   ensureRawOffers() the first time the app needs per-offer data or a
   non-default computation. */

// Fetch the offers index and, if it advertises a summary, the summary file.
// Returns { index, summary } on success, or null if no usable summary exists
// (caller falls back to loading raw offers up front).
async function loadSummary() {
  const index = await fetchJson("./data/offers-index.json");
  if (!index || !index.summaryFile || !index.summaryVersion) {
    return null; // older build with no summary → caller falls back
  }
  const sep = index.summaryFile.includes("?") ? "&" : "?";
  const summary = await fetchJson(`${index.summaryFile}${sep}v=${encodeURIComponent(index.summaryVersion)}`);
  if (!summary || summary.splitFormat !== "summary-v1" || !summary.scopes || !summary.restaurantDeals || !summary.facets) {
    return null; // malformed summary → fall back to raw offers
  }
  return { index, summary };
}

// Memoized lazy loader for the raw per-city offers. The first call runs
// loadOffersPayload() (the original heavy path), stashes the result on
// state.data, and resolves. Every subsequent call resolves immediately to the
// same promise. After the first resolution state.data is set, so isDefaultScope()
// returns false and all render paths use the normal compute functions.
// Raw payloads are cached per category: "food" is the core per-city dataset,
// every other category is one data/categories/<slug>.json file in the same
// shape. state.data always points at the ACTIVE category's payload (or null
// while it streams in / while food is still served from the summary).
const _categoryLoads = {};
const _categoryData = {};
function ensureRawOffers() {
  const slug = state.category || "food";
  if (!_categoryLoads[slug]) {
    _categoryLoads[slug] = (async () => {
      showRawLoadingIndicator(true);
      try {
        const payload = slug === "food" ? await loadOffersPayload() : await loadCategoryPayload(slug);
        _categoryData[slug] = payload;
        return payload;
      } finally {
        showRawLoadingIndicator(false);
      }
    })();
    // A failed load must be retryable on the next interaction.
    _categoryLoads[slug].catch(() => { delete _categoryLoads[slug]; });
  }
  return _categoryLoads[slug].then((payload) => {
    // The user may have switched category while this was in flight; only
    // install the payload if it's still the active one.
    if ((state.category || "food") === slug) state.data = payload;
    return payload;
  });
}

// Run `fn` once raw offers are guaranteed loaded. If they're already loaded it
// runs synchronously. Otherwise it awaits ensureRawOffers() and then runs.
// Used at every lazy-load trigger point so the first non-default action pulls
// in raw offers before re-rendering.
function withRawOffers(fn) {
  if (state.data) { fn(); return; }
  const slug = state.category || "food";
  ensureRawOffers().then(() => {
    // Category changed mid-flight: that switch already scheduled its own render.
    if ((state.category || "food") !== slug) return;
    fn();
  }).catch((err) => {
    console.error("[offers] lazy load failed", err);
    // Best-effort: still try to render so the UI isn't frozen.
    try { fn(); } catch (_) {}
  });
}

// Render after a view switch. If the (post-switch) view can be served from the
// summary, render immediately. Otherwise lazy-load raw offers first. Wallet /
// my-wallet / compare always fall through to the raw path because
// isDefaultScope() is false for them.
function renderForView() {
  if (isDefaultScope() || isAggregateView()) { render(); return; }
  withRawOffers(render);
}

// Subtle, best-effort loading affordance while raw offers stream in on the
// first non-default action. Reuses the result header's body[data-loading]
// hook if present; otherwise it's a no-op (never throws, never blocks).
function showRawLoadingIndicator(on) {
  try {
    if (on) document.body.setAttribute("data-raw-loading", "1");
    else document.body.removeAttribute("data-raw-loading");
  } catch (_) { /* no DOM — ignore */ }
}

// The default scope is anything the precomputed summary can answer verbatim.
// True ONLY when raw offers are NOT yet loaded AND the current view is the
// plain ranked cards/restaurants list at the default bill with no filters and
// eligibility off. Once any of these is false we must fall through to the
// compute path (which requires raw offers).
function isDefaultScope() {
  if (!isFoodCategory()) return false;    // summary is precomputed for food only
  if (state.data) return false;           // raw loaded → always use compute path
  if (!state.summary) return false;       // no summary → can't serve from it
  // View must be a precomputed list (wallet / my-wallet / compare need raw).
  if (state.viewMode !== "cards" && state.viewMode !== "restaurants") return false;
  // City must be one of the precomputed scopes.
  const cityKey = getSummaryCityKey();
  if (!cityKey || !state.summary.scopes[cityKey]) return false;
  // Default bill only.
  if (Number(state.orderValue) !== Number(state.summary.orderValue ?? 10000)) return false;
  // No active filters of any kind.
  if (state.selectedDays.size > 0) return false;
  if (state.selectedRestaurants.size > 0) return false;
  if (state.selectedBanks.size > 0) return false;
  if (state.selectedCardTypes.size > 0) return false;
  if (state.selectedCards.size > 0) return false;
  if (state.selectedCuisines.size > 0) return false;
  // The summary has one best card per restaurant, not per-card coverage, so
  // it can't say which restaurants the user's own cards cover.
  if (state.viewMode === "restaurants" && state.ownedCards.size > 0) return false;
  // Eligibility off.
  if (state.useEligibility) return false;
  if (state.monthlySalary !== null) return false;
  if (state.accountBalance !== null) return false;
  return true;
}

// Map state.selectedCity → the summary scope key. The summary scopes are
// keyed "all","karachi","lahore","islamabad" (lowercase); state.selectedCity
// is already lowercase per state.js / normalizeCityValue. Returns null for any
// city not present as a precomputed scope (caller falls through to compute).
function getSummaryCityKey() {
  const key = normalizeCityValue(state.selectedCity);
  return state.summary && state.summary.scopes[key] ? key : null;
}

/**
 * Lightweight runtime schema check on the offers payload. We don't pull in
 * Zod or Ajv — instead we assert the shape the rest of the app assumes,
 * and surface a clear error in the UI if the build pipeline ever emits
 * something malformed (rather than silently rendering blank cards).
 *
 * Throws OffersSchemaError; the boot handler catches this and shows a
 * visible error block.
 */
function validateOffersPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== "object") errors.push("payload is not an object");
  if (!Array.isArray(payload?.cities) || payload.cities.length === 0) errors.push("cities[] missing or empty");
  if (!Array.isArray(payload?.dayNames) || payload.dayNames.length !== 7) errors.push("dayNames must be a 7-element array");
  if (!Array.isArray(payload?.offers)) errors.push("offers[] missing");
  if (!payload?.stats || typeof payload.stats.offers !== "number") errors.push("stats.offers must be a number");

  if (Array.isArray(payload?.offers) && payload.offers.length > 0) {
    // Spot-check the first few offers — full validation across 26k+ rows would be wasteful.
    const sampleSize = Math.min(50, payload.offers.length);
    for (let i = 0; i < sampleSize; i++) {
      const o = payload.offers[i];
      if (!o || typeof o !== "object") { errors.push(`offer[${i}] not an object`); continue; }
      const required = ["city", "restaurant", "bank", "card", "days"];
      for (const k of required) {
        if (o[k] === undefined || o[k] === null) {
          errors.push(`offer[${i}].${k} missing (bank=${o.bank || "?"} card=${o.card || "?"})`);
          break;
        }
      }
      if (!Array.isArray(o.days)) { errors.push(`offer[${i}].days must be an array`); continue; }
      // discountPct OR fixedDiscountPkr must be present for the saving math to work
      if (!Number.isFinite(o.discountPct) && !Number.isFinite(o.fixedDiscountPkr)) {
        // Some legitimate offers may have neither (e.g., text-only), so this is a warning, not an error.
      }
    }
  }

  if (errors.length > 0) {
    const err = new Error(
      "Offers payload failed schema validation:\n  - " + errors.slice(0, 8).join("\n  - ") +
      (errors.length > 8 ? `\n  ...and ${errors.length - 8} more` : "")
    );
    err.name = "OffersSchemaError";
    throw err;
  }
}

async function fetchJson(path) {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`Failed to load ${path}: ${response.status}`);
  return response.json();
}

async function loadRequirementsContext() {
  try {
    const [requirementsPayload, mappingPayload, sourcesPayload] = await Promise.all([
      fetchJson("./data/card-requirements/normalized/card_requirements.json"),
      fetchJson("./data/card-requirements/normalized/deal_requirement_card_map.json"),
      fetchJson("./data/card-requirements/normalized/sources.json"),
    ]);
    return {
      available: true,
      byCardId: new Map(requirementsPayload.map((row) => [row.card_id, row])),
      mappingByDealKey: new Map(
        mappingPayload.map((row) => [buildDealCardKey(row.deal_bank_name, row.deal_card_name), row]),
      ),
      sourcesById: new Map(sourcesPayload.map((s) => [s.source_id, s])),
      estimatesByTier: buildEstimatesByTier(requirementsPayload),
    };
  } catch {
    return { available: false, byCardId: new Map(), mappingByDealKey: new Map(), sourcesById: new Map(), estimatesByTier: new Map() };
  }
}

/* ── BIND EVENTS ── */
function bindCityChip() {
  const chip = document.getElementById("rh-chip-city");
  if (!chip) return;
  chip.addEventListener("click", () => {
    let picker = document.getElementById("city-chip-picker");
    if (picker) { picker.remove(); return; }
    const meta = getMeta();
    if (!meta) return;
    const cities = ["all", ...meta.cities.map((c) => normalizeCityValue(c)).filter(Boolean)];
    picker = document.createElement("div");
    picker.id = "city-chip-picker";
    picker.className = "city-chip-picker";
    cities.forEach((city) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `city-chip-opt${state.selectedCity === city ? " active" : ""}`;
      btn.textContent = formatCityLabel(city);
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        state.selectedCity = city;
        picker.remove();
        render();
      });
      picker.appendChild(btn);
    });
    chip.style.position = "relative";
    chip.appendChild(picker);
    const dismiss = (ev) => {
      if (!chip.contains(ev.target)) {
        picker.remove();
        document.removeEventListener("click", dismiss, true);
      }
    };
    setTimeout(() => document.addEventListener("click", dismiss, true), 0);
  });
}

function bindEvents() {
  // Nav city tabs
  renderNavCityTabs();
  setupMobileNavMenu();

  // Sidebar bill slider
  const orderSlider = document.getElementById("order-value");
  if (orderSlider) {
    orderSlider.addEventListener("input", (e) => {
      state.orderValue = Number(e.target.value);
      syncPersonaActive();
      // Bill ≠ default is a non-default computation → needs raw offers. Render
      // synchronously so the bill label and URL (?bill=) update immediately;
      // renderRecommendations() self-defers the results re-render until raw
      // offers land (its own !state.data && !isDefaultScope() backstop).
      render();
    });
  }

  // Persona-preset chips. Each chip sets the bill + outings to a typical
  // Pakistani-consumer scenario, so first-timers don't have to guess where
  // their number sits on a 1k–50k slider.
  document.querySelectorAll(".persona-chip").forEach((btn) => {
    btn.addEventListener("click", () => {
      const bill = Number(btn.dataset.bill);
      const outings = Number(btn.dataset.outings || 1);
      if (Number.isFinite(bill)) state.orderValue = bill;
      if (Number.isFinite(outings)) state.outingsPerWeek = outings;
      const slider = document.getElementById("order-value");
      if (slider) slider.value = String(bill);
      syncPersonaActive();
      trackEvent("persona_chip", { label: btn.dataset.label, bill, outings });
      // Persona presets change the bill (and usually push it off the default)
      // → needs raw offers for the recompute.
      withRawOffers(render);
    });
  });

  // Saving-window toggle. Reframes the per-card hero stat as /outing | /month
  // | /year — same underlying number, different mental anchor. Default is
  // "year" because the annual figure is the one users get excited about.
  state.savingWindow = state.savingWindow || "yr";
  document.querySelectorAll(".saving-window-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.savingWindow = btn.dataset.window;
      document.querySelectorAll(".saving-window-btn").forEach((b) =>
        b.classList.toggle("active", b === btn)
      );
      render();
    });
  });

  // Restaurant search
  const restSearch = document.getElementById("restaurant-search");
  if (restSearch) {
    restSearch.addEventListener("input", (e) => {
      state.restSearchTerm = e.target.value.trim();
      renderRestaurantSearch();
    });
  }

  // Bank search
  const bankSearch = document.getElementById("bank-search");
  if (bankSearch) {
    bankSearch.addEventListener("input", (e) => {
      state.bankSearchTerm = e.target.value.trim();
      renderBankSearch();
    });
  }

  // Card search
  const cardSearch = document.getElementById("card-search");
  if (cardSearch) {
    cardSearch.addEventListener("input", (e) => {
      state.cardSearchTerm = e.target.value.trim();
      renderCardSearch();
    });
  }

  // Eligibility
  const monthlySalary = document.getElementById("monthly-salary");
  const accountBalance = document.getElementById("account-balance");
  const clearElig = document.getElementById("clear-eligibility");

  function syncEligibilityState() {
    state.useEligibility = state.monthlySalary !== null || state.accountBalance !== null;
    if (clearElig) clearElig.style.display = state.useEligibility ? "" : "none";
  }

  if (monthlySalary) {
    monthlySalary.addEventListener("input", (e) => {
      state.monthlySalary = parseOptionalNumber(e.target.value);
      syncEligibilityState();
      // Render synchronously so eligibility UI + URL update immediately; the
      // list re-render self-defers until raw offers load.
      render();
    });
  }
  if (accountBalance) {
    accountBalance.addEventListener("input", (e) => {
      state.accountBalance = parseOptionalNumber(e.target.value);
      syncEligibilityState();
      // Render synchronously so eligibility UI + URL update immediately; the
      // list re-render self-defers until raw offers load.
      render();
    });
  }
  if (clearElig) {
    clearElig.addEventListener("click", () => {
      state.useEligibility = false;
      state.monthlySalary = null;
      state.accountBalance = null;
      if (monthlySalary) monthlySalary.value = "";
      if (accountBalance) accountBalance.value = "";
      clearElig.style.display = "none";
      render();
    });
  }

  // Reset all
  const resetBtn = document.getElementById("reset-filters");
  if (resetBtn) resetBtn.addEventListener("click", resetFilters);

  // Quiz. quiz.js renders against state.data (dayNames + per-offer matching),
  // so ensure raw offers are loaded before opening it.
  const openQuizBtn = document.getElementById("btn-open-quiz");
  if (openQuizBtn) openQuizBtn.addEventListener("click", () => {
    trackEvent("quiz_open");
    withRawOffers(openQuiz);
  });

  // Find My Card FAB (mobile)

  // Compare tray
  const cmpClear = document.getElementById("btn-cmp-clear");
  if (cmpClear) cmpClear.addEventListener("click", () => {
    state.compareList = [];
    renderCompareTray();
    renderRecommendations();
  });
  const cmpOpen = document.getElementById("btn-compare-open");
  if (cmpOpen) cmpOpen.addEventListener("click", () => openCompareModal());

  // Mobile bottom tabs. Each `mob-tab` carries a data-view that maps to a
  // viewMode value; clicking it switches the view (same handler as the
  // desktop view-toggle buttons) and closes any open overlay sheet.
  document.querySelectorAll(".mob-tab[data-view]").forEach((tab) => {
    tab.addEventListener("click", () => {
      const view = tab.dataset.view;
      if (!view) return;
      closeAllModals();
      closeMobileSheet();
      state.viewMode = view;
      trackEvent("view_change", { view, source: "mob-tab" });
      // wallet / my-wallet always need raw offers; cards/restaurants only if
      // the current scope isn't summary-servable. renderForView() decides.
      renderForView();
    });
  });

  // Filters live in the result header now (closer to the user's eye-line
  // than a small icon in the top nav). Tap → bottom sheet opens.
  document.getElementById("rh-filters-btn")?.addEventListener("click", openMobileSheet);
  // Tap scrim to close the bottom sheet.
  document.getElementById("mob-sheet-scrim")?.addEventListener("click", closeMobileSheet);

  // Tonight button
  document.getElementById("btn-tonight")?.addEventListener("click", () => {
    const todayIdx = (new Date().getDay() + 6) % 7;
    if (state.selectedDays.size === 1 && state.selectedDays.has(todayIdx)) {
      state.selectedDays = new Set();
    } else {
      state.selectedDays = new Set([todayIdx]);
    }
    // Day filter → needs raw offers (clearing it back to none is summary-OK
    // once raw is loaded; withRawOffers handles both cases safely).
    withRawOffers(render);
  });

  // View toggle. We close any still-mounted modal before switching views,
  // otherwise an overlay from the previous view can intercept clicks behind
  // it (Playwright caught this on the cards→detail→cards path).
  document.getElementById("btn-view-cards")?.addEventListener("click", () => {
    closeAllModals();
    state.viewMode = "cards";
    trackEvent("view_change", { view: "cards" });
    renderForView();
  });
  document.getElementById("btn-view-restaurants")?.addEventListener("click", () => {
    closeAllModals();
    state.viewMode = "restaurants";
    trackEvent("view_change", { view: "restaurants" });
    renderForView();
  });
  document.getElementById("btn-view-next-card")?.addEventListener("click", () => {
    closeAllModals();
    state.viewMode = "my-wallet";
    trackEvent("view_change", { view: "my-wallet" });
    renderForView();
  });
  document.getElementById("btn-view-wallet")?.addEventListener("click", () => {
    closeAllModals();
    state.viewMode = "wallet";
    trackEvent("view_change", { view: "wallet" });
    renderForView();
  });

  // Card detail modal
  document.getElementById("card-detail-modal")?.addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeCardDetail();
  });
  document.getElementById("restaurant-detail-modal")?.addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeRestaurantDetail();
  });

  // Modals close on backdrop click
  document.getElementById("quiz-modal")?.addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeQuiz();
  });
  document.getElementById("compare-modal")?.addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeCompareModal();
  });

  // Global Escape closes whichever modal is open. Previously only the quiz
  // had a keyboard handler, so users hitting Esc on card/restaurant/compare
  // had to hunt for the × button.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (document.getElementById("card-detail-modal")?.style.display === "flex") closeCardDetail();
    if (document.getElementById("restaurant-detail-modal")?.style.display === "flex") closeRestaurantDetail();
    if (document.getElementById("compare-modal")?.style.display === "flex") closeCompareModal();
  });
}

// Force every overlay-style modal closed. Defensive helper called when the
// user navigates away (e.g. taps a view-toggle tab) — without it, a
// programmatically-shown modal could remain mounted and intercept clicks
// behind it on its way to z-index oblivion.
function closeAllModals() {
  ["card-detail-modal", "restaurant-detail-modal", "compare-modal", "quiz-modal"].forEach((id) => {
    const el = document.getElementById(id);
    if (el && el.style.display === "flex") el.style.display = "none";
  });
  state.detailCardKey = null;
  state.detailRestaurantKey = null;
}

function setActiveTab(id) {
  document.querySelectorAll(".mob-tab").forEach((t) => t.classList.remove("active"));
  document.getElementById(id)?.classList.add("active");
}

// Sync the bottom-tab .active class to whatever the current state.viewMode
// is, and stamp the view onto <body> so view-specific CSS (e.g. the tight
// card-row stats strip in the cards view, which would mangle the 4-stat
// next-card grid in my-wallet) can target it.
function syncMobileTabActive() {
  const view = state.viewMode || "cards";
  document.querySelectorAll(".mob-tab[data-view]").forEach((t) => {
    t.classList.toggle("active", t.dataset.view === view);
  });
  document.body.setAttribute("data-view", view);
}

// Mobile filter bottom-sheet open/close. The sidebar element is repurposed
// — same DOM, different positioning on small screens (see styles.css).
function openMobileSheet() {
  document.getElementById("sidebar")?.classList.add("mob-open");
  document.getElementById("mob-sheet-scrim")?.classList.add("is-open");
  document.getElementById("mob-sheet-scrim")?.removeAttribute("hidden");
  // Lock body scroll so background doesn't move while the sheet is open.
  document.body.classList.add("mob-sheet-open");
}
function closeMobileSheet() {
  document.getElementById("sidebar")?.classList.remove("mob-open");
  const scrim = document.getElementById("mob-sheet-scrim");
  if (scrim) {
    scrim.classList.remove("is-open");
    // Re-hide the scrim after the transition so it doesn't intercept clicks
    // when "transitioning back". 250ms is the same as the CSS transition.
    setTimeout(() => scrim.setAttribute("hidden", ""), 250);
  }
  document.body.classList.remove("mob-sheet-open");
}

/* ── RESET ── */
function resetFilters() {
  state.selectedCity = "all";
  state.selectedDays = new Set();
  state.selectedRestaurants = new Set();
  state.selectedBanks = new Set();
  state.selectedCardTypes = new Set();
  state.selectedCards = new Set();
  state.bankSearchTerm = "";
  state.restSearchTerm = "";
  state.cardSearchTerm = "";
  state.orderValue = getCategoryDefaultBill();
  state.outingsPerWeek = getCategoryDefaultFrequency();
  state.viewMode = "cards";
  state.useEligibility = false;
  state.monthlySalary = null;
  state.accountBalance = null;
  state.ownedCards = new Set();
  state.ownedCardSearchTerm = "";
  state.manageWalletExpanded = false;
  state.walletRestaurantsExpanded = false;
  state.walletSize = 2;
  state.walletBuildOnOwned = false;
  state.walletMaxFee = null;
  state.walletNoSameBank = false;
  state.walletMixedTypes = false;
  state.walletObjective = "savings";
  state.walletMustInclude = new Set();
  state.walletMustIncludeSearchTerm = "";
  state.walletAdvancedOpen = false;

  syncBillControls();
  const restSearch = document.getElementById("restaurant-search");
  if (restSearch) restSearch.value = "";
  const bankSearch = document.getElementById("bank-search");
  if (bankSearch) bankSearch.value = "";
  const cardSearch = document.getElementById("card-search");
  if (cardSearch) cardSearch.value = "";
  const clearEligBtn = document.getElementById("clear-eligibility");
  if (clearEligBtn) clearEligBtn.style.display = "none";
  const monthlySalary = document.getElementById("monthly-salary");
  if (monthlySalary) monthlySalary.value = "";
  const accountBalance = document.getElementById("account-balance");
  if (accountBalance) accountBalance.value = "";

  render();
}

/* ── RENDER ── */
function render() {
  // First render resolves the loading skeleton: drop the header's .loading
  // class (its real count/subtitle text is populated below) and let the
  // results-grid skeletons be overwritten by the view renderers.
  document.getElementById("result-header")?.classList.remove("loading");
  pruneRestaurants();
  pruneCards();
  renderOrderValueLabel();
  renderOutingsPills();
  renderNavCityTabs();
  renderDayPills();
  renderCardTypePills();
  renderBankSearch();
  renderSelectedBanks();
  renderRestaurantSearch();
  renderSelectedRestaurants();
  renderCuisinePills();
  renderRecommendations();
  updateMobileFilterBadge();
  updateTonightButton();
  updateViewToggle();
  syncMobileTabActive();
  encodeStateToUrl();
}

function renderOrderValueLabel() {
  const label = document.getElementById("order-value-label");
  if (label) label.textContent = formatCurrency(state.orderValue);
  const billSummary = document.getElementById("bill-summary");
  if (billSummary) billSummary.textContent = formatCurrency(state.orderValue);
}

/* ── NAV CITY TABS ── */
function renderNavCityTabs() {
  const meta = getMeta();
  if (!meta) return;
  const cities = ["all", ...meta.cities.map((city) => normalizeCityValue(city)).filter(Boolean)];

  const buildTabs = (container, btnClass) => {
    if (!container) return;
    container.innerHTML = "";
    cities.forEach((city) => {
      const btn = document.createElement("button");
      btn.className = `${btnClass}${state.selectedCity === city ? " active" : ""}`;
      btn.textContent = formatCityLabel(city);
      btn.type = "button";
      btn.addEventListener("click", () => {
        state.selectedCity = city;
        render();
        // Reset to the top so the new city's list reads from the header / top
        // pick, not from wherever the previous city was scrolled to. Mirrors
        // the mobile app, which scrolls its list to offset 0 on city change.
        window.scrollTo({ top: 0, behavior: "auto" });
      });
      container.appendChild(btn);
    });
  };

  buildTabs(document.getElementById("nav-city-tabs"), "city-tab");
  buildTabs(document.getElementById("rh-city-tabs"), "rh-city-tab");
}

function updateCityChip() {
  // city chip replaced by rh-city-tabs — kept for any external callers
}

/* ── DAY PILLS ── */
function renderDayPills() {
  const meta = getMeta();
  if (!meta) return;
  const container = document.getElementById("day-pills");
  if (!container) return;
  container.innerHTML = "";
  meta.dayNames.forEach((dayName, index) => {
    const btn = document.createElement("button");
    btn.className = `s-pill${state.selectedDays.has(index) ? " active" : ""}`;
    btn.textContent = DAY_SHORT[index];
    btn.title = dayName;
    btn.type = "button";
    btn.addEventListener("click", () => {
      if (state.selectedDays.has(index)) {
        state.selectedDays.delete(index);
      } else {
        state.selectedDays.add(index);
      }
      // Day is a real filter the summary can't answer. Render synchronously so
      // encodeStateToUrl() runs immediately and `?days=...` lands in the URL
      // (plus the pill lights up) without waiting on the raw-offers fetch;
      // renderRecommendations() self-defers the list re-render until raw
      // offers arrive.
      render();
    });
    container.appendChild(btn);
  });
}

/* ── CARD TYPE PILLS ── */
function renderCardTypePills() {
  const container = document.getElementById("card-type-pills");
  if (!container) return;
  container.innerHTML = "";
  CARD_TYPE_OPTIONS.forEach(({ value, label }) => {
    const btn = document.createElement("button");
    btn.className = `s-pill${state.selectedCardTypes.has(value) ? " active" : ""}`;
    btn.textContent = label;
    btn.type = "button";
    btn.addEventListener("click", () => {
      if (state.selectedCardTypes.has(value)) {
        state.selectedCardTypes.delete(value);
      } else {
        state.selectedCardTypes.add(value);
      }
      // Card-type filter → needs raw offers. Same reasoning as the day-pill
      // click above — render synchronously so `?types=...` makes it into the
      // URL and the pill lights up immediately; the list re-render self-defers
      // until raw offers load.
      render();
    });
    container.appendChild(btn);
  });
}

/* ── BANK SEARCH ── */
function getAvailableBanks() {
  // Raw not loaded yet → serve the bank list from the precomputed facets so
  // the sidebar populates on first paint without the heavy offers array.
  if (!state.data) {
    const banks = state.summary?.facets?.banks;
    return Array.isArray(banks) ? [...banks].sort((a, b) => a.localeCompare(b)) : [];
  }
  const bankCounts = new Map();
  state.data.offers.forEach((offer) => {
    if (!cityMatches(offer.city)) return;
    if (!cardTypeMatches(offer.cardCategory)) return;
    bankCounts.set(offer.bank, (bankCounts.get(offer.bank) || 0) + 1);
  });
  return Array.from(bankCounts.keys()).sort((a, b) => a.localeCompare(b));
}

function renderBankSearch() {
  const banks = getAvailableBanks();
  state.selectedBanks.forEach((bank) => {
    if (!banks.includes(bank)) state.selectedBanks.delete(bank);
  });

  const term = state.bankSearchTerm.toLowerCase();
  const results = banks
    .filter((b) => !state.selectedBanks.has(b))
    .filter((b) => !term || b.toLowerCase().includes(term))
    .slice(0, 10);

  const container = document.getElementById("bank-results");
  if (!container) return;
  container.innerHTML = "";
  results.forEach((bank) => {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "s-search-item";
    item.innerHTML = `<span>${escapeHtml(bank)}</span><span class="s-search-item-add">Add</span>`;
    item.addEventListener("click", () => {
      state.selectedBanks.add(bank);
      state.bankSearchTerm = "";
      const bankSearch = document.getElementById("bank-search");
      if (bankSearch) bankSearch.value = "";
      // Render synchronously so the chip + `?banks=` URL param land immediately;
      // the filtered list re-render self-defers until raw offers load.
      render();
    });
    container.appendChild(item);
  });
}

function renderSelectedBanks() {
  const container = document.getElementById("selected-banks");
  if (!container) return;
  container.innerHTML = "";
  Array.from(state.selectedBanks).sort((a, b) => a.localeCompare(b)).forEach((bank) => {
    const chip = document.createElement("div");
    chip.className = "s-chip";
    chip.innerHTML = `<span>${escapeHtml(bank)}</span>`;
    const rm = document.createElement("button");
    rm.className = "s-chip-remove";
    rm.type = "button";
    rm.textContent = "×";
    rm.setAttribute("aria-label", `Remove ${bank}`);
    rm.addEventListener("click", () => { state.selectedBanks.delete(bank); render(); });
    chip.appendChild(rm);
    container.appendChild(chip);
  });
}

/* ── CARD SEARCH ── */
function getAvailableCards() {
  // Raw not loaded yet → derive the card-name list from the precomputed
  // "Bank || Card" facets. (Bank/restaurant filters can't be active without
  // raw offers having been loaded, so no further filtering is needed here.)
  if (!state.data) {
    const facetCards = state.summary?.facets?.cards;
    if (!Array.isArray(facetCards)) return [];
    const cardSet = new Set();
    facetCards.forEach((entry) => {
      const card = String(entry).split(" || ")[1];
      if (card) cardSet.add(card);
    });
    return Array.from(cardSet).sort((a, b) => a.localeCompare(b));
  }
  const cardSet = new Set();
  state.data.offers.forEach((offer) => {
    if (!cityMatches(offer.city)) return;
    if (!cardTypeMatches(offer.cardCategory)) return;
    if (state.selectedBanks.size > 0 && !state.selectedBanks.has(offer.bank)) return;
    if (state.selectedRestaurants.size > 0 && !state.selectedRestaurants.has(offer.restaurant)) return;
    cardSet.add(offer.card);
  });
  return Array.from(cardSet).sort((a, b) => a.localeCompare(b));
}

function pruneCards() {
  // Unloaded non-food category / Overall: the fallback lists are food's, so
  // pruning now would wrongly drop a card filter carried in from elsewhere.
  if (!state.data && (!isFoodCategory() || isAggregateView())) return;
  const available = new Set(getAvailableCards());
  state.selectedCards.forEach((card) => {
    if (!available.has(card)) state.selectedCards.delete(card);
  });
}

function renderCardSearch() {
  const available = getAvailableCards();
  const term = state.cardSearchTerm.toLowerCase();
  const results = available
    .filter((c) => !state.selectedCards.has(c))
    .filter((c) => !term || c.toLowerCase().includes(term))
    .slice(0, 14);

  const container = document.getElementById("card-results");
  if (!container) return;
  container.innerHTML = "";
  results.forEach((card) => {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "s-search-item";
    item.innerHTML = `<span>${escapeHtml(card)}</span><span class="s-search-item-add">Add</span>`;
    item.addEventListener("click", () => {
      state.selectedCards.add(card);
      state.cardSearchTerm = "";
      const cardSearch = document.getElementById("card-search");
      if (cardSearch) cardSearch.value = "";
      // Render synchronously so the chip + `?cards=` URL param land immediately;
      // the filtered list re-render self-defers until raw offers load.
      render();
    });
    container.appendChild(item);
  });
}

function renderSelectedCards() {
  const container = document.getElementById("selected-cards");
  if (!container) return;
  container.innerHTML = "";

  const count = state.selectedCards.size;
  const countEl = document.getElementById("selected-cards-count");
  if (countEl) {
    countEl.textContent = count > 0 ? `(${count})` : "";
    countEl.style.display = count > 0 ? "" : "none";
  }

  Array.from(state.selectedCards).sort((a, b) => a.localeCompare(b)).forEach((card) => {
    const chip = document.createElement("div");
    chip.className = "s-chip";
    chip.innerHTML = `<span>${escapeHtml(card)}</span>`;
    const rm = document.createElement("button");
    rm.className = "s-chip-remove";
    rm.type = "button";
    rm.textContent = "×";
    rm.setAttribute("aria-label", `Remove ${card}`);
    rm.addEventListener("click", () => { state.selectedCards.delete(card); render(); });
    chip.appendChild(rm);
    container.appendChild(chip);
  });
}

/* ── RESTAURANT SEARCH ── */
function getAvailableRestaurants() {
  // Raw not loaded yet → serve restaurant names from the index's
  // restaurantsByCity map, honoring the selected city. (No bank/cardType
  // filter can be active at this point — those load raw offers.)
  if (!state.data) {
    if (!isFoodCategory()) return [];
    const byCity = state.index?.restaurantsByCity;
    if (!byCity) return [];
    const names = new Set();
    const sel = normalizeCityValue(state.selectedCity);
    for (const [cityName, list] of Object.entries(byCity)) {
      if (sel !== "all" && normalizeCityValue(cityName) !== sel) continue;
      (Array.isArray(list) ? list : []).forEach((n) => names.add(n));
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }
  const names = new Set();
  state.data.offers.forEach((offer) => {
    if (!cityMatches(offer.city)) return;
    if (!cardTypeMatches(offer.cardCategory)) return;
    if (state.selectedBanks.size > 0 && !state.selectedBanks.has(offer.bank)) return;
    names.add(offer.restaurant);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

function pruneRestaurants() {
  if (!state.data && (!isFoodCategory() || isAggregateView())) return;
  const available = new Set(getAvailableRestaurants());
  state.selectedRestaurants.forEach((name) => {
    if (!available.has(name)) state.selectedRestaurants.delete(name);
  });
}

function renderRestaurantSearch() {
  const available = getAvailableRestaurants();
  const term = state.restSearchTerm.toLowerCase();
  const results = available
    .filter((r) => !state.selectedRestaurants.has(r))
    .filter((r) => !term || r.toLowerCase().includes(term))
    .slice(0, 14);

  const container = document.getElementById("restaurant-results");
  if (!container) return;
  container.innerHTML = "";
  results.forEach((restaurant) => {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "s-search-item";
    item.innerHTML = `<span>${escapeHtml(restaurant)}</span><span class="s-search-item-add">Add</span>`;
    item.addEventListener("click", () => {
      state.selectedRestaurants.add(restaurant);
      state.restSearchTerm = "";
      const restSearch = document.getElementById("restaurant-search");
      if (restSearch) restSearch.value = "";
      // Render synchronously so the chip + `?rests=` URL param land immediately;
      // the filtered list re-render self-defers until raw offers load.
      render();
    });
    container.appendChild(item);
  });
}

function renderSelectedRestaurants() {
  const container = document.getElementById("selected-restaurants");
  if (!container) return;
  container.innerHTML = "";

  const count = state.selectedRestaurants.size;
  const countEl = document.getElementById("selected-rest-count");
  if (countEl) {
    countEl.textContent = count > 0 ? `(${count})` : "";
    countEl.style.display = count > 0 ? "" : "none";
  }

  Array.from(state.selectedRestaurants).sort((a, b) => a.localeCompare(b)).forEach((restaurant) => {
    const chip = document.createElement("div");
    chip.className = "s-chip";
    chip.innerHTML = `<span>${escapeHtml(restaurant)}</span>`;
    const rm = document.createElement("button");
    rm.className = "s-chip-remove";
    rm.type = "button";
    rm.textContent = "×";
    rm.setAttribute("aria-label", `Remove ${restaurant}`);
    rm.addEventListener("click", () => { state.selectedRestaurants.delete(restaurant); render(); });
    chip.appendChild(rm);
    container.appendChild(chip);
  });
}

/* ── RECOMMENDATIONS ── */
function renderRecommendations() {
  // Defensive backstop: if any code path mutated state into a non-default
  // scope (or a wallet/compare view) but left raw offers unloaded — e.g. the
  // onboarding flow in quiz.js calls renderRecommendations() directly after
  // setting a custom bill/card-type — lazy-load raw offers and re-render once
  // they arrive. Without this the compute functions (which guard on
  // !state.data) would quietly return empty results. At true default scope
  // this is a no-op and we render straight from the summary.
  if (state.viewMode === "overall") {
    renderOverallView();
    return;
  }
  if (!state.data && !isDefaultScope()) {
    withRawOffers(renderRecommendations);
    return;
  }

  // Always source cards through computeRecommendations(). Its default-scope
  // fast path reuses the precomputed summary while still layering on the
  // browser-only requirementStatus overlay used by the annual-fee UI.
  const results = computeRecommendations();
  const countEl   = document.getElementById("result-count");
  const rhSub     = document.getElementById("rh-sub");
  const emptyState = document.getElementById("empty-state");
  const topPick   = document.getElementById("top-pick");
  const resultsGrid = document.getElementById("results-grid");
  const summaryBest = document.getElementById("summary-best");

  updateCityChip();

  const countLabel = document.getElementById("result-count-label");
  if (state.viewMode === "my-wallet") {
    renderNextCardView(resultsGrid);
    renderCompareTray();
    return;
  }
  if (state.viewMode === "wallet") {
    renderWalletView(resultsGrid);
    renderCompareTray();
    return;
  }
  if (state.viewMode === "restaurants") {
    const scoped = getRestaurantDealsForRender();
    const walletOnly = state.ownedCards.size > 0 && state.restaurantWalletOnly;
    const deals = walletOnly
      ? scoped.filter((d) => d.wallet).sort((a, b) => b.wallet.saving - a.wallet.saving)
      : scoped.filter((d) => !d.walletOnlyRow);
    const top = deals[0];
    if (countEl) countEl.textContent = String(deals.length);
    if (countLabel) countLabel.textContent = walletOnly ? `${venueNoun()} your wallet covers` : `${venueNoun()} matched`;
    if (rhSub) rhSub.textContent = `${walletOnly ? "Deals on the cards you carry" : `${venueNoun(undefined, true)} with active deals`} · at ${formatCurrency(state.orderValue)} ${billWord()}`;
    if (summaryBest) summaryBest.textContent = top ? `${walletOnly ? top.wallet.discountLabel : top.discountLabel} at ${top.restaurant}` : "—";
    if (emptyState) emptyState.classList.add("hidden");
    if (topPick) topPick.innerHTML = "";
    renderRestaurantView(resultsGrid);
    renderCompareTray();
    return;
  }

  if (countEl) countEl.textContent = String(results.length);
  if (countLabel) countLabel.textContent = "cards matched";
  if (rhSub) {
    rhSub.textContent = `Ranked by estimated savings on a ${formatCurrency(state.orderValue)} ${billWord()}`;
  }
  if (summaryBest) {
    summaryBest.textContent = results.length > 0
      ? `${formatCurrency(results[0].avgExpectedSaving)} / ${visitWord()}`
      : "—";
  }

  if (results.length === 0) {
    setEmptyStateCopy();
    if (emptyState) emptyState.classList.remove("hidden");
    if (topPick) topPick.innerHTML = "";
    if (resultsGrid) resultsGrid.innerHTML = "";
    renderCompareTray();
    return;
  }

  if (emptyState) emptyState.classList.add("hidden");
  if (topPick) topPick.innerHTML = "";
  renderPagedResultCards(results, resultsGrid);
  renderCompareTray();
}

function normalizeBankKey(bank) {
  return String(bank || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function getBankLogoPath(bank) {
  const filename = BANK_LOGO_FILES[normalizeBankKey(bank)];
  return filename ? `./assets/bank-logos/${filename}` : "";
}

function getBankLogoInitials(bank) {
  return String(bank || "")
    .split(/\s+/)
    .map((part) => part[0] || "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

function renderBankLogo(bank, className) {
  const src = getBankLogoPath(bank);
  const initials = getBankLogoInitials(bank);
  return `
    <div class="${escapeAttr(className)}">
      ${src ? `<img class="bank-logo-image" src="${escapeAttr(src)}" alt="${escapeAttr(bank)} logo" width="42" height="42" loading="lazy" decoding="async" onerror="this.style.display='none'" />` : ""}
      ${src ? "" : `<span class="bank-logo-fallback">${escapeHtml(initials)}</span>`}
    </div>
  `;
}

function renderMetricLabel(label) {
  return `<span class="metric-label-text">${escapeHtml(label)}</span>`;
}

// Score → human tier. The score floor is 20 (baseScore = 20 + 80·R), so the
// effective range is 20–100, not 0–100. Calibration #7 shifts the tier
// thresholds to ~the same percentiles of the effective range as the original
// 80/70/50 cuts would have been on a true 0-100 scale: 84/72/56. We avoid
// "poor" / "bad" — Pakistani-consumer tone is encouraging.
function fitTier(score) {
  const s = Number(score) || 0;
  if (s >= 84) return "Excellent fit";
  if (s >= 72) return "Strong fit";
  if (s >= 56) return "Decent fit";
  return "Weak fit";
}

// Multiplier from `/outing` (the algorithm's native unit) into the current
// saving-window view. Used by the per-card hero saving so a user reading the
// list can see the same number in /outing, /month, or /year terms.
function savingWindowMultiplier() {
  const win = state.savingWindow || "yr";
  const op = state.outingsPerWeek || 1;
  if (win === "month") return op * 52 / 12;
  if (win === "yr") return op * 52;
  return 1; // outing
}

function savingWindowSuffix() {
  const win = state.savingWindow || "yr";
  return win === "yr" ? "/year" : win === "month" ? "/month" : `/${visitWord()}`;
}

// Format the per-card saving in whatever window the user picked, with lakh
// formatting kicking in automatically for amounts ≥ 1 lakh.
function formatCardSaving(avgPerOuting) {
  const v = Number(avgPerOuting) || 0;
  return `${formatCurrencyShort(v * savingWindowMultiplier())} ${savingWindowSuffix()}`;
}

// Mark whichever persona chip matches the current bill (if any) so the active
// state stays in sync if the user nudges the slider afterward.
function syncPersonaActive() {
  document.querySelectorAll(".persona-chip").forEach((c) => {
    const bill = Number(c.dataset.bill);
    c.classList.toggle("active", Number(state.orderValue) === bill);
  });
}

// Render the "sweet spot" note: where this card's cap kicks in. Below the
// saturation bill the user gets the headline %; above it the saving plateaus.
// For cards with no cap in scope, surface "uncapped at any bill size".
function renderSweetSpot(result) {
  if (result?.saturationBill === undefined) return "";
  const text = result.saturationBill === null
    ? `<strong>Uncapped saving</strong> at any ${billWord()} size`
    : `Sweet spot: <strong>${isFoodCategory() ? "bills" : "spend"} ≤ ${formatCurrency(result.saturationBill)}</strong>`;
  return `<div class="card-sweet-spot">${text}</div>`;
}

/* ── FEATURED CARD ── */
function renderFeaturedCard(result, container) {
  if (!container) return;
  const cardKey = buildCardKey(result.bank, result.card);
  const inCmp = state.compareList.includes(cardKey);
  const canCmp = state.compareList.length < 2 || inCmp;
  const isExpanded = state.expandedCard === cardKey;
  const score = Number(result.score).toFixed(1);
  const scorePct = Math.max(0, Math.min(100, Number(result.score) || 0));
  const sc = scoreColor(scorePct);
  const showEligibility = isEligibilityContextActive();
  const eligStatus = result.requirementStatus;
  // Score → integer + qualitative tier ("Excellent fit"). Previously every
  // row read "86.2 / 100" which is precise but unhelpful to non-technical
  // users — the tier is the load-bearing signal.
  const scoreInt = Math.round(Number(result.score) || 0);
  const tier = fitTier(result.score);
  container.innerHTML = `
    <article class="card-item card-item--featured${inCmp ? " in-compare" : ""}" id="feat-${escapeAttr(cardKey)}" data-key="${escapeAttr(cardKey)}" tabindex="0" role="button" aria-label="Open details for ${escapeAttr(result.card)} from ${escapeAttr(result.bank)}">
      <div class="card-row card-row--clickable">
        <div class="card-rank rank-1">1</div>
        ${renderBankLogo(result.bank, "card-logo-box")}
        <div class="card-info">
          <div class="card-badges">
            <span class="badge-top-pick">#1 TOP PICK</span>
            ${showEligibility ? renderEligibilityBadge(eligStatus) : ""}
            ${renderBnplBadge(result.bank, result.card)}
          </div>
          <div class="card-name">${escapeHtml(result.card)}</div>
          <div class="card-bank">${escapeHtml(result.bank)}</div>
        </div>
        <div class="score-box">
          <div class="score-num" style="color:${sc}">${scoreInt}</div>
          <div class="score-label">${escapeHtml(tier)}</div>
          <div class="score-bar">
            <div class="score-bar-fill" style="width:${scorePct}%;background:${sc}"></div>
          </div>
        </div>
        <button class="btn-compare${inCmp ? " active" : ""}" data-key="${escapeAttr(cardKey)}" type="button"
          ${!canCmp ? "disabled" : ""}>
          ${inCmp ? `${phIcon("check")} Comparing` : "+ Compare"}
        </button>
      </div>
      <div class="card-stats-row card-stats-row--clickable">
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel("Saving")}</div>
          <div class="cs-v green">${formatCardSaving(result.avgExpectedSaving)}</div>
        </div>
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel(venueNoun(undefined, true))}</div>
          <div class="cs-v">${result.coveredVenueCount} of ${result.totalVenueCount}</div>
        </div>
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel("Annual fee")}</div>
          <div class="cs-v">${result.requirementStatus?.annualFeePkr === null ? "Not listed" : result.requirementStatus?.annualFeePkr === 0 ? "Free" : result.requirementStatus?.annualFeeWaiverRule ? `${formatCurrencyShort(result.requirementStatus?.annualFeePkr)} (waivable)` : formatCurrencyShort(result.requirementStatus?.annualFeePkr)}</div>
        </div>
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel("Typical cap")}</div>
          <div class="cs-v">${result.medianCap !== null ? formatCurrencyShort(result.medianCap) : "No cap"}</div>
        </div>
      </div>
      ${renderSweetSpot(result)}
      ${isExpanded ? renderCardDetail(result) : ""}
    </article>
  `;

  container.querySelector(".btn-compare")?.addEventListener("click", (e) => {
    toggleCompare(e.currentTarget.dataset.key);
  });
  bindCardOpenInteractions(container.querySelector("article"), cardKey);
}

/* ── RESULT CARDS ── */
function renderResultCards(results, container) {
  if (!container) return;
  container.innerHTML = "";

  results.forEach((result, index) => {
    const page = state.pagination.results || 1;
    const rank = page === 1 ? index + 2 : PAGE_ONE_LIST_SIZE + (page - 2) * PAGINATION_ITEMS_PER_PAGE + index + 2;
    const cardKey = buildCardKey(result.bank, result.card);
    const inCmp = state.compareList.includes(cardKey);
    const canCmp = state.compareList.length < 2 || inCmp;
    const isExpanded = state.expandedCard === cardKey;
    const scoreInt = Math.round(Number(result.score) || 0);
    const tier = fitTier(result.score);
    const scorePct = Math.max(0, Math.min(100, Number(result.score) || 0));
    const sc = scoreColor(scorePct);
    const showEligibility = isEligibilityContextActive();
    const eligStatus = result.requirementStatus;

    const article = document.createElement("article");
    article.className = `card-item${inCmp ? " in-compare" : ""}`;
    article.style.animationDelay = `${index * 0.05}s`;
    article.dataset.key = cardKey;

    article.innerHTML = `
      <div class="card-row card-row--clickable">
        <div class="card-rank${rank === 1 ? " rank-1" : ""}">${rank}</div>
        ${renderBankLogo(result.bank, "card-logo-box")}
        <div class="card-info">
          <div class="card-badges">
            ${showEligibility ? renderEligibilityBadge(eligStatus) : ""}
            ${renderBnplBadge(result.bank, result.card)}
          </div>
          <div class="card-name">${escapeHtml(result.card)}</div>
          <div class="card-bank">${escapeHtml(result.bank)}</div>
        </div>
        <div class="score-box">
          <div class="score-num" style="color:${sc}">${scoreInt}</div>
          <div class="score-label">${escapeHtml(tier)}</div>
          <div class="score-bar">
            <div class="score-bar-fill" style="width:${scorePct}%;background:${sc}"></div>
          </div>
        </div>
        <button class="btn-compare${inCmp ? " active" : ""}" data-key="${escapeAttr(cardKey)}" type="button"
          ${!canCmp ? "disabled" : ""}>
          ${inCmp ? `${phIcon("check")} Comparing` : "+ Compare"}
        </button>
      </div>
      <div class="card-stats-row card-stats-row--clickable">
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel("Saving")}</div>
          <div class="cs-v green">${formatCardSaving(result.avgExpectedSaving)}</div>
        </div>
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel(venueNoun(undefined, true))}</div>
          <div class="cs-v">${result.coveredVenueCount} of ${result.totalVenueCount}</div>
        </div>
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel("Annual fee")}</div>
          <div class="cs-v">${result.requirementStatus?.annualFeePkr === null ? "Not listed" : result.requirementStatus?.annualFeePkr === 0 ? "Free" : result.requirementStatus?.annualFeeWaiverRule ? `${formatCurrencyShort(result.requirementStatus?.annualFeePkr)} (waivable)` : formatCurrencyShort(result.requirementStatus?.annualFeePkr)}</div>
        </div>
        <div class="card-stat">
          <div class="cs-l">${renderMetricLabel("Typical cap")}</div>
          <div class="cs-v">${result.medianCap !== null ? formatCurrencyShort(result.medianCap) : "No cap"}</div>
        </div>
      </div>
      ${renderSweetSpot(result)}
      ${isExpanded ? renderCardDetail(result) : ""}
    `;

    article.querySelector(".btn-compare")?.addEventListener("click", (e) => {
      toggleCompare(e.currentTarget.dataset.key);
    });
    article.tabIndex = 0;
    article.setAttribute("role", "button");
    article.setAttribute("aria-label", `Open details for ${result.card} from ${result.bank}`);
    bindCardOpenInteractions(article, cardKey);

    container.appendChild(article);
  });
}

function renderPagedResultCards(results, container) {
  if (!container) return;

  const allResults = results;
  container.innerHTML = `
    <div class="cards-view-search-wrap">
      <input type="search" class="s-search cards-view-search" placeholder="Search ${results.length} cards…" autocomplete="off" />
    </div>
    <div class="cards-view-featured"></div>
    <div class="cards-view-list"></div>
  `;

  const searchInput = container.querySelector(".cards-view-search");
  const featuredContainer = container.querySelector(".cards-view-featured");
  const listContainer = container.querySelector(".cards-view-list");

  const renderRows = () => {
    const term = searchInput?.value.trim().toLowerCase() || "";
    const filteredResults = allResults.filter((result) =>
      !term || result.card.toLowerCase().includes(term) || result.bank.toLowerCase().includes(term)
    );

    if ((state.pagination.results || 1) === 1 && filteredResults.length > 0) {
      renderFeaturedCard(filteredResults[0], featuredContainer);
    } else {
      featuredContainer.innerHTML = "";
    }

    const restResults = filteredResults.slice(1);
    const listPageSize = (state.pagination.results || 1) === 1 ? PAGE_ONE_LIST_SIZE : PAGINATION_ITEMS_PER_PAGE;
    const pageData = paginateItems(restResults, "results", listPageSize);
    renderResultCards(pageData.items, listContainer);
    if (pageData.totalPages > 1) {
      listContainer.insertAdjacentHTML("beforeend", renderPaginationControls("results", pageData));
      bindPaginationControls(listContainer, "results", () => renderRows());
    }
  };

  searchInput?.addEventListener("input", () => {
    state.pagination.results = 1;
    renderRows();
  });
  attachSearchTracking(searchInput, "cards_list", () => {
    const term = searchInput?.value.trim().toLowerCase() || "";
    return allResults.filter(
      (r) => !term || r.card.toLowerCase().includes(term) || r.bank.toLowerCase().includes(term)
    ).length;
  });

  renderRows();
}

function renderOrderTypeBadges(orderTypes) {
  const styles = {
    "Dine-In": "background:#e8f5e9;color:#2e7d32",
    "Takeaway": "background:#e3f2fd;color:#1565c0",
    "Delivery": "background:#fff3e0;color:#e65100",
    "In-Store": "background:#e8f5e9;color:#2e7d32",
    "Online": "background:#ede7f6;color:#5e35b1",
  };
  if (!orderTypes || !orderTypes.length) return "";
  return orderTypes.map((ot) => `<span class="pill" style="${styles[ot] || "background:#f5f5f5;color:#666"}">${escapeHtml(ot)}</span>`).join("");
}

function renderCardDetail(result) {
  const eligStatus = result.requirementStatus;
  const showEligibility = isEligibilityContextActive();
  const reqSummary = renderRequirementSummary(eligStatus, { showStatus: showEligibility });

  const detailItems = [
    { icon: phIcon("map-pin"), l: "Available In", v: getTopPickCitiesLabel() },
    { icon: phIcon("storefront"), l: `${venueNoun(undefined, true)} Matched`, v: `${result.coveredVenueCount} of ${result.totalVenueCount}` },
    { icon: phIcon("credit-card"), l: "Annual Fees", v: result.requirementStatus?.annualFeePkr === null ? "Not listed" : result.requirementStatus?.annualFeePkr === 0 ? "Free" : result.requirementStatus?.annualFeeWaiverRule ? `${formatCurrency(result.requirementStatus?.annualFeePkr)} (waivable)` : formatCurrency(result.requirementStatus?.annualFeePkr) },
    { icon: phIcon("coins"), l: "Typical Cap", v: result.medianCap !== null ? formatCurrency(result.medianCap) : "No cap listed" },
  ];

  const topMatchesHtml = result.topMatches.slice(0, 3).map((match) => `
    <div class="match-item">
      <div>
        <div class="match-rest">${escapeHtml(match.restaurant)} <span style="color:var(--muted);font-weight:400">(${escapeHtml(match.city)})</span></div>
        <div class="match-meta">${escapeHtml(match.discountLabel)} · ${escapeHtml(match.daysLabel)}${match.offerTitle ? ` · ${escapeHtml(match.offerTitle)}` : ""}</div>
        ${match.offerDescription ? `<div class="match-meta" style="margin-top:1px"><span class="offer-detail-toggle" onclick="var d=this.nextElementSibling;d.style.display=d.style.display==='none'?'block':'none';this.textContent=d.style.display==='none'?'Details ▾':'Details ▴'" style="cursor:pointer;font-size:11px;color:var(--brand);font-weight:600">Details ▾</span><div class="offer-detail-text" style="display:none;font-size:11px;color:var(--muted);margin-top:2px;line-height:1.4">${escapeHtml(match.offerDescription)}</div></div>` : ""}
        ${match.orderTypes && match.orderTypes.length ? `<div class="match-meta" style="margin-top:2px">${renderOrderTypeBadges(match.orderTypes)}</div>` : ""}
      </div>
      <div class="match-saving">${formatCurrency(match.expectedSaving)}</div>
    </div>
  `).join("");

  return `
    <div class="card-detail">
      <div class="card-detail-grid">
        ${detailItems.map((i) => `
          <div class="card-detail-item">
            <span class="card-detail-icon">${i.icon}</span>
            <div>
              <div class="card-detail-label">${i.l}</div>
              <div class="card-detail-val">${escapeHtml(i.v)}</div>
            </div>
          </div>
        `).join("")}
      </div>
      ${reqSummary}
      ${topMatchesHtml ? `<div class="card-detail-matches">${topMatchesHtml}</div>` : ""}
    </div>
  `;
}

/* ── COMPARE ── */
function buildCardKey(bank, card) {
  return `${bank} || ${card}`;
}

function toggleCompare(cardKey) {
  const [bank, card] = cardKey.split(" || ");
  const wasIn = state.compareList.includes(cardKey);
  if (wasIn) {
    state.compareList = state.compareList.filter((k) => k !== cardKey);
  } else if (state.compareList.length < 2) {
    state.compareList.push(cardKey);
  }
  trackEvent(wasIn ? "compare_remove" : "compare_add", {
    bank,
    card,
    compare_size_after: state.compareList.length,
  });
  renderCompareTray();
  renderRecommendations();
}

function renderCompareTray() {
  const tray = document.getElementById("cmp-tray");
  const cardsEl = document.getElementById("cmp-tray-cards");
  const openBtn = document.getElementById("btn-compare-open");
  if (!tray || !cardsEl) return;

  if (state.compareList.length === 0) {
    tray.style.display = "none";
    document.body.classList.remove("has-cmp-tray");
    return;
  }

  tray.style.display = "";
  document.body.classList.add("has-cmp-tray");
  cardsEl.innerHTML = "";

  state.compareList.forEach((key) => {
    const [bank, card] = key.split(" || ");
    const shortName = card.split(" ").slice(0, 3).join(" ");
    const cardEl = document.createElement("div");
    cardEl.className = "cmp-tray-card";
    cardEl.innerHTML = `
      <div class="cmp-tray-card-text">
        <span class="cmp-tray-card-name">${escapeHtml(shortName)}</span>
        <span class="cmp-tray-card-bank">${escapeHtml(bank)}</span>
      </div>
      <button class="cmp-tray-card-remove" type="button" data-key="${escapeAttr(key)}" aria-label="Remove ${escapeAttr(card)} from compare">×</button>
    `;
    cardEl.querySelector(".cmp-tray-card-remove").addEventListener("click", (e) => {
      toggleCompare(e.currentTarget.dataset.key);
    });
    cardsEl.appendChild(cardEl);
  });

  if (state.compareList.length < 2) {
    const placeholder = document.createElement("div");
    placeholder.className = "cmp-tray-placeholder";
    placeholder.textContent = "+ Pick 1 more card";
    cardsEl.appendChild(placeholder);
  }

  if (openBtn) {
    openBtn.style.display = state.compareList.length === 2 ? "" : "none";
  }
}

function openCompareModal() {
  if (state.compareList.length !== 2) return;
  // The head-to-head modal recomputes via computeRecommendations() and reads
  // per-offer data (getCompareRestaurantRows / getExclusiveRestaurantCounts)
  // → ensure raw offers are loaded first.
  if (!state.data) {
    withRawOffers(openCompareModal);
    return;
  }
  state.pagination.compareRestaurants = 1;
  const results = computeRecommendations();
  const cards = state.compareList.map((key) => {
    const [bank, card] = key.split(" || ");
    return results.find((r) => r.bank === bank && r.card === card);
  }).filter(Boolean);
  if (cards.length < 2) return;

  const modal = document.getElementById("compare-modal");
  const inner = document.getElementById("compare-modal-inner");
  if (!modal || !inner) return;

  const annuals   = cards.map((c) => c.avgExpectedSaving * state.outingsPerWeek * 52);
  const fees      = cards.map((c) => c.requirementStatus?.annualFeePkr ?? null);
  const nets      = cards.map((c, i) => fees[i] !== null ? annuals[i] - fees[i] : null);
  const salaryReqs = cards.map((c) => c.requirementStatus?.salaryReq ?? null);
  const balanceReqs = cards.map((c) => c.requirementStatus?.balanceReq ?? null);
  const excl      = getExclusiveRestaurantCounts(state.compareList[0], state.compareList[1]);
  const restaurantRows = getCompareRestaurantRows(state.compareList);
  const outLabel  = formatFrequency(state.outingsPerWeek);

  const rows = [
    { l: "Fit Score",                 vals: cards.map((c) => Number(c.score)),         fmt: (v)    => `${v.toFixed(1)} / 100`,               compare: "high" },
    { l: `Est. Saving / ${visitWord()}`,      vals: cards.map((c) => c.avgExpectedSaving),     fmt: (v)    => formatSavingsAmount(v, { per: "outing" }), compare: "high" },
    { l: `Annual saving (${outLabel})`, vals: annuals,                                 fmt: (v)    => formatSavingsAmount(v, { per: "yr" }), compare: "high" },
    { l: "Salary requirement",        vals: salaryReqs,                                fmt: (v, i) => formatRequirementFieldValue(cards[i].requirementStatus, "salaryReq"), compare: "none" },
    { l: "Minimum balance",           vals: balanceReqs,                               fmt: (v, i) => formatRequirementFieldValue(cards[i].requirementStatus, "balanceReq"), compare: "none" },
    { l: "Annual fee",                vals: fees,                                      fmt: (v, i) => formatRequirementFieldValue(cards[i].requirementStatus, "annualFeePkr"), compare: "low" },
    nets.some((n) => n !== null)
      ? { l: "Net annual saving",     vals: nets,                                      fmt: (v, i) => nets[i] !== null ? formatSavingsAmount(nets[i], { per: "yr", signed: true }) : "—", compare: "high" }
      : null,
    { l: `${venueNoun(undefined, true)} Matched`,       vals: cards.map((c) => c.coveredVenueCount),    fmt: (v)    => `${v} of ${cards[0].totalVenueCount}`,  compare: "high" },
    { l: `Exclusive ${venueNoun()}`,     vals: excl,                                     fmt: (v)    => v > 0 ? `${v} only here` : "—",         compare: "high" },
    { l: "Avg Discount",              vals: cards.map((c) => c.averageDiscount || 0), fmt: (v)    => v ? v.toFixed(1) + "%" : "—",           compare: "high" },
  ].filter(Boolean);

  let wins = [0, 0];
  rows.forEach((row) => {
    const winner = getCompareRowWinner(row);
    if (winner >= 0) wins[winner]++;
  });

  const wi = wins[0] === wins[1]
    ? (cards[0].score >= cards[1].score ? 0 : 1)
    : (wins[0] > wins[1] ? 0 : 1);
  const headColors  = ["var(--brand-light)", "var(--green-light)"];
  const labelColors = ["var(--brand)", "var(--green)"];
  const sharedRestaurantCount = restaurantRows.filter((row) => row.entries.every(Boolean)).length;

  inner.innerHTML = `
    <div class="cmp-modal-head">
      <div class="cmp-modal-title">Head-to-Head</div>
      <button class="btn-modal-close" id="btn-cmp-modal-close" type="button">×</button>
    </div>
    <div style="overflow-x:auto">
      <div class="cmp-grid">
        <div class="cmp-head-cell" style="background:var(--surface2);display:flex;align-items:center;justify-content:center">
          <div style="text-align:center">
            <div class="cmp-tally">${wins[0]}<span style="color:var(--muted2);font-size:14px;margin:0 6px;font-weight:400">vs</span>${wins[1]}</div>
            <div style="font-size:9px;font-weight:600;color:var(--muted);letter-spacing:.06em;text-transform:uppercase;margin-top:2px">categories</div>
          </div>
        </div>
        ${cards.map((c, i) => `
          <div class="cmp-head-cell" style="background:${headColors[i]};border-left:1px solid var(--line)">
            <div style="font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:${labelColors[i]};margin-bottom:4px">${wins[i] > wins[1 - i] ? `${phIcon("trophy")} ` : ""}Card ${i + 1}</div>
            <div style="font-weight:800;font-size:14px;color:var(--ink);line-height:1.25;margin-bottom:1px">${escapeHtml(c.card)}</div>
            <div style="font-size:11px;color:var(--muted);margin-bottom:10px">${escapeHtml(c.bank)}</div>
            <div class="cmp-score-bar-wrap"><div class="cmp-score-bar-fill" style="width:${Number(c.score)}%;background:${scoreColor(c.score)}"></div></div>
            <div style="font-size:22px;font-weight:800;color:${scoreColor(c.score)};line-height:1.2">${Number(c.score).toFixed(1)}<span style="font-size:11px;color:var(--muted);font-weight:500"> / 100</span></div>
          </div>
        `).join("")}
        ${rows.map((row, ri) => {
          const winner = getCompareRowWinner(row);
          const bg = ri % 2 === 0 ? "var(--surface2)" : "var(--surface)";
          return `
            <div class="cmp-label-cell" style="background:${bg}">${escapeHtml(row.l)}</div>
            ${cards.map((c, i) => {
              const cellClass = winner === i ? " win" : (winner >= 0 ? " lose" : "");
              return `
              <div class="cmp-val-cell${cellClass}" style="background:${bg}">
                ${row.fmt(row.vals[i], i)}
                ${winner === i ? `<span class="better-badge">Better</span>` : ""}
              </div>`;
            }).join("")}
          `;
        }).join("")}
      </div>
    </div>
    <div class="cmp-winner-bar">
      <div class="cmp-winner-label">${phIcon("trophy")} Verdict</div>
      <div style="flex:1">
        <div class="cmp-winner-name">${escapeHtml(cards[wi].card)}</div>
        <div class="cmp-winner-sub">
          Wins ${wins[wi]} of ${wins[0] + wins[1]} categories ·
          ~${formatCurrency(annuals[wi])} / yr ·
          ${Math.abs(cards[0].score - cards[1].score).toFixed(1)} pts ahead
        </div>
      </div>
      ${getBankApplyUrl(cards[wi].bank) ? `<a class="btn-apply btn-apply-sm" href="${escapeAttr(getBankApplyUrl(cards[wi].bank))}" target="_blank" rel="noopener">Apply →</a>` : ""}
    </div>
    <div class="cmp-rest-section">
      <div class="cmp-rest-head">
        <div>
          <div class="cmp-rest-title">${venueNoun(1, true)}-by-${venueNoun(1)}</div>
          <div class="cmp-rest-sub">${sharedRestaurantCount} shared ${venueNoun()} · ${restaurantRows.length} total in current filters</div>
        </div>
      </div>
      <input type="search" class="s-search cmp-rest-search" placeholder="Search ${venueNoun()}…" autocomplete="off" />
      <div class="cmp-rest-list"></div>
    </div>
  `;

  modal.style.display = "flex";
  inner.querySelector("#btn-cmp-modal-close")?.addEventListener("click", closeCompareModal);
  const searchInput = inner.querySelector(".cmp-rest-search");
  const listContainer = inner.querySelector(".cmp-rest-list");
  const renderRows = () => {
    const term = searchInput?.value.trim().toLowerCase() || "";
    const filteredRows = restaurantRows.filter((row) => !term || `${row.restaurant} ${row.city}`.toLowerCase().includes(term));
    renderCompareRestaurantRows(listContainer, cards, filteredRows);
  };
  searchInput?.addEventListener("input", () => {
    state.pagination.compareRestaurants = 1;
    renderRows();
  });
  renderRows();
}

function closeCompareModal() {
  const modal = document.getElementById("compare-modal");
  if (modal) modal.style.display = "none";
}

/* ── QUIZ + LANDING + ONBOARDING ── moved to assets/quiz.js */



/* ── COMPUTE RECOMMENDATIONS ── moved to assets/algorithms.js */


/* ── NEXT CARD: OWNED CARDS INPUT + VIEW ── */
function getAllCardsCatalog() {
  // List of { cardKey, bank, card, cardCategory } across the entire dataset.
  if (!state.data) return [];
  const seen = new Map();
  state.data.offers.forEach((offer) => {
    const key = buildCardKey(offer.bank, offer.card);
    if (!seen.has(key)) {
      seen.set(key, { cardKey: key, bank: offer.bank, card: offer.card, cardCategory: offer.cardCategory || "other" });
    }
  });
  return Array.from(seen.values()).sort((a, b) => {
    const bankCmp = a.bank.localeCompare(b.bank);
    if (bankCmp !== 0) return bankCmp;
    return a.card.localeCompare(b.card);
  });
}

function renderOwnedCardsPanel(container, walletStats) {
  if (!container) return;
  const catalog = getAllCardsCatalog();
  const ownedCount = state.ownedCards.size;
  const hasWallet = ownedCount > 0;
  // Wrapped in <details> so users can tap to expand the 4-stat grid.
  // Defaults: open on desktop (familiar), closed on phones (so Best
  // Next Card surfaces near the top). User toggles persist on either
  // platform via state.walletSummaryOpen.
  const isPhoneSummary = typeof window !== "undefined"
    && window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
  const summaryOpen = state.walletSummaryOpen === undefined
    ? !isPhoneSummary
    : state.walletSummaryOpen;

  const summaryHtml = hasWallet && walletStats ? `
    <details class="mw-summary mw-summary--collapsible"${summaryOpen ? " open" : ""}>
      <summary class="mw-summary-head">
        <div class="mw-summary-title-wrap">
          <div class="mw-summary-kicker">Your wallet</div>
          <div class="mw-summary-title">${ownedCount} card${ownedCount === 1 ? "" : "s"} · ${formatCurrency(walletStats.perOuting)} / ${visitWord()}</div>
          <div class="mw-summary-sub">What your current wallet is worth at a ${formatCurrency(state.orderValue)} bill</div>
        </div>
        <span class="mw-summary-chevron" aria-hidden="true">
          <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 4.5 L6 8.5 L10 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
      </summary>
      <div class="mw-summary-stats">
        <div class="mw-summary-stat">
          <div class="mw-summary-l">Savings / ${visitWord()}</div>
          <div class="mw-summary-v green">${formatCurrency(walletStats.perOuting)}</div>
        </div>
        <div class="mw-summary-stat">
          <div class="mw-summary-l">${venueNoun(undefined, true)} Covered</div>
          <div class="mw-summary-v">${walletStats.coveredVenues} of ${walletStats.venueCount}</div>
        </div>
        <div class="mw-summary-stat">
          <div class="mw-summary-l">Est. Yearly</div>
          <div class="mw-summary-v green">${formatCurrency(walletStats.yearly)}</div>
        </div>
        <div class="mw-summary-stat">
          <div class="mw-summary-l">Total Annual Fees</div>
          <div class="mw-summary-v">${walletStats.annualFee === 0 ? (walletStats.feeUnknown ? "Not listed" : "Free") : formatCurrency(walletStats.annualFee)}${walletStats.feeUnknown ? ` <span class="wo-fee-note">(some unlisted)</span>` : ""}</div>
        </div>
      </div>
    </details>
  ` : "";

  // When the wallet already has cards, collapse the manage panel by default
  // so the "Best Next Card" sits closer to the fold. The wallet-empty case
  // is always expanded since adding cards is the primary action there.
  const isCollapsible = hasWallet;
  const isExpanded = !isCollapsible || state.manageWalletExpanded;
  const headTag = isCollapsible ? "button" : "div";
  const headAttrs = isCollapsible
    ? `type="button" id="nc-manage-toggle" class="nc-setup-head nc-setup-head--toggle" aria-expanded="${isExpanded}" aria-controls="nc-setup-body"`
    : `class="nc-setup-head"`;

  // When the wallet has cards, also surface the restaurants it covers.
  // Collapsed by default; counts shown in the head so users see scope without expanding.
  const walletRestExpanded = !!state.walletRestaurantsExpanded;
  const restaurantsHtml = hasWallet && walletStats ? `
    <div class="nc-setup${!walletRestExpanded ? " nc-setup--collapsed" : ""} nc-rests">
      <button type="button" id="nc-rests-toggle" class="nc-setup-head nc-setup-head--toggle" aria-expanded="${walletRestExpanded}" aria-controls="nc-rests-body">
        <div class="nc-setup-title-wrap">
          <div class="nc-setup-kicker">Coverage</div>
          <div class="nc-setup-title">${venueNoun(undefined, true)} your wallet covers</div>
        </div>
        <div class="nc-setup-meta">
          <div class="nc-setup-count">
            <span class="nc-setup-count-num">${walletStats.coveredVenues}</span>
            <span class="nc-setup-count-label">of ${walletStats.venueCount}</span>
          </div>
          <span class="nc-setup-chevron${walletRestExpanded ? " nc-setup-chevron--open" : ""}" aria-hidden="true"><svg viewBox="0 0 12 12" width="10" height="10"><path d="M2 4.5 L6 8.5 L10 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
        </div>
      </button>
      <div id="nc-rests-body" class="nc-setup-body" ${!walletRestExpanded ? "hidden" : ""}>
        <div id="nc-rests-list" class="nc-rests-list"></div>
      </div>
    </div>
  ` : "";

  container.innerHTML = `
    ${summaryHtml}
    <div class="nc-setup${isCollapsible && !isExpanded ? " nc-setup--collapsed" : ""}">
      <${headTag} ${headAttrs}>
        <div class="nc-setup-title-wrap">
          <div class="nc-setup-kicker">${hasWallet ? "Manage" : "Step 1"}</div>
          <div class="nc-setup-title">${hasWallet ? "Cards in your wallet" : "Add the cards you carry"}</div>
          ${hasWallet
            ? ""
            : `<div class="nc-setup-sub">Tell us what's in your wallet and we'll show what it's worth and the best card to add next.</div>`}
        </div>
        <div class="nc-setup-meta">
          <div class="nc-setup-count">
            <span class="nc-setup-count-num">${ownedCount}</span>
            <span class="nc-setup-count-label">${ownedCount === 1 ? "card" : "cards"}</span>
          </div>
          ${isCollapsible ? `<span class="nc-setup-chevron${isExpanded ? " nc-setup-chevron--open" : ""}" aria-hidden="true"><svg viewBox="0 0 12 12" width="10" height="10"><path d="M2 4.5 L6 8.5 L10 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>` : ""}
        </div>
      </${headTag}>
      <div id="nc-setup-body" class="nc-setup-body" ${isCollapsible && !isExpanded ? `hidden` : ""}>
        <input id="nc-owned-search" class="s-search nc-search" type="search" placeholder="Search bank or card name…" autocomplete="off" value="${escapeAttr(state.ownedCardSearchTerm)}" />
        <div id="nc-owned-results" class="s-search-results nc-search-results"></div>
        <div id="nc-owned-chips" class="s-chips nc-chips"></div>
        <div class="nc-setup-foot">
          ${ownedCount > 0 ? `<button id="nc-owned-clear" class="nc-clear-btn" type="button">Clear all</button>` : `<span class="nc-setup-foot-spacer"></span>`}
          <span class="nc-crosslink">
            Want to redesign? <a class="nc-link" id="nc-go-wallet">Try Build Wallet →</a>
          </span>
        </div>
      </div>
    </div>
    ${restaurantsHtml}
  `;

  // Persist the YOUR WALLET <details> open/closed state so the user's
  // toggle survives re-renders. The first render defaults to closed on
  // phones (set via openMobileSheet/initial flag below) and open on
  // desktop via CSS.
  container.querySelector(".mw-summary--collapsible")?.addEventListener("toggle", (e) => {
    state.walletSummaryOpen = e.target.open;
  });

  document.getElementById("nc-manage-toggle")?.addEventListener("click", () => {
    state.manageWalletExpanded = !state.manageWalletExpanded;
    render();
  });

  document.getElementById("nc-rests-toggle")?.addEventListener("click", () => {
    state.walletRestaurantsExpanded = !state.walletRestaurantsExpanded;
    render();
  });

  const searchInput = document.getElementById("nc-owned-search");
  if (searchInput) {
    searchInput.addEventListener("input", (e) => {
      state.ownedCardSearchTerm = e.target.value.trim();
      renderOwnedCardsResults(catalog);
    });
  }
  document.getElementById("nc-owned-clear")?.addEventListener("click", () => {
    state.ownedCards = new Set();
    state.ownedCardSearchTerm = "";
    render();
  });
  document.getElementById("nc-go-wallet")?.addEventListener("click", () => {
    state.viewMode = "wallet";
    render();
  });

  renderOwnedCardsResults(catalog);
  renderOwnedCardsChips();
  if (hasWallet && walletRestExpanded) renderWalletRestaurants();
}

function renderWalletRestaurants() {
  const container = document.getElementById("nc-rests-list");
  if (!container) return;
  const deals = computeWalletRestaurantCoverage();
  if (deals.length === 0) {
    container.innerHTML = `<div class="cd-empty">No ${venueNoun()} in scope match your wallet under the current filters.</div>`;
    return;
  }
  renderRestaurantDealRows(container, deals);
}

function renderOwnedCardsResults(catalogArg) {
  const container = document.getElementById("nc-owned-results");
  if (!container) return;
  const catalog = catalogArg || getAllCardsCatalog();
  const term = (state.ownedCardSearchTerm || "").toLowerCase();
  // Only show suggestions when user is typing; otherwise keep panel quiet.
  if (!term) { container.innerHTML = ""; return; }
  const results = catalog
    .filter((c) => !state.ownedCards.has(c.cardKey))
    .filter((c) => `${c.bank} ${c.card}`.toLowerCase().includes(term))
    .slice(0, 12);

  container.innerHTML = "";
  if (results.length === 0) {
    container.innerHTML = `<div class="nc-search-empty">No matching cards. Try the bank name (e.g. "HBL", "Meezan").</div>`;
    return;
  }
  results.forEach((entry) => {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "s-search-item nc-search-item";
    item.innerHTML = `
      <span class="nc-search-item-text">
        <span class="nc-search-item-card">${escapeHtml(entry.card)}</span>
        <span class="nc-search-item-bank">${escapeHtml(entry.bank)}</span>
      </span>
      <span class="s-search-item-add">+ Add</span>
    `;
    item.addEventListener("click", () => {
      state.ownedCards.add(entry.cardKey);
      state.ownedCardSearchTerm = "";
      const input = document.getElementById("nc-owned-search");
      if (input) input.value = "";
      trackEvent("owned_card_add", { bank: entry.bank, total: state.ownedCards.size });
      render();
    });
    container.appendChild(item);
  });
}

function renderOwnedCardsChips() {
  const container = document.getElementById("nc-owned-chips");
  if (!container) return;
  container.innerHTML = "";
  Array.from(state.ownedCards).sort().forEach((cardKey) => {
    const [bank, card] = cardKey.split(" || ");
    const chip = document.createElement("div");
    chip.className = "s-chip nc-chip nc-chip--clickable";
    chip.setAttribute("role", "button");
    chip.setAttribute("tabindex", "0");
    chip.setAttribute("aria-label", `View details for ${card}`);
    chip.innerHTML = `
      <span class="nc-chip-text">
        <span class="nc-chip-card">${escapeHtml(card || "")}</span>
        <span class="nc-chip-bank">${escapeHtml(bank || "")}</span>
      </span>
    `;
    chip.addEventListener("click", () => openCardDetail(cardKey));
    chip.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openCardDetail(cardKey); }
    });
    const rm = document.createElement("button");
    rm.className = "s-chip-remove";
    rm.type = "button";
    rm.textContent = "×";
    rm.setAttribute("aria-label", `Remove ${card}`);
    rm.addEventListener("click", (e) => {
      e.stopPropagation();
      state.ownedCards.delete(cardKey);
      render();
    });
    chip.appendChild(rm);
    container.appendChild(chip);
  });
}

function renderNextCardView(resultsGrid) {
  const setupContainer = document.getElementById("next-card-setup");
  const topPick = document.getElementById("top-pick");
  const emptyState = document.getElementById("empty-state");
  const countEl = document.getElementById("result-count");
  const countLabel = document.getElementById("result-count-label");
  const rhSub = document.getElementById("rh-sub");
  const summaryBest = document.getElementById("summary-best");

  // No cards entered → setup-only state (no wallet stats yet)
  if (state.ownedCards.size === 0) {
    renderOwnedCardsPanel(setupContainer, null);
    if (countEl) countEl.textContent = "0";
    if (countLabel) countLabel.textContent = "cards in wallet";
    if (rhSub) rhSub.textContent = "Add the cards you carry to find the best next one";
    if (summaryBest) summaryBest.textContent = "—";
    if (emptyState) {
      emptyState.classList.remove("hidden");
      const title = document.getElementById("empty-state-title");
      const msg = document.getElementById("empty-state-message");
      if (title) title.textContent = "Your wallet is empty";
      if (msg) msg.textContent = "Add the cards you carry above. You'll see what your wallet is worth and the best next card to add.";
    }
    if (topPick) topPick.innerHTML = "";
    if (resultsGrid) resultsGrid.innerHTML = "";
    return;
  }

  const { ranked, stats } = computeNextCardRecommendations();
  const walletStats = stats.wallet;
  renderOwnedCardsPanel(setupContainer, walletStats);

  if (countEl) countEl.textContent = String(ranked.length);
  if (countLabel) countLabel.textContent = "cards would add extra savings";
  if (rhSub) {
    rhSub.textContent = `Your wallet covers ${walletStats.coveredVenues} of ${walletStats.venueCount} ${venueNoun(walletStats.venueCount)} · ${formatCurrency(state.orderValue)} ${billWord()}`;
  }
  if (summaryBest) {
    summaryBest.textContent = ranked.length > 0
      ? `+${formatCurrency(ranked[0].avgDeltaPerOuting)} / ${visitWord()}`
      : "—";
  }

  if (ranked.length === 0) {
    if (emptyState) {
      emptyState.classList.remove("hidden");
      const title = document.getElementById("empty-state-title");
      const msg = document.getElementById("empty-state-message");
      if (title) title.textContent = "Nothing beats what you already have";
      if (msg) msg.textContent = "With your current wallet, no other card meaningfully improves savings in this scope. Try a different city, broader days, or a higher " + billWord() + ".";
    }
    if (topPick) topPick.innerHTML = "";
    if (resultsGrid) resultsGrid.innerHTML = "";
    return;
  }

  if (emptyState) emptyState.classList.add("hidden");

  // Section header + featured top pick
  if (topPick) {
    topPick.innerHTML = "";
    const header = document.createElement("div");
    header.className = "mw-section-header";
    header.innerHTML = `
      <span class="mw-section-label">Best next card to add</span>
      <span class="mw-section-sub">Ranked by extra savings on top of your wallet</span>
    `;
    topPick.appendChild(header);
    const slot = document.createElement("div");
    topPick.appendChild(slot);
    renderNextCardFeatured(ranked[0], slot);
  }
  // Ranked list (skip the featured one)
  if (resultsGrid) {
    resultsGrid.innerHTML = "";
    const rest = ranked.slice(1, 21);
    rest.forEach((result, idx) => {
      renderNextCardItem(result, resultsGrid, idx + 2);
    });
  }
}

function buildNextCardReason(result) {
  const bits = [];
  if (result.newVenues > 0) {
    bits.push(`<strong>${result.newVenues}</strong> new ${venueNoun(result.newVenues)} covered`);
  }
  if (result.boostedVenues > 0) {
    bits.push(`<strong>${result.boostedVenues}</strong> existing ${venueNoun(result.boostedVenues)} boosted`);
  }
  if (!bits.length) bits.push(`covers ${result.coveredVenues} ${venueNoun(result.coveredVenues)} in scope`);
  return bits.join(" · ");
}

function renderNextCardFeatured(result, container) {
  if (!container) return;
  const cardKey = buildCardKey(result.bank, result.card);
  const score = Math.round(Number(result.score) || 0);
  const tier = fitTier(result.score);
  const scorePct = Math.max(0, Math.min(100, Number(result.score) || 0));
  const sc = scoreColor(scorePct);
  const showEligibility = isEligibilityContextActive();
  const eligStatus = result.requirementStatus;
  const reason = buildNextCardReason(result);
  const yearly = result.yearlyDelta;
  const topVenues = result.topVenueWins.slice(0, 3).map((v) => escapeHtml(v.restaurant)).join(", ");

  container.innerHTML = `
    <article class="card-item card-item--featured nc-featured" data-key="${escapeAttr(cardKey)}" tabindex="0" role="button" aria-label="Open details for ${escapeAttr(result.card)} from ${escapeAttr(result.bank)}">
      <div class="card-row card-row--clickable">
        <div class="card-rank rank-1">1</div>
        ${renderBankLogo(result.bank, "card-logo-box")}
        <div class="card-info">
          <div class="card-badges">
            <span class="badge-top-pick">BEST NEXT CARD</span>
            ${showEligibility ? renderEligibilityBadge(eligStatus) : ""}
          </div>
          <div class="card-name">${escapeHtml(result.card)}</div>
          <div class="card-bank">${escapeHtml(result.bank)}</div>
        </div>
        <div class="score-box">
          <div class="score-num" style="color:${sc}">${score}</div>
          <div class="score-label">${escapeHtml(tier)}</div>
          <div class="score-bar">
            <div class="score-bar-fill" style="width:${scorePct}%;background:${sc}"></div>
          </div>
        </div>
      </div>
      <!-- Hero saving driven by the toolbar saving-window toggle so the
           number matches the user's chosen unit (no more both /outing AND
           /year shown side-by-side). The counts (295 new, 41 boosted)
           live in the meta line ONLY — used to be repeated above in a
           "reason" string, removed now to avoid the triple-redundancy. -->
      <div class="nc-meta nc-meta--featured">
        <div class="nc-meta-hero">+${formatCurrencyShort(result.avgDeltaPerOuting * savingWindowMultiplier())}<span class="nc-meta-unit"> ${savingWindowSuffix()}</span></div>
        <div class="nc-meta-line">
          <span>Adds <strong>${result.newVenues}</strong> ${venueNoun(result.newVenues)}</span>
          ${result.boostedVenues > 0 ? `<span class="nc-meta-sep">·</span><span>Boosts <strong>${result.boostedVenues}</strong> existing</span>` : ""}
        </div>
      </div>
      ${topVenues ? `<div class="nc-feat-topvenues"><span class="nc-feat-topvenues-l">Best wins:</span> ${topVenues}</div>` : ""}
    </article>
  `;
  bindCardOpenInteractions(container.querySelector("article"), cardKey);
}

function renderNextCardItem(result, container, rank) {
  const cardKey = buildCardKey(result.bank, result.card);
  const score = Number(result.score).toFixed(1);
  const scorePct = Math.max(0, Math.min(100, Number(result.score) || 0));
  const sc = scoreColor(scorePct);
  const showEligibility = isEligibilityContextActive();
  const eligStatus = result.requirementStatus;
  const reason = buildNextCardReason(result);

  const article = document.createElement("article");
  article.className = "card-item nc-item";
  article.style.animationDelay = `${Math.min(rank, 12) * 0.04}s`;
  article.dataset.key = cardKey;
  article.innerHTML = `
    <div class="card-row card-row--clickable">
      <div class="card-rank">${rank}</div>
      ${renderBankLogo(result.bank, "card-logo-box")}
      <div class="card-info">
        <div class="card-badges">
          ${showEligibility ? renderEligibilityBadge(eligStatus) : ""}
        </div>
        <div class="card-name">${escapeHtml(result.card)}</div>
        <div class="card-bank">${escapeHtml(result.bank)}</div>
      </div>
      <div class="score-box">
        <div class="score-num" style="color:${sc}">${score}</div>
        <div class="score-label">Fit</div>
        <div class="score-bar">
          <div class="score-bar-fill" style="width:${scorePct}%;background:${sc}"></div>
        </div>
      </div>
    </div>
    <div class="nc-meta">
      <span class="nc-meta-pri">+${formatCurrencyShort(result.avgDeltaPerOuting * savingWindowMultiplier())}<span class="nc-meta-unit"> ${savingWindowSuffix()}</span></span>
      <span class="nc-meta-sep">·</span>
      <span>Adds <strong>${result.newVenues}</strong></span>
      ${result.boostedVenues > 0 ? `<span class="nc-meta-sep">·</span><span>Boosts <strong>${result.boostedVenues}</strong></span>` : ""}
    </div>
  `;
  container.appendChild(article);
  bindCardOpenInteractions(article, cardKey);
}

/* ── BUILD WALLET: VIEW + SETUP PANEL ── */
function renderWalletSetupPanel(container, stats) {
  if (!container) return;
  const K = state.walletSize;
  const ownedCount = state.ownedCards.size;
  const canBuildOnOwned = ownedCount > 0;
  const buildOnOwned = state.walletBuildOnOwned && canBuildOnOwned;
  const sizeOptions = [2, 3, 4];
  const objectiveOptions = [
    { v: "savings",  label: "Savings",  hint: `Maximum gross saving per ${visitWord()}` },
    { v: "coverage", label: "Coverage", hint: `Most ${venueNoun()} helped` },
    { v: "roi",      label: "ROI",      hint: "Best net of annual fees" },
  ];
  const obj = state.walletObjective || "savings";
  const noSameBank = !!state.walletNoSameBank;
  const mixedTypes = !!state.walletMixedTypes;
  const maxFeeRaw = state.walletMaxFee;
  const mustCount = state.walletMustInclude.size;

  // Compact summary the user sees on mobile when the config is collapsed.
  // Gives them a one-line read of the current build settings without
  // forcing them to scroll past ~600px of pills + toggles to reach the
  // actual wallet results.
  const objLabel = (objectiveOptions.find((o) => o.v === obj) || { label: "Max savings" }).label;
  const startLabel = buildOnOwned ? `On top of ${stats?.anchorCount || ownedCount}` : "From scratch";
  const feeLabel = maxFeeRaw !== null ? `Max fee ${formatCurrencyShort(maxFeeRaw)}/yr` : "No fee cap";
  const summaryBits = `${K} cards · ${startLabel} · ${objLabel} · ${feeLabel}`;

  // First-load defaults: open on desktop (so the user sees the controls
  // they're used to), closed on phones (so the wallet list rises to the
  // top). After the user toggles, that choice sticks on either platform.
  const isPhone = typeof window !== "undefined"
    && window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
  const configOpen = state.walletConfigOpen === undefined
    ? !isPhone
    : state.walletConfigOpen;

  container.innerHTML = `
    <details class="wo-setup wo-setup--collapsible"${configOpen ? " open" : ""}>
      <summary class="wo-setup-head">
        <div class="wo-setup-title-wrap">
          <div class="wo-setup-kicker">Wallet Builder</div>
          <div class="wo-setup-title">Best ${K}-card wallet for you</div>
          <div class="wo-setup-sub">We pick a combination of cards that <strong>together</strong> covers the most ${venueNoun()} and saves the most.</div>
          <div class="wo-setup-summary-line">${escapeHtml(summaryBits)}</div>
        </div>
        <span class="wo-setup-chevron" aria-hidden="true">
          <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 4.5 L6 8.5 L10 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
      </summary>

      <div class="wo-controls-grid">
        <div class="wo-control">
          <div class="wo-control-label">Wallet size</div>
          <div class="wo-pill-group" id="wo-k-pills">
            ${sizeOptions.map((n) => `<button class="wo-pill${n === K ? " active" : ""}" type="button" data-k="${n}">${n} cards</button>`).join("")}
          </div>
        </div>

        <div class="wo-control">
          <div class="wo-control-label">Start from</div>
          <div class="wo-mode-toggle">
            <button class="wo-mode-btn${!buildOnOwned ? " active" : ""}" type="button" data-mode="scratch">Scratch</button>
            <button class="wo-mode-btn${buildOnOwned ? " active" : ""}${canBuildOnOwned ? "" : " disabled"}" type="button" data-mode="owned" ${canBuildOnOwned ? "" : "disabled"}>
              My ${ownedCount} card${ownedCount === 1 ? "" : "s"}
            </button>
          </div>
        </div>

        <div class="wo-control">
          <div class="wo-control-label">Optimize for</div>
          <div class="wo-pill-group" id="wo-obj-pills">
            ${objectiveOptions.map((o) => `<button class="wo-pill${o.v === obj ? " active" : ""}" type="button" data-obj="${o.v}" title="${escapeAttr(o.hint)}">${o.label}</button>`).join("")}
          </div>
        </div>

        <div class="wo-control">
          <div class="wo-control-label">Max annual fee</div>
          <div class="wo-fee-wrap">
            <span class="wo-fee-prefix">PKR</span>
            <input id="wo-max-fee" type="number" inputmode="numeric" min="0" step="1000" placeholder="No cap" value="${maxFeeRaw !== null ? String(maxFeeRaw) : ""}" />
            <span class="wo-fee-suffix">/ year</span>
            ${maxFeeRaw !== null ? `<button class="wo-fee-clear" id="wo-max-fee-clear" type="button" aria-label="Clear cap">×</button>` : ""}
          </div>
        </div>
      </div>

      <details class="wo-advanced" ${state.walletAdvancedOpen || noSameBank || mixedTypes || mustCount > 0 ? "open" : ""}>
        <summary class="wo-advanced-summary">
          <span class="wo-advanced-summary-text">
            Advanced options
          </span>
          ${(() => {
            const activeBits = [];
            if (noSameBank) activeBits.push("No same bank");
            if (mixedTypes) activeBits.push("Mixed types");
            if (mustCount > 0) activeBits.push(`${mustCount} pinned`);
            return activeBits.length > 0
              ? `<span class="wo-advanced-badges">${activeBits.map((b) => `<span class="wo-advanced-badge">${escapeHtml(b)}</span>`).join("")}</span>`
              : `<span class="wo-advanced-hint">Diversity, mixed types, pin specific cards</span>`;
          })()}
          <span class="wo-advanced-chevron" aria-hidden="true"></span>
        </summary>

        <div class="wo-advanced-body">
          <div class="wo-toggles">
            <label class="wo-toggle">
              <input type="checkbox" id="wo-nobank" ${noSameBank ? "checked" : ""} />
              <span class="wo-toggle-slider"></span>
              <span class="wo-toggle-label">No two cards from the same bank</span>
            </label>
            <label class="wo-toggle">
              <input type="checkbox" id="wo-mix" ${mixedTypes ? "checked" : ""} />
              <span class="wo-toggle-slider"></span>
              <span class="wo-toggle-label">Mix card types (≥1 debit + ≥1 credit)</span>
            </label>
          </div>

          <div class="wo-must-section">
            <div class="wo-must-head">
              <div class="wo-control-label">Must include cards <span class="wo-must-count">${mustCount > 0 ? `(${mustCount})` : ""}</span></div>
              ${mustCount > 0 ? `<button class="wo-must-clear" id="wo-must-clear" type="button">Clear</button>` : ""}
            </div>
            <input id="wo-must-search" class="s-search wo-must-search" type="search" placeholder="Pin specific cards to the wallet…" autocomplete="off" value="${escapeAttr(state.walletMustIncludeSearchTerm)}" />
            <div id="wo-must-results" class="s-search-results wo-must-results"></div>
            <div id="wo-must-chips" class="s-chips wo-must-chips"></div>
          </div>
        </div>
      </details>

      ${buildOnOwned ? `
        <div class="wo-anchor-note">
          Building on top of: ${Array.from(state.ownedCards).slice(0, 3).map((ck) => {
            const [bank, card] = ck.split(" || ");
            return `<span class="wo-anchor-pill">${escapeHtml(card)}</span>`;
          }).join(" ")}${state.ownedCards.size > 3 ? ` <span class="wo-anchor-pill wo-anchor-pill--more">+${state.ownedCards.size - 3} more</span>` : ""}
        </div>
      ` : `
        <div class="wo-crosslink">
          ${canBuildOnOwned
            ? `Already added your cards in <a class="wo-link" id="wo-go-next-card">My Wallet</a>? <a class="wo-link" id="wo-switch-build-owned">Build on top of them →</a>`
            : `Have cards already? <a class="wo-link" id="wo-go-next-card">Open My Wallet →</a> to add them and see the best next card.`
          }
        </div>
      `}
    </details>
  `;

  // Persist the open/closed state so the user's choice survives re-renders.
  container.querySelector(".wo-setup--collapsible")?.addEventListener("toggle", (e) => {
    state.walletConfigOpen = e.target.open;
  });

  // Event wiring
  container.querySelectorAll("#wo-k-pills .wo-pill").forEach((btn) => {
    btn.addEventListener("click", () => {
      const k = Number(btn.dataset.k);
      if (k >= 2 && k <= 4 && k !== state.walletSize) {
        state.walletSize = k;
        trackEvent("wallet_k_change", { k });
        render();
      }
    });
  });
  container.querySelectorAll(".wo-mode-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.classList.contains("disabled")) return;
      const mode = btn.dataset.mode;
      const next = mode === "owned";
      if (next !== state.walletBuildOnOwned) {
        state.walletBuildOnOwned = next;
        trackEvent("wallet_mode_change", { mode: next ? "owned" : "scratch" });
        render();
      }
    });
  });
  container.querySelectorAll("#wo-obj-pills .wo-pill").forEach((btn) => {
    btn.addEventListener("click", () => {
      const o = btn.dataset.obj;
      if (o && o !== state.walletObjective) {
        state.walletObjective = o;
        trackEvent("wallet_objective_change", { objective: o });
        render();
      }
    });
  });
  const maxFeeInput = container.querySelector("#wo-max-fee");
  if (maxFeeInput) {
    maxFeeInput.addEventListener("input", debounce((e) => {
      const v = parseOptionalNumber(e.target.value);
      state.walletMaxFee = (v !== null && v >= 0) ? v : null;
      trackEvent("wallet_max_fee_set", { cap: state.walletMaxFee });
      render();
    }, 220));
  }
  container.querySelector("#wo-max-fee-clear")?.addEventListener("click", () => {
    state.walletMaxFee = null;
    render();
  });
  container.querySelector("#wo-nobank")?.addEventListener("change", (e) => {
    state.walletNoSameBank = !!e.target.checked;
    trackEvent("wallet_diversity_toggle", { rule: "no_same_bank", on: state.walletNoSameBank });
    render();
  });
  container.querySelector("#wo-mix")?.addEventListener("change", (e) => {
    state.walletMixedTypes = !!e.target.checked;
    trackEvent("wallet_diversity_toggle", { rule: "mixed_types", on: state.walletMixedTypes });
    render();
  });
  container.querySelector("#wo-must-clear")?.addEventListener("click", () => {
    state.walletMustInclude = new Set();
    state.walletMustIncludeSearchTerm = "";
    render();
  });
  const mustSearch = container.querySelector("#wo-must-search");
  if (mustSearch) {
    mustSearch.addEventListener("input", (e) => {
      state.walletMustIncludeSearchTerm = e.target.value.trim();
      renderWalletMustResults();
    });
  }
  container.querySelector(".wo-advanced")?.addEventListener("toggle", (e) => {
    state.walletAdvancedOpen = !!e.target.open;
  });
  container.querySelector("#wo-switch-build-owned")?.addEventListener("click", () => {
    state.walletBuildOnOwned = true;
    render();
  });
  container.querySelector("#wo-go-next-card")?.addEventListener("click", () => {
    state.viewMode = "my-wallet";
    render();
  });

  renderWalletMustResults();
  renderWalletMustChips();
}

// Tiny debounce helper for the fee input
function debounce(fn, delay) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

function renderWalletMustResults() {
  const container = document.getElementById("wo-must-results");
  if (!container) return;
  const term = (state.walletMustIncludeSearchTerm || "").toLowerCase();
  if (!term) { container.innerHTML = ""; return; }
  const catalog = getAllCardsCatalog();
  const results = catalog
    .filter((c) => !state.walletMustInclude.has(c.cardKey))
    .filter((c) => `${c.bank} ${c.card}`.toLowerCase().includes(term))
    .slice(0, 12);
  container.innerHTML = "";
  if (results.length === 0) {
    container.innerHTML = `<div class="nc-search-empty">No matching cards.</div>`;
    return;
  }
  results.forEach((entry) => {
    const item = document.createElement("button");
    item.type = "button";
    item.className = "s-search-item nc-search-item";
    item.innerHTML = `
      <span class="nc-search-item-text">
        <span class="nc-search-item-card">${escapeHtml(entry.card)}</span>
        <span class="nc-search-item-bank">${escapeHtml(entry.bank)}</span>
      </span>
      <span class="s-search-item-add">+ Pin</span>
    `;
    item.addEventListener("click", () => {
      state.walletMustInclude.add(entry.cardKey);
      state.walletMustIncludeSearchTerm = "";
      const input = document.getElementById("wo-must-search");
      if (input) input.value = "";
      trackEvent("wallet_pin_card", { bank: entry.bank, pinned: state.walletMustInclude.size });
      render();
    });
    container.appendChild(item);
  });
}

function renderWalletMustChips() {
  const container = document.getElementById("wo-must-chips");
  if (!container) return;
  container.innerHTML = "";
  Array.from(state.walletMustInclude).sort().forEach((cardKey) => {
    const [bank, card] = cardKey.split(" || ");
    const chip = document.createElement("div");
    chip.className = "s-chip nc-chip wo-must-chip";
    chip.innerHTML = `
      <span class="wo-must-pin-icon">${phIcon("push-pin")}</span>
      <span class="nc-chip-text">
        <span class="nc-chip-card">${escapeHtml(card || "")}</span>
        <span class="nc-chip-bank">${escapeHtml(bank || "")}</span>
      </span>
    `;
    const rm = document.createElement("button");
    rm.className = "s-chip-remove";
    rm.type = "button";
    rm.textContent = "×";
    rm.setAttribute("aria-label", `Unpin ${card}`);
    rm.addEventListener("click", () => { state.walletMustInclude.delete(cardKey); render(); });
    chip.appendChild(rm);
    container.appendChild(chip);
  });
}

function renderWalletView(resultsGrid) {
  const setupContainer = document.getElementById("wallet-setup");
  const topPick = document.getElementById("top-pick");
  const emptyState = document.getElementById("empty-state");
  const countEl = document.getElementById("result-count");
  const countLabel = document.getElementById("result-count-label");
  const rhSub = document.getElementById("rh-sub");
  const summaryBest = document.getElementById("summary-best");

  const { ranked, stats } = computeWalletRecommendations();
  renderWalletSetupPanel(setupContainer, stats);
  const K = stats.K;

  if (countEl) countEl.textContent = ranked.length ? String(K) : "0";
  if (countLabel) countLabel.textContent = ranked.length ? `card wallet recommended` : "wallets possible";
  if (rhSub) {
    const ctx = stats.buildOnOwned
      ? `Best ${K} cards to add on top of your ${stats.anchorCount} · ${formatCurrency(state.orderValue)} ${billWord()}`
      : `Best ${K}-card combination from scratch · ${formatCurrency(state.orderValue)} ${billWord()}`;
    rhSub.textContent = ctx;
  }
  if (summaryBest) {
    summaryBest.textContent = ranked.length > 0
      ? `${formatCurrency(ranked[0].perOutingTotal)} / ${visitWord()}`
      : "—";
  }

  if (ranked.length === 0) {
    if (emptyState) {
      emptyState.classList.remove("hidden");
      const title = document.getElementById("empty-state-title");
      const msg = document.getElementById("empty-state-message");
      if (title) title.textContent = "Couldn't build a wallet with current filters";
      const baseMsg = "Try a broader scope: clear bank/card-type filters, pick a city with more coverage, or turn off eligibility mode.";
      if (msg) msg.textContent = (stats.warnings || []).length > 0 ? stats.warnings.join(" ") : baseMsg;
    }
    if (topPick) topPick.innerHTML = "";
    if (resultsGrid) resultsGrid.innerHTML = "";
    return;
  }
  if (emptyState) emptyState.classList.add("hidden");

  if (topPick) {
    topPick.innerHTML = "";
    // Surface warnings (over budget, missing types, etc.)
    if ((stats.warnings || []).length > 0) {
      const banner = document.createElement("div");
      banner.className = "wo-warning-banner";
      banner.innerHTML = stats.warnings.map((w) => `<div class="wo-warning-line"><span class="wo-warning-icon">${phIcon("warning")}</span><span>${escapeHtml(w)}</span></div>`).join("");
      topPick.appendChild(banner);
    }
    const slot = document.createElement("div");
    topPick.appendChild(slot);
    renderWalletFeatured(ranked[0], slot, stats);
  }
  if (resultsGrid) {
    resultsGrid.innerHTML = "";
    const alternates = ranked.slice(1);
    if (alternates.length > 0) {
      const header = document.createElement("div");
      header.className = "wo-alt-header";
      header.innerHTML = `<span class="wo-alt-header-label">Alternative wallets</span> <span class="wo-alt-header-sub">other strong ${K}-card combinations</span>`;
      resultsGrid.appendChild(header);
      alternates.forEach((wallet, idx) => renderWalletAlternative(wallet, resultsGrid, idx + 2, stats));
    }
  }
}

function renderWalletFeatured(wallet, container, stats) {
  if (!container) return;
  const K = wallet.picks.length;
  const yearly = wallet.perOutingTotal * (state.outingsPerWeek || 1) * 52 * wallet.coverage;
  const totalFee = wallet.totalAnnualFee || 0;
  const feeNote = wallet.feeUnknown ? " <span class='wo-fee-note'>(some unlisted)</span>" : "";
  const showEligibility = isEligibilityContextActive();

  const picksHtml = wallet.picks.map((p, idx) => {
    const eligBadge = showEligibility ? renderEligibilityBadge(p.requirementStatus) : "";
    const role = p.pinned
      ? "Pinned by you"
      : (idx === 0
          ? "Anchor"
          : `Adds${p.newVenues > 0 ? ` ${p.newVenues} new` : ""}${p.newVenues > 0 && p.boostedVenues > 0 ? "," : ""}${p.boostedVenues > 0 ? ` boosts ${p.boostedVenues}` : ""}`);
    return `
      <div class="wo-stack-card${p.pinned ? " wo-stack-card--pinned" : ""}" data-key="${escapeAttr(p.cardKey)}" tabindex="0" role="button">
        <div class="wo-stack-card-rank">${p.pinned ? phIcon("push-pin") : idx + 1}</div>
        ${renderBankLogo(p.bank, "wo-stack-logo")}
        <div class="wo-stack-card-info">
          <div class="wo-stack-card-name">${escapeHtml(p.card)}</div>
          <div class="wo-stack-card-bank">${escapeHtml(p.bank)}</div>
          <div class="wo-stack-card-meta">
            <span class="wo-stack-card-role">${escapeHtml(role)}</span>
            ${!p.pinned && p.newVenues > 0 ? `<span class="wo-stack-card-tag wo-stack-card-tag--new">+${p.newVenues} new</span>` : ""}
            ${!p.pinned && p.boostedVenues > 0 ? `<span class="wo-stack-card-tag">${p.boostedVenues} boosts</span>` : ""}
          </div>
          ${eligBadge ? `<div class="wo-stack-card-elig">${eligBadge}</div>` : ""}
        </div>
      </div>
    `;
  }).join("");

  container.innerHTML = `
    <article class="card-item card-item--featured wo-featured">
      <div class="wo-featured-head">
        <div class="wo-featured-head-left">
          <div class="card-badges">
            <span class="badge-top-pick">BEST WALLET</span>
          </div>
          <div class="wo-featured-title">${K}-card wallet · ${stats.buildOnOwned ? `on top of your ${stats.anchorCount}` : "from scratch"}</div>
          <div class="wo-featured-sub">Covers <strong>${wallet.coveredVenues} of ${wallet.venueCount}</strong> ${venueNoun(wallet.venueCount)} in your scope</div>
        </div>
      </div>

      <div class="wo-stack">
        ${picksHtml}
      </div>

      <!-- Hero combined-saving driven by the saving-window toggle. The
           coverage info already lives in .wo-featured-sub at the top of
           the card (498 of 860 restaurants), so we don't repeat it here.
           Fee surfaces alongside since it's the one stat the user cares
           about that isn't in the header. -->
      <div class="wo-combined-stats">
        <div class="wo-combined-stat wo-combined-stat--hero">
          <div class="wo-combined-l">Combined saving</div>
          <div class="wo-combined-v green">${formatCurrencyShort(wallet.perOutingTotal * savingWindowMultiplier())} ${savingWindowSuffix()}</div>
        </div>
        <div class="wo-combined-stat">
          <div class="wo-combined-l">Annual fees</div>
          <div class="wo-combined-v">${totalFee === 0 ? "Free / not listed" : formatCurrencyShort(totalFee) + " / yr"}${feeNote}</div>
        </div>
      </div>
    </article>
  `;

  container.querySelectorAll(".wo-stack-card").forEach((node) => {
    const key = node.dataset.key;
    if (!key) return;
    node.addEventListener("click", () => openCardDetail(key));
    node.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openCardDetail(key); }
    });
  });
}

function renderWalletAlternative(wallet, container, rank, stats) {
  const article = document.createElement("article");
  article.className = "card-item wo-alt-item";
  article.style.animationDelay = `${Math.min(rank, 12) * 0.05}s`;
  const picksLine = wallet.picks.map((p) =>
    `<span class="wo-alt-card-chip" data-key="${escapeAttr(p.cardKey)}">${escapeHtml(p.card)} <span class="wo-alt-card-chip-bank">· ${escapeHtml(p.bank)}</span></span>`
  ).join("");
  const totalFee = wallet.totalAnnualFee || 0;
  article.innerHTML = `
    <div class="card-row wo-alt-row">
      <div class="card-rank">${rank}</div>
      <div class="wo-alt-body">
        <div class="wo-alt-cards">${picksLine}</div>
        <div class="wo-alt-stats">
          <span class="wo-alt-stat"><span class="green">${formatCurrencyShort(wallet.perOutingTotal * savingWindowMultiplier())} ${savingWindowSuffix()}</span></span>
          <span class="wo-alt-stat-sep">·</span>
          <span class="wo-alt-stat">${wallet.coveredVenues} of ${wallet.venueCount}</span>
          <span class="wo-alt-stat-sep">·</span>
          <span class="wo-alt-stat">${totalFee === 0 ? "Free" : "Fee " + formatCurrencyShort(totalFee)}</span>
        </div>
      </div>
    </div>
  `;
  container.appendChild(article);
  article.querySelectorAll(".wo-alt-card-chip").forEach((chip) => {
    const key = chip.dataset.key;
    if (!key) return;
    chip.addEventListener("click", (e) => { e.stopPropagation(); openCardDetail(key); });
  });
}

/* ── NEXT CARD RECOMMENDATIONS ── moved to assets/algorithms.js */


/* ── BUILD WALLET RECOMMENDATIONS ──
   Moved to assets/algorithms.js — loaded before this file via <script> in
   index.html. The functions (precomputeCardSavingsByVenueDay, marginalForCard,
   applyCardToCurrentBest, summarizeWallet, annualFeeForCard,
   computeWalletRecommendations) are accessible globally by name. */


/* ── ELIGIBILITY MATH ── moved to assets/algorithms.js (renderEligibilityBadge/Meta remain below) */



function renderEligibilityBadge(status) {
  return `<span class="status-badge status-badge--${escapeHtml(status.tone)}">${escapeHtml(status.label)}</span>`;
}

function renderEligibilityMeta(status) {
  const bits = [];
  if (status.annualFeeWaiverRule) bits.push(status.annualFeeWaiverRule);
  return bits;
}

function setEmptyStateCopy() {
  const title = document.getElementById("empty-state-title");
  const msg = document.getElementById("empty-state-message");
  if (state.useEligibility) {
    if (title) title.textContent = "No cards passed the current eligibility filter";
    if (msg) msg.textContent = "Try turning off eligibility mode, increasing the salary or balance values, or broadening the filters.";
  } else {
    if (title) title.textContent = "No cards match your filters";
    if (msg) msg.textContent = "Try a different city or reset the filters.";
  }
}


function renderRequirementSummary(status, options = {}) {
  const { showStatus = false } = options;
  const fields = [
    { label: "Salary", field: "salaryReq" },
    { label: "Min balance", field: "balanceReq" },
    { label: "Annual fee", field: "annualFeePkr" },
  ];
  if (status?.benefitSummary) {
    fields.push({ label: "Benefits", field: "benefitSummary" });
  }
  const estNote = status.isEstimated && status.estimationNote
    ? `<div class="requirement-est-note">~ values are estimates: ${escapeHtml(status.estimationNote)}</div>`
    : "";
  return `
    <div class="requirement-panel">
      <div class="requirement-panel-head">
        <div class="requirement-panel-title">Requirements & fees</div>
        ${showStatus ? renderEligibilityBadge(status) : ""}
      </div>
      <div class="requirement-grid">
        ${fields.map(({ label, field }) => `
          <div class="requirement-item">
            <div class="requirement-label">${escapeHtml(label)}</div>
            <div class="requirement-value">${escapeHtml(formatRequirementFieldValue(status, field))}</div>
          </div>
        `).join("")}
      </div>
      ${estNote}
      ${renderRequirementNotes(status)}
    </div>
  `;
}

function renderSourcesSection(sourceIds) {
  if (!state.requirements?.sourcesById || !sourceIds?.length) return "";
  const sources = sourceIds
    .map((id) => state.requirements.sourcesById.get(id))
    .filter((s) => s && s.source_type !== "spreadsheet");
  if (!sources.length) return "";
  return `
    <div class="cd-section cd-sources-section">
      <div class="cd-section-title">Data sources</div>
      <div class="cd-sources-list">
        ${sources.map((s) => {
          const isPdf = s.source_type === "pdf";
          return `<a class="cd-source-link" href="${escapeAttr(s.url)}" target="_blank" rel="noopener noreferrer">
            <span class="cd-source-label">${escapeHtml(isPdf ? "Official document" : "Official bank page")}</span>
            <span class="cd-source-badge cd-source-badge--${isPdf ? "pdf" : "web"}">${isPdf ? "PDF" : "Web"}</span>
          </a>`;
        }).join("")}
      </div>
      <div class="cd-sources-note">Requirements and fees are sourced from publicly available bank documents. Values may change, so verify with your bank before applying.</div>
    </div>
  `;
}

function getCompareRowWinner(row) {
  if (!row || row.compare === "none") return -1;
  const [left, right] = row.vals;
  if (!Number.isFinite(left) || !Number.isFinite(right) || left === right) return -1;
  if (row.compare === "low") return left < right ? 0 : 1;
  return left > right ? 0 : 1;
}

function bindCardOpenInteractions(article, cardKey) {
  if (!article) return;
  const openDetail = () => openCardDetail(cardKey);
  article.querySelectorAll(".card-row--clickable, .card-stats-row--clickable").forEach((node) => {
    node.addEventListener("click", (e) => {
      if (e.target.closest("button, a, input, select, textarea, label")) return;
      openDetail();
    });
  });
  article.addEventListener("keydown", (e) => {
    if (e.target !== article) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    openDetail();
  });
}


/* ── TONIGHT BUTTON ── */
function updateTonightButton() {
  const btn = document.getElementById("btn-tonight");
  if (!btn) return;
  const todayIdx = (new Date().getDay() + 6) % 7;
  const isActive = state.selectedDays.size === 1 && state.selectedDays.has(todayIdx);
  btn.innerHTML = `${phIcon("moon")} ${DAY_SHORT[todayIdx]} Tonight`;
  btn.classList.toggle("active", isActive);
}

/* ── VIEW TOGGLE ── */
function updateViewToggle() {
  document.getElementById("btn-view-cards")?.classList.toggle("active", state.viewMode === "cards");
  document.getElementById("btn-view-restaurants")?.classList.toggle("active", state.viewMode === "restaurants");
  document.getElementById("btn-view-next-card")?.classList.toggle("active", state.viewMode === "my-wallet");
  document.getElementById("btn-view-wallet")?.classList.toggle("active", state.viewMode === "wallet");
  const ncSetup = document.getElementById("next-card-setup");
  if (ncSetup) ncSetup.style.display = state.viewMode === "my-wallet" ? "" : "none";
  const woSetup = document.getElementById("wallet-setup");
  if (woSetup) woSetup.style.display = state.viewMode === "wallet" ? "" : "none";
}

function setupMobileNavMenu() {
  const nav = document.querySelector(".nav");
  const toggle = document.getElementById("nav-toggle");
  if (!nav || !toggle) return;

  let utilityNav = nav.querySelector(".utility-nav");
  if (!utilityNav) {
    utilityNav = document.createElement("div");
    utilityNav.className = "utility-nav";
    nav.appendChild(utilityNav);
  }
  // Always rebuild contents so home and sub-pages render an identical mobile
  // dropdown. Mirrors the same JS in assets/content-pages.js.
  const deskLinks = Array.from(nav.querySelectorAll(".nav-links-desk a"))
    .map((link) => {
      const href = link.getAttribute("href") || "#";
      const text = link.textContent || "";
      const cur = link.getAttribute("aria-current") === "page" ? ' aria-current="page"' : "";
      return `<a class="nav-link utility-link" href="${escapeAttr(href)}"${cur}>${escapeHtml(text)}</a>`;
    })
    .join("");
  utilityNav.innerHTML = deskLinks;
  utilityNav.querySelector("#nav-mobile-quiz")?.addEventListener("click", () => {
    closeMobileNavMenu();
    openQuiz();
  });

  const toggleMenu = (forceOpen) => {
    const willOpen = typeof forceOpen === "boolean" ? forceOpen : !utilityNav.classList.contains("nav-open");
    utilityNav.classList.toggle("nav-open", willOpen);
    toggle.setAttribute("aria-expanded", willOpen ? "true" : "false");
    toggle.setAttribute("aria-label", willOpen ? "Close menu" : "Open menu");
  };

  toggle.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleMenu();
  });

  utilityNav.addEventListener("click", (e) => {
    if (e.target.closest("a")) toggleMenu(false);
  });

  document.addEventListener("click", (e) => {
    if (!utilityNav.classList.contains("nav-open")) return;
    if (nav.contains(e.target)) return;
    toggleMenu(false);
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth > 768) toggleMenu(false);
  });
}

function closeMobileNavMenu() {
  const nav = document.querySelector(".nav");
  const toggle = document.getElementById("nav-toggle");
  const utilityNav = nav?.querySelector(".utility-nav");
  if (!toggle || !utilityNav) return;
  utilityNav.classList.remove("nav-open");
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-label", "Open menu");
}

/* ── BANK APPLY LINK ── */
function getBankApplyUrl(bank) {
  return BANK_APPLY_URLS[normalizeBankKey(bank)] || null;
}

/* ── DEAL DENSITY ── */
function getDealDensityByDay(bank, card) {
  const dayRests = Array.from({ length: 7 }, () => new Set());
  if (!state.data?.offers) return dayRests.map(() => 0);
  state.data.offers.forEach((offer) => {
    if (offer.bank !== bank || offer.card !== card) return;
    if (!cityMatches(offer.city)) return;
    offer.days.forEach((d) => dayRests[d].add(offer.restaurant));
  });
  return dayRests.map((s) => s.size);
}


/* ── CARD DETAIL MODAL ── */
function openCardDetail(cardKey) {
  // Card detail reads per-offer data (state.data.offers) → ensure raw offers
  // are loaded before rendering the modal body.
  if (!state.data) {
    withRawOffers(() => openCardDetail(cardKey));
    return;
  }
  state.detailCardKey = cardKey;
  state.pagination.cardDetailRestaurants = 1;
  const modal = document.getElementById("card-detail-modal");
  const inner = document.getElementById("card-detail-inner");
  if (!modal || !inner) return;
  const [bank, card] = String(cardKey).split(" || ");
  trackEvent("card_open", { bank, card });
  renderCardDetailModal(inner);
  modal.style.display = "flex";
}

function closeCardDetail() {
  state.detailCardKey = null;
  const modal = document.getElementById("card-detail-modal");
  if (modal) modal.style.display = "none";
}

function getRequirementNotes(status) {
  const items = [];
  if (status.annualFeeWaiverRule) items.push(status.annualFeeWaiverRule);
  (status.cardNotes || []).forEach((n) => items.push(n));
  (status.bankGaps || []).forEach((n) => items.push(n));

  return [...new Set(
    items
      .filter((n) => typeof n === "string")
      .map((n) => n.trim())
      .filter(Boolean)
  )];
}

function renderRequirementNotes(status) {
  const items = getRequirementNotes(status);
  if (!items.length) return "";

  return `
    <details class="cd-notes-details">
      <summary class="cd-notes-summary">
        <span class="cd-notes-summary-left">
          <span class="cd-notes-heading">Notes</span>
          <span class="cd-notes-count">${items.length}</span>
        </span>
        <span class="cd-notes-caret" aria-hidden="true"></span>
      </summary>
      <ul class="cd-notes-list">
        ${items.map((text) => `<li class="cd-note-item">${escapeHtml(text)}</li>`).join("")}
      </ul>
    </details>
  `;
}

function renderCardDetailModal(inner) {
  const key = state.detailCardKey;
  if (!key || !state.data) return;
  const [bank, card] = key.split(" || ");

  const results = computeRecommendations();
  const result  = results.find((r) => r.bank === bank && r.card === card);

  const allOffers = state.data.offers.filter((o) =>
    o.bank === bank && o.card === card && cityMatches(o.city),
  );

  const byRest = new Map();
  allOffers.forEach((offer) => {
    const rk     = `${offer.city}|||${offer.restaurant}`;
    const saving = getOfferSavingValue(offer, state.orderValue) || 0;
    const cur    = byRest.get(rk);
    if (!cur || saving > cur.saving) {
      byRest.set(rk, {
        restaurant:   offer.restaurant,
        city:         offer.city,
        saving,
        discountLabel: offer.discountLabel,
        offerTitle:   offer.offerTitle,
        offerDescription: offer.offerDescription,
        orderTypes:   offer.orderTypes || [],
        daysLabel:    offer.daysLabel,
        capPkr:       offer.capPkr,
      });
    }
  });

  const restaurants = [...byRest.values()]
    .filter((r) => r.saving > 0)
    .sort((a, b) => b.saving - a.saving);

  const annualSaving = result ? result.avgExpectedSaving * state.outingsPerWeek * 52 : 0;
  const fee          = result?.requirementStatus?.annualFeePkr ?? null;
  // Only show a separate "net" row if the fee is meaningful — otherwise it's
  // identical to "Annual" and just makes the modal look more crowded.
  const netAnnual    = fee !== null && fee > 0 ? annualSaving - fee : null;
  const applyUrl     = getBankApplyUrl(bank);
  const scoreInt     = result ? Math.round(Number(result.score) || 0) : "—";
  const tier         = result ? fitTier(result.score) : "";
  // Pre-build a WhatsApp share URL with a tight one-liner so users can pass
  // this card to a friend in a tap. wa.me works on web + mobile alike.
  const waShareUrl = result
    ? `https://wa.me/?text=${encodeURIComponent(
        `I found a card that could save me ~${formatCurrencyShort(annualSaving)}/year on ${isFoodCategory() ? "dining" : getActiveCategory().label.toLowerCase()}: ${result.bank} ${result.card}. Check it on konsacard.pk`
      )}`
    : null;
  inner.innerHTML = `
    <div class="cd-wrap">
      <div class="cd-head">
          <div class="cd-head-left">
            ${renderBankLogo(bank, "cd-logo")}
            <div class="cd-head-info">
              ${result ? `<div class="cd-score" style="color:${scoreColor(Number(result.score))}">${scoreInt} / 100 · ${escapeHtml(tier)}</div>` : ""}
              <div class="cd-card-name">${escapeHtml(card)}</div>
              <div class="cd-bank-name">${escapeHtml(bank)}</div>
            </div>
        </div>
        <div class="cd-head-actions">
          ${waShareUrl ? `<a class="btn-share-wa" href="${escapeAttr(waShareUrl)}" target="_blank" rel="noopener noreferrer" aria-label="Share via WhatsApp"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.5 3.5A11 11 0 0 0 3.6 17.2L2 22l4.9-1.6A11 11 0 1 0 20.5 3.5Zm-8.5 17a9 9 0 0 1-4.6-1.3l-.3-.2-3 1 1-2.9-.2-.3a9 9 0 1 1 7.1 3.7Zm5-6.7c-.3-.1-1.6-.8-1.9-.9-.3-.1-.4-.1-.6.1l-.8 1c-.2.2-.3.3-.6.1-.3-.2-1.2-.4-2.3-1.4-.9-.8-1.4-1.8-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.2-1 1-1 2.5s1 2.9 1.1 3.1c.2.3 2.1 3.3 5.2 4.6 2.6 1.1 3 1 3.5.9.6 0 1.7-.7 1.9-1.3.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3Z"/></svg> Share</a>` : ""}
          ${applyUrl ? `<a class="btn-apply" href="${escapeAttr(applyUrl)}" target="_blank" rel="noopener noreferrer">Apply →</a>` : ""}
          <button class="btn-modal-close" id="btn-cd-close" type="button" aria-label="Close">×</button>
        </div>
      </div>

      ${result ? `
      <div class="cd-stats">
        <div class="cd-stat cd-stat--saving">
          <div class="cd-stat-l">Saving / ${visitWord()}</div>
          <div class="cd-stat-v green">${formatCurrency(result.avgExpectedSaving)}</div>
        </div>
        <div class="cd-stat cd-stat--annual">
          <div class="cd-stat-l">Annual (${formatFrequency(state.outingsPerWeek)})</div>
          <div class="cd-stat-v green">~${formatCurrencyShort(annualSaving)}</div>
        </div>
        ${netAnnual !== null ? `
        <div class="cd-stat cd-stat--net">
          <div class="cd-stat-l">Net annual</div>
          <div class="cd-stat-v" style="color:${netAnnual >= 0 ? "var(--green)" : "var(--red)"}">${netAnnual >= 0 ? "~" : "-"}${formatCurrencyShort(Math.abs(netAnnual))}</div>
          <div class="cd-stat-sub">After ${formatCurrencyShort(fee)} fee</div>
        </div>` : ""}
        <div class="cd-stat cd-stat--restaurants">
          <div class="cd-stat-l">${venueNoun(undefined, true)} matched</div>
          <div class="cd-stat-v">${result.coveredVenueCount} of ${result.totalVenueCount}</div>
        </div>
      </div>` : ""}

      ${result ? renderRequirementSummary(result.requirementStatus, { showStatus: true }) : ""}

      ${result ? renderSourcesSection(result.requirementStatus.sourceIds) : ""}

      <div class="cd-section">
        <div class="cd-section-title">
          All ${isFoodCategory() ? "restaurant" : getActiveCategory().label.toLowerCase()} deals
          <span class="cd-section-sub">${restaurants.length} ${venueNoun(restaurants.length)} · at ${formatCurrency(state.orderValue)} ${billWord()}</span>
        </div>
        <input type="search" class="s-search cd-rest-search" placeholder="Search ${venueNoun()}…" autocomplete="off" />
        <div class="cd-rest-list"></div>
      </div>

      <div class="cd-report">
        <span class="cd-report-l">See something wrong?</span>
        <a class="cd-report-link" href="${escapeAttr(buildReportMailto(bank, card))}">Report this card →</a>
        <span class="cd-report-fresh">${escapeHtml(getDataFreshnessDaysAgo() !== null ? `Data verified ${formatDaysAgo(getDataFreshnessDaysAgo())}` : "")}</span>
      </div>
    </div>
  `;

  inner.querySelector("#btn-cd-close")?.addEventListener("click", closeCardDetail);
  const searchInput = inner.querySelector(".cd-rest-search");
  const listContainer = inner.querySelector(".cd-rest-list");
  const renderRows = () => {
    const term = searchInput?.value.trim().toLowerCase() || "";
    const filteredRestaurants = restaurants.filter((entry) => !term || entry.restaurant.toLowerCase().includes(term));
    renderCardDetailRestaurantRows(listContainer, filteredRestaurants);
  };
  searchInput?.addEventListener("input", () => {
    state.pagination.cardDetailRestaurants = 1;
    renderRows();
  });
  attachSearchTracking(
    searchInput,
    "card_detail",
    () => {
      const term = searchInput?.value.trim().toLowerCase() || "";
      return restaurants.filter((e) => !term || e.restaurant.toLowerCase().includes(term)).length;
    },
    { bank, card },
  );
  renderRows();
}

/* ── RESTAURANT VIEW ── */
function computeRestaurantDeals() {
  // FIRST-PAINT FAST PATH: at the default scope serve the precomputed
  // per-restaurant best-deal list straight from the summary. Same shared
  // saving math (getOfferSavingValue) produced these in precompute, so the
  // list is byte-identical to what the loop below would emit once raw offers
  // load. The summary rows carry exactly the fields renderRestaurantDealRows
  // reads (restaurant, city, saving, discountLabel, daysLabel, bestCard,
  // bestBank). Open-status / distance affordances come from
  // state.data.restaurants enrichment, which simply no-ops (renders without
  // the pill) until raw offers stream in — graceful, no crash.
  if (!state.data && isDefaultScope()) {
    const cityKey = getSummaryCityKey();
    const summaryDeals = cityKey ? state.summary?.restaurantDeals?.[cityKey] : null;
    if (Array.isArray(summaryDeals)) {
      const userLoc = getUserLocation();
      // Precompute already saving-sorted; only re-sort when the user has
      // granted location (distance sort needs branch coords we don't have at
      // default scope, so this falls back to saving order anyway).
      if (!userLoc) return summaryDeals;
      return [...summaryDeals]
        .map((d) => ({ d, km: getNearestBranchDistanceKm(d.restaurant, d.city, userLoc) }))
        .sort((a, b) => {
          if (a.km === null && b.km === null) return b.d.saving - a.d.saving;
          if (a.km === null) return 1;
          if (b.km === null) return -1;
          return a.km - b.km;
        })
        .map(({ d }) => d);
    }
  }

  // Defensive: every consumer either hits the summary fast path above or has
  // already loaded raw offers via a lazy-load trigger. Guard anyway so a stray
  // call with raw not loaded degrades to empty rather than throwing on null
  // (mirrors the compute functions in algorithms.js).
  if (!state.data?.offers) return [];

  const validKeys = new Set(computeRecommendations().map((r) => `${r.bank} || ${r.card}`));
  const effectiveDays = getEffectiveSelectedDays();
  const best = new Map();
  const cuisineFilter = state.selectedCuisines;
  const hasCuisineFilter = cuisineFilter && cuisineFilter.size > 0;

  // Best deal per restaurant among the cards the user already holds. Owned
  // cards skip the validKeys check: the card filters and eligibility gate
  // which cards to recommend, and say nothing about a card already in hand.
  const owned = state.ownedCards;
  const walletBest = new Map();

  state.data.offers.forEach((offer) => {
    if (!cityMatches(offer.city)) return;
    const cardKey = buildCardKey(offer.bank, offer.card);
    const isOwned = owned.size > 0 && owned.has(cardKey);
    const inScope = validKeys.has(cardKey);
    if (!inScope && !isOwned) return;
    if (state.selectedRestaurants.size > 0 && !state.selectedRestaurants.has(offer.restaurant)) return;
    if (!offer.days.some((d) => effectiveDays.has(d))) return;
    if (hasCuisineFilter) {
      const enr = getRestaurantEnrichment(offer.restaurant);
      const cuisines = enr?.servesCuisine || [];
      if (!cuisines.some((c) => cuisineFilter.has(c))) return;
    }

    const saving = getOfferSavingValue(offer, state.orderValue);
    if (!Number.isFinite(saving) || saving <= 0) return;

    const rk  = `${offer.city}|||${offer.restaurant}`;
    if (isOwned) {
      const w = walletBest.get(rk);
      if (!w || saving > w.saving) {
        walletBest.set(rk, { saving, discountLabel: offer.discountLabel, daysLabel: offer.daysLabel, card: offer.card, bank: offer.bank });
      }
    }
    if (!inScope) return;
    const cur = best.get(rk);
    if (!cur || saving > cur.saving) {
      best.set(rk, {
        restaurant:   offer.restaurant,
        city:         offer.city,
        saving,
        discountLabel: offer.discountLabel,
        discountPct:  getOfferDiscountPct(offer) || 0,
        daysLabel:    offer.daysLabel,
        bestCard:     offer.card,
        bestBank:     offer.bank,
      });
    }
  });

  const userLoc = getUserLocation();
  const deals = [...best.values()];
  // wallet: the best deal from an owned card, or null when no owned card
  // covers the restaurant. Left undefined when the wallet is empty so the
  // rows render exactly as before.
  if (owned.size > 0) {
    deals.forEach((d) => { d.wallet = walletBest.get(`${d.city}|||${d.restaurant}`) || null; });
    // Restaurants only an owned card covers (its card is outside the current
    // card filters). They belong in the "My wallet" list but not in "All".
    walletBest.forEach((w, rk) => {
      if (best.has(rk)) return;
      const [city, restaurant] = rk.split("|||");
      deals.push({
        restaurant, city,
        saving: w.saving,
        discountLabel: w.discountLabel,
        discountPct: 0,
        daysLabel: w.daysLabel,
        bestCard: w.card,
        bestBank: w.bank,
        wallet: w,
        walletOnlyRow: true,
      });
    });
  }
  // When the user has granted location, sort by distance to nearest branch.
  // Restaurants without coords fall to the end (saving-sorted within).
  if (userLoc) {
    return deals
      .map((d) => ({ d, km: getNearestBranchDistanceKm(d.restaurant, d.city, userLoc) }))
      .sort((a, b) => {
        if (a.km === null && b.km === null) return b.d.saving - a.d.saving;
        if (a.km === null) return 1;
        if (b.km === null) return -1;
        return a.km - b.km;
      })
      .map(({ d }) => d);
  }
  return deals.sort((a, b) => b.saving - a.saving);
}

// Source the restaurant deals list for the restaurants view. At default scope
// (raw not loaded) the precomputed summary.restaurantDeals matches
// computeRestaurantDeals()'s output shape exactly. Otherwise compute normally.
// Note: distance-sorting in computeRestaurantDeals only kicks in when the user
// has granted geolocation — which is itself a raw-loading trigger path — so the
// summary list is always the saving-sorted default.
function getRestaurantDealsForRender() {
  const cityKey = getSummaryCityKey();
  if (isDefaultScope() && state.summary && cityKey) {
    return state.summary.restaurantDeals[cityKey];
  }
  return computeRestaurantDeals();
}

function renderRestaurantView(resultsGrid) {
  if (!resultsGrid) return;
  const scopedDeals = getRestaurantDealsForRender();
  const allDeals = scopedDeals.filter((d) => !d.walletOnlyRow);

  if (scopedDeals.length === 0) {
    resultsGrid.innerHTML = "";
    return;
  }

  const userLoc = getUserLocation();
  const locateLabel = userLoc ? "Sorted by distance" : "Sort by distance";
  const hasGeo = typeof navigator !== "undefined" && "geolocation" in navigator;

  // "My wallet" scope: only restaurants one of the user's own cards covers,
  // ranked by what their best owned card saves there. Distance order (when
  // located) is kept as-is.
  const hasWallet = state.ownedCards.size > 0;
  const walletDeals = hasWallet ? scopedDeals.filter((d) => d.wallet) : [];
  if (hasWallet && !userLoc) walletDeals.sort((a, b) => b.wallet.saving - a.wallet.saving);
  const walletOnly = hasWallet && state.restaurantWalletOnly;
  const deals = walletOnly ? walletDeals : allDeals;
  // Keep what the user typed when they flip the scope (the view re-renders).
  const prevTerm = resultsGrid.querySelector(".rest-view-search")?.value || "";

  const scopeHtml = hasWallet ? `
    <div class="rest-scope" role="group" aria-label="Which ${venueNoun()} to show">
      <button type="button" class="rest-scope-btn ${walletOnly ? "" : "active"}" data-wallet-only="0" aria-pressed="${!walletOnly}">All <span class="rest-scope-n">${allDeals.length}</span></button>
      <button type="button" class="rest-scope-btn ${walletOnly ? "active" : ""}" data-wallet-only="1" aria-pressed="${walletOnly}">My wallet <span class="rest-scope-n">${walletDeals.length}</span></button>
    </div>` : "";

  resultsGrid.innerHTML = `
    <div class="rest-view-search-wrap" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
      ${scopeHtml}
      <input type="search" class="s-search rest-view-search" placeholder="Search ${deals.length} ${walletOnly ? "wallet " : ""}${venueNoun()}…" autocomplete="off" value="${escapeAttr(prevTerm)}" style="flex:1;min-width:200px" />
      ${hasGeo ? `<button class="btn-locate ${userLoc ? "is-active" : ""}" id="btn-locate-restaurants" type="button">${escapeHtml(locateLabel)}</button>` : ""}
    </div>
    ${hasWallet ? "" : `<div class="rest-wallet-hint">Add the cards you carry under <button type="button" class="rest-wallet-hint-link" id="rest-wallet-hint-link">My Wallet</button> to see which of these they cover.</div>`}
    <div class="rest-view-list"></div>
  `;

  resultsGrid.querySelectorAll(".rest-scope-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = btn.dataset.walletOnly === "1";
      if (next === state.restaurantWalletOnly) return;
      state.restaurantWalletOnly = next;
      state.pagination.restaurantView = 1;
      trackEvent("restaurants_scope", { scope: next ? "wallet" : "all", count: next ? walletDeals.length : allDeals.length });
      saveStateToLocal();
      renderRecommendations(); // refreshes the result header count too
    });
  });
  resultsGrid.querySelector("#rest-wallet-hint-link")?.addEventListener("click", () => {
    closeAllModals();
    state.viewMode = "my-wallet";
    trackEvent("view_change", { view: "my-wallet", source: "restaurants_hint" });
    renderForView();
  });

  const searchInput = resultsGrid.querySelector(".rest-view-search");
  const listContainer = resultsGrid.querySelector(".rest-view-list");
  const renderRows = () => {
    const term = searchInput?.value.trim().toLowerCase() || "";
    const filteredDeals = deals.filter((deal) => !term || deal.restaurant.toLowerCase().includes(term));
    renderRestaurantDealRows(listContainer, filteredDeals, { walletOnly, searching: !!term });
  };
  searchInput?.addEventListener("input", () => {
    state.pagination.restaurantView = 1;
    renderRows();
  });
  attachSearchTracking(searchInput, "restaurants_list", () => {
    const term = searchInput?.value.trim().toLowerCase() || "";
    return deals.filter((d) => !term || d.restaurant.toLowerCase().includes(term)).length;
  });
  const locateBtn = resultsGrid.querySelector("#btn-locate-restaurants");
  locateBtn?.addEventListener("click", async () => {
    if (getUserLocation()) {
      // Already located — clicking again clears the location preference.
      state.userLocation = null;
      try { localStorage.removeItem(USER_LOCATION_KEY); } catch {}
      // Distance-sorting runs through computeRestaurantDeals() + branch
      // enrichment, so ensure raw offers are loaded before re-rendering.
      withRawOffers(render);
      return;
    }
    locateBtn.disabled = true;
    locateBtn.textContent = "Locating…";
    const loc = await requestUserLocation();
    locateBtn.disabled = false;
    if (!loc) {
      locateBtn.textContent = "Location unavailable";
      setTimeout(() => { locateBtn.textContent = locateLabel; }, 2500);
      return;
    }
    withRawOffers(render);
  });
  renderRows();
}

function openRestaurantDetail(restaurantKey, source) {
  // Restaurant detail reads per-offer data (state.data.offers) → ensure raw
  // offers are loaded before rendering the modal body.
  if (!state.data) {
    withRawOffers(() => openRestaurantDetail(restaurantKey, source));
    return;
  }
  state.detailRestaurantKey = restaurantKey;
  state.pagination.restaurantDetailCards = 1;
  const modal = document.getElementById("restaurant-detail-modal");
  const inner = document.getElementById("restaurant-detail-inner");
  if (!modal || !inner) return;
  const [city, restaurant] = String(restaurantKey).split("|||");
  trackEvent("restaurant_open", {
    restaurant,
    city,
    source: source || "unknown",
  });
  renderRestaurantDetailModal(inner);
  modal.style.display = "flex";
}

function closeRestaurantDetail() {
  state.detailRestaurantKey = null;
  const modal = document.getElementById("restaurant-detail-modal");
  if (modal) modal.style.display = "none";
}

function renderRestaurantDetailModal(inner) {
  const key = state.detailRestaurantKey;
  if (!key || !state.data) return;

  const [city, restaurant] = key.split("|||");
  const validKeys = new Set(computeRecommendations().map((r) => `${r.bank} || ${r.card}`));
  const bestByCard = new Map();

  state.data.offers.forEach((offer) => {
    if (offer.city !== city || offer.restaurant !== restaurant) return;
    if (!validKeys.has(`${offer.bank} || ${offer.card}`)) return;

    const saving = getOfferSavingValue(offer, state.orderValue);
    if (!Number.isFinite(saving) || saving <= 0) return;

    const cardKey = `${offer.bank} || ${offer.card}`;
    const current = bestByCard.get(cardKey);
    if (!current || saving > current.saving) {
      bestByCard.set(cardKey, {
        key: cardKey,
        bank: offer.bank,
        card: offer.card,
        saving,
        offerTitle: offer.offerTitle,
        offerDescription: offer.offerDescription,
        orderTypes: offer.orderTypes || [],
        discountLabel: offer.discountLabel,
        discountPct: getOfferDiscountPct(offer) || 0,
        daysLabel: offer.daysLabel,
        capPkr: offer.capPkr ?? null,
        applyUrl: getBankApplyUrl(offer.bank),
      });
    }
  });

  const cards = [...bestByCard.values()].sort((a, b) => {
    if (b.saving !== a.saving) return b.saving - a.saving;
    return b.discountPct - a.discountPct;
  });

  const enr = getRestaurantEnrichment(restaurant);
  const openStatus = getRestaurantOpenStatus(restaurant, city);
  const cuisines = enr?.servesCuisine || [];
  const branches = getBranchesForRestaurant(restaurant, city);
  const description = enr?.description || "";
  const socialsHtml = renderSocialLinks(restaurant);

  const openPillHtml = openStatus
    ? `<span class="rest-open-pill ${openStatus === "open" ? "is-open" : "is-closed"}"><span class="dot"></span>${openStatus === "open" ? "Open now" : "Closed"}</span>`
    : "";

  inner.innerHTML = `
    <div class="cd-wrap">
      <div class="cd-head">
        <div class="cd-head-left">
          <div class="cd-logo rd-logo">${renderRestaurantLogo(restaurant, "restaurant-logo")}</div>
          <div class="cd-head-info">
            <div class="cd-card-name">${escapeHtml(restaurant)}</div>
            <div class="cd-bank-name">${escapeHtml(city)} · ${cards.length} matching cards ${openPillHtml ? "· " + openPillHtml : ""}</div>
          </div>
        </div>
        <div class="cd-head-actions">
          <button class="btn-fav${state.favoriteRestaurants.has(restaurant) ? " active" : ""}" id="btn-rd-fav" type="button" aria-label="Favorite this ${escapeAttr(venueNoun(1))}" aria-pressed="${state.favoriteRestaurants.has(restaurant)}" title="Get notified about new offers here">
            <span class="btn-fav-star">${state.favoriteRestaurants.has(restaurant) ? phIcon("star-fill") : phIcon("star")}</span>
            <span class="btn-fav-label">${state.favoriteRestaurants.has(restaurant) ? "Saved" : "Save"}</span>
          </button>
          <button class="btn-modal-close" id="btn-rd-close" type="button">×</button>
        </div>
      </div>

      ${(description || cuisines.length || socialsHtml) ? `
      <div class="cd-section">
        ${description ? `<div class="rd-desc">${escapeHtml(description)}</div>` : ""}
        ${cuisines.length ? `<div class="rd-cuisine-row">${cuisines.map((c) => `<span class="rd-cuisine-tag">${escapeHtml(c)}</span>`).join("")}</div>` : ""}
        ${socialsHtml}
      </div>` : ""}

      <div class="cd-section">
        <div class="cd-section-title">
          Available card discounts
          <span class="cd-section-sub">at ${formatCurrency(state.orderValue)} bill</span>
        </div>
        <input type="search" class="s-search rd-card-search" placeholder="Search cards or banks…" autocomplete="off" />
        <div class="cd-rest-list rd-card-list"></div>
      </div>

      ${branches.length ? `
      <div class="cd-section">
        <div class="cd-section-title">Branches in ${escapeHtml(city)} <span class="cd-section-sub">${branches.length}</span></div>
        <div class="rd-branches">
          ${branches.map((b) => renderBranchCard(b)).join("")}
        </div>
      </div>` : ""}
    </div>
  `;

  inner.querySelector("#btn-rd-close")?.addEventListener("click", closeRestaurantDetail);
  inner.querySelector("#btn-rd-fav")?.addEventListener("click", () => {
    toggleFavoriteRestaurant(restaurant);
    // Re-render so the star/label flips
    renderRestaurantDetailModal(inner);
  });
  const searchInput = inner.querySelector(".rd-card-search");
  const listContainer = inner.querySelector(".rd-card-list");
  const renderRows = () => {
    const term = searchInput?.value.trim().toLowerCase() || "";
    const filteredCards = cards.filter((entry) => !term || `${entry.card} ${entry.bank}`.toLowerCase().includes(term));
    renderRestaurantDetailCardRows(listContainer, filteredCards);
  };
  searchInput?.addEventListener("input", () => {
    state.pagination.restaurantDetailCards = 1;
    renderRows();
  });
  attachSearchTracking(
    searchInput,
    "restaurant_detail",
    () => {
      const term = searchInput?.value.trim().toLowerCase() || "";
      return cards.filter((e) => !term || `${e.card} ${e.bank}`.toLowerCase().includes(term)).length;
    },
    { restaurant, city },
  );
  renderRows();
}

/* ── EXCLUSIVE RESTAURANTS FOR COMPARE ── */
function getExclusiveRestaurantCounts(key1, key2) {
  // Reached only via openCompareModal (which lazy-loads raw offers first), but
  // guard defensively so a stray call can't throw on null state.data.
  if (!state.data?.offers) return [0, 0];
  const [b1, c1] = key1.split(" || ");
  const [b2, c2] = key2.split(" || ");
  const get = (bank, card) => new Set(
    state.data.offers
      .filter((o) => o.bank === bank && o.card === card && cityMatches(o.city))
      .map((o) => o.restaurant),
  );
  const r1 = get(b1, c1);
  const r2 = get(b2, c2);
  return [[...r1].filter((r) => !r2.has(r)).length, [...r2].filter((r) => !r1.has(r)).length];
}

function getCompareRestaurantRows(compareKeys) {
  if (!state.data || !Array.isArray(compareKeys) || compareKeys.length !== 2) return [];

  const selectedDays = getEffectiveSelectedDays();
  const keySet = new Set(compareKeys);
  const byVenue = new Map();

  state.data.offers.forEach((offer) => {
    if (!cityMatches(offer.city)) return;
    if (!cardTypeMatches(offer.cardCategory)) return;
    if (state.selectedRestaurants.size > 0 && !state.selectedRestaurants.has(offer.restaurant)) return;

    const cardKey = `${offer.bank} || ${offer.card}`;
    if (!keySet.has(cardKey)) return;

    const saving = getOfferSavingValue(offer, state.orderValue);
    if (!Number.isFinite(saving) || saving <= 0) return;

    const venueKey = `${offer.city}|||${offer.restaurant}`;
    if (!byVenue.has(venueKey)) {
      byVenue.set(venueKey, {
        venueKey,
        city: offer.city,
        restaurant: offer.restaurant,
        byCard: new Map(compareKeys.map((key) => [key, new Map()])),
      });
    }

    const dayMap = byVenue.get(venueKey).byCard.get(cardKey);
    selectedDays.forEach((day) => {
      if (!offer.days.includes(day)) return;
      const current = dayMap.get(day);
      const candidate = {
        saving,
        discountLabel: offer.discountLabel,
        offerTitle: offer.offerTitle,
        offerDescription: offer.offerDescription,
        orderTypes: offer.orderTypes || [],
        capPkr: offer.capPkr ?? null,
      };
      if (!current || candidate.saving > current.saving) {
        dayMap.set(day, candidate);
      }
    });
  });

  const summarizeDayMap = (dayMap) => {
    if (!dayMap || dayMap.size === 0) return null;
    const matches = Array.from(dayMap.entries()).sort((a, b) => a[0] - b[0]);
    const strongest = matches.reduce((best, [, entry]) =>
      !best || entry.saving > best.saving ? entry : best, null);
    return {
      saving: strongest.saving,
      discountLabel: strongest.discountLabel,
      offerTitle: strongest.offerTitle,
      offerDescription: strongest.offerDescription,
      orderTypes: strongest.orderTypes,
      capPkr: strongest.capPkr,
      daysLabel: matches.length === selectedDays.size
        ? "All chosen days"
        : matches.map(([day]) => DAY_SHORT[day]).join(", "),
    };
  };

  return Array.from(byVenue.values())
    .map((venue) => {
      const entries = compareKeys.map((key) => summarizeDayMap(venue.byCard.get(key)));
      if (entries.every((entry) => !entry)) return null;
      const strongestSaving = Math.max(...entries.map((entry) => entry?.saving ?? 0));
      return { ...venue, entries, strongestSaving };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aShared = a.entries.every(Boolean) ? 1 : 0;
      const bShared = b.entries.every(Boolean) ? 1 : 0;
      if (bShared !== aShared) return bShared - aShared;
      if (b.strongestSaving !== a.strongestSaving) return b.strongestSaving - a.strongestSaving;
      return a.restaurant.localeCompare(b.restaurant);
    });
}

function renderRestaurantDealRows(container, deals, opts = {}) {
  if (!container) return;
  const { walletOnly = false, searching = false } = opts;
  if (deals.length === 0) {
    const msg = walletOnly && !searching
      ? "None of your cards have a deal here with the current filters."
      : walletOnly
        ? `None of your cards cover a ${venueNoun(1)} matching that search.`
        : `No ${venueNoun(1)} deals match that search.`;
    container.innerHTML = `<div class="cd-empty">${msg}</div>`;
    return;
  }

  const pageData = paginateItems(deals, "restaurantView", PAGINATION_ITEMS_PER_PAGE);
  const userLoc = getUserLocation();
  container.innerHTML = `
    ${pageData.items.map((deal, index) => {
      const openStatus = getRestaurantOpenStatus(deal.restaurant, deal.city);
      const openPill = openStatus
        ? `<span class="rest-open-pill ${openStatus === "open" ? "is-open" : "is-closed"}"><span class="dot"></span>${openStatus === "open" ? "Open" : "Closed"}</span>`
        : "";
      const distanceKm = userLoc ? getNearestBranchDistanceKm(deal.restaurant, deal.city, userLoc) : null;
      const distanceLabel = distanceKm !== null ? ` · <span class="rest-distance">${escapeHtml(formatDistanceKm(distanceKm))}</span>` : "";
      // deal.wallet: undefined = no wallet set up, null = wallet doesn't
      // cover this restaurant, object = best deal from an owned card.
      const w = deal.wallet;
      const bestIsOwned = w && w.card === deal.bestCard && w.bank === deal.bestBank;
      let cardLine;
      if (walletOnly) {
        cardLine = `<div class="rest-deal-card">Use your <strong>${escapeHtml(w.card)}</strong> <span style="color:var(--muted)">by ${escapeHtml(w.bank)}</span>${bestIsOwned ? "" : ` <span class="rest-wallet-note">· best overall: ${escapeHtml(deal.discountLabel)} with ${escapeHtml(deal.bestCard)}</span>`}</div>`;
      } else {
        cardLine = `<div class="rest-deal-card">Best with: <strong>${escapeHtml(deal.bestCard)}</strong> <span style="color:var(--muted)">by ${escapeHtml(deal.bestBank)}</span></div>`;
        if (w === null) {
          cardLine += `<div class="rest-wallet-line is-none">Not covered by your wallet</div>`;
        } else if (w && bestIsOwned) {
          cardLine += `<div class="rest-wallet-line is-yes">${phIcon("check")} In your wallet</div>`;
        } else if (w) {
          cardLine += `<div class="rest-wallet-line is-yes">${phIcon("check")} Your ${escapeHtml(w.card)}: ${escapeHtml(w.discountLabel)} · ${formatCurrency(w.saving)}</div>`;
        }
      }
      const pct = walletOnly ? w.discountLabel : deal.discountLabel;
      const saving = walletOnly ? w.saving : deal.saving;
      return `
      <button class="rest-deal-row" style="animation-delay:${index * 0.04}s" data-restaurant-key="${escapeAttr(`${deal.city}|||${deal.restaurant}`)}" type="button">
        <div class="rest-deal-rank">${pageData.startIndex + index + 1}</div>
        <div class="rest-deal-body">
          <div class="rest-deal-name">${escapeHtml(deal.restaurant)} ${openPill}</div>
          <div class="rest-deal-sub">${escapeHtml(deal.city)} · ${escapeHtml(deal.daysLabel)}${distanceLabel}</div>
          ${cardLine}
        </div>
        <div class="rest-deal-right">
          <div class="rest-deal-pct">${escapeHtml(pct)}</div>
          <div class="rest-deal-saving">${formatCurrency(saving)}</div>
        </div>
      </button>
    `;
    }).join("")}
    ${renderPaginationControls("restaurantView", pageData)}
  `;

  bindPaginationControls(container, "restaurantView", () => renderRestaurantDealRows(container, deals, opts));
  container.querySelectorAll(".rest-deal-row").forEach((row) => {
    row.addEventListener("click", () => openRestaurantDetail(row.dataset.restaurantKey, "restaurants_list"));
  });
}

function renderCardDetailRestaurantRows(container, restaurants) {
  if (!container) return;
  if (restaurants.length === 0) {
    container.innerHTML = `<div class="cd-empty">No active deals match that search.</div>`;
    return;
  }

  const pageData = paginateItems(restaurants, "cardDetailRestaurants", PAGINATION_ITEMS_PER_PAGE);
  container.innerHTML = `
    ${pageData.items.map((entry) => `
      <div class="cd-rest-row">
        <div class="cd-rest-info">
          <div class="cd-rest-name">${escapeHtml(entry.restaurant)}</div>
          <div class="cd-rest-meta">${escapeHtml(entry.city)} · ${escapeHtml(entry.daysLabel)}${entry.capPkr ? ` · cap ${formatCurrency(entry.capPkr)}` : ""}${entry.offerTitle ? ` · ${escapeHtml(entry.offerTitle)}` : ""}</div>
          ${entry.orderTypes && entry.orderTypes.length ? `<div class="cd-rest-meta" style="margin-top:2px">${renderOrderTypeBadges(entry.orderTypes)}</div>` : ""}
          ${entry.offerDescription ? `<div class="cd-rest-meta" style="margin-top:1px"><span class="offer-detail-toggle" onclick="var d=this.nextElementSibling;d.style.display=d.style.display==='none'?'block':'none';this.textContent=d.style.display==='none'?'Details ▾':'Details ▴'" style="cursor:pointer;font-size:11px;color:var(--brand);font-weight:600">Details ▾</span><div class="offer-detail-text" style="display:none;font-size:11px;color:var(--muted);margin-top:2px;line-height:1.4">${escapeHtml(entry.offerDescription)}</div></div>` : ""}
        </div>
        <div class="cd-rest-right">
          <span class="cd-rest-pct">${escapeHtml(entry.discountLabel)}</span>
          <span class="cd-rest-saving">${formatCurrency(entry.saving)}</span>
        </div>
      </div>
    `).join("")}
    ${renderPaginationControls("cardDetailRestaurants", pageData)}
  `;

  bindPaginationControls(container, "cardDetailRestaurants", () => renderCardDetailRestaurantRows(container, restaurants));
}

function renderRestaurantDetailCardRows(container, cards) {
  if (!container) return;
  if (cards.length === 0) {
    container.innerHTML = `<div class="cd-empty">No matching card deals for this ${venueNoun(1)} under your current filters.</div>`;
    return;
  }

  const pageData = paginateItems(cards, "restaurantDetailCards", PAGINATION_ITEMS_PER_PAGE);
  container.innerHTML = `
    ${pageData.items.map((entry) => `
      <div class="cd-rest-row rd-card-row">
        <div class="cd-rest-info">
          <div class="rd-card-top">
            ${renderBankLogo(entry.bank, "rd-card-logo")}
            <div class="rd-card-copy">
              <div class="cd-rest-name">${escapeHtml(entry.card)}</div>
              <div class="cd-rest-meta">${escapeHtml(entry.bank)} · ${escapeHtml(entry.daysLabel)}${entry.capPkr ? ` · cap ${formatCurrency(entry.capPkr)}` : ""}${entry.offerTitle ? ` · ${escapeHtml(entry.offerTitle)}` : ""}</div>
              ${entry.orderTypes && entry.orderTypes.length ? `<div class="cd-rest-meta" style="margin-top:2px">${renderOrderTypeBadges(entry.orderTypes)}</div>` : ""}
              ${entry.offerDescription ? `<div class="cd-rest-meta" style="margin-top:1px"><span class="offer-detail-toggle" onclick="var d=this.nextElementSibling;d.style.display=d.style.display==='none'?'block':'none';this.textContent=d.style.display==='none'?'Details ▾':'Details ▴'" style="cursor:pointer;font-size:11px;color:var(--brand);font-weight:600">Details ▾</span><div class="offer-detail-text" style="display:none;font-size:11px;color:var(--muted);margin-top:2px;line-height:1.4">${escapeHtml(entry.offerDescription)}</div></div>` : ""}
            </div>
          </div>
        </div>
        <div class="cd-rest-right">
          <span class="cd-rest-pct">${escapeHtml(entry.discountLabel)}</span>
          <span class="cd-rest-saving">${formatCurrency(entry.saving)}</span>
          <div class="rd-card-actions">
            <button class="btn-detail rd-open-card" data-key="${escapeAttr(entry.key)}" type="button" title="View card details">⤢</button>
            ${entry.applyUrl ? `<a class="btn-apply btn-apply-sm" href="${escapeAttr(entry.applyUrl)}" target="_blank" rel="noopener noreferrer">Apply →</a>` : ""}
          </div>
        </div>
      </div>
    `).join("")}
    ${renderPaginationControls("restaurantDetailCards", pageData)}
  `;

  bindPaginationControls(container, "restaurantDetailCards", () => renderRestaurantDetailCardRows(container, cards));
  container.querySelectorAll(".rd-open-card").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      const cardKey = e.currentTarget.dataset.key;
      closeRestaurantDetail();
      openCardDetail(cardKey);
    });
  });
}

function renderCompareRestaurantRows(container, cards, rows) {
  if (!container) return;
  if (rows.length === 0) {
    container.innerHTML = `<div class="cd-empty">No ${venueNoun(1)} deals are available for these two cards under the current filters.</div>`;
    return;
  }

  const pageData = paginateItems(rows, "compareRestaurants", PAGINATION_ITEMS_PER_PAGE);
  container.innerHTML = `
    ${pageData.items.map((row) => `
      <button class="cmp-rest-row" data-restaurant-key="${escapeAttr(row.venueKey)}" type="button">
        <div class="cmp-rest-venue">
          <div class="cmp-rest-venue-top">
            <div class="cmp-rest-venue-name">${escapeHtml(row.restaurant)}</div>
          </div>
          <div class="cmp-rest-venue-meta">${escapeHtml(row.city)}</div>
        </div>
        ${row.entries.map((entry, i) => `
          <div class="cmp-rest-card${entry ? "" : " empty"}">
            <div class="cmp-rest-card-label">${escapeHtml(cards[i].card)}</div>
            <div class="cmp-rest-card-bank">${escapeHtml(cards[i].bank)}</div>
            ${entry
              ? `
                <div class="cmp-rest-card-pct">${escapeHtml(entry.discountLabel)}</div>
                <div class="cmp-rest-card-saving">${formatCurrency(entry.saving)}</div>
                <div class="cmp-rest-card-meta">${escapeHtml(entry.daysLabel)}${entry.capPkr ? ` · cap ${formatCurrency(entry.capPkr)}` : ""}${entry.offerTitle ? ` · ${escapeHtml(entry.offerTitle)}` : ""}</div>
              `
              : `
                <div class="cmp-rest-card-pct">—</div>
                <div class="cmp-rest-card-meta">No active deal</div>
              `}
          </div>
        `).join("")}
      </button>
    `).join("")}
    ${renderPaginationControls("compareRestaurants", pageData)}
  `;

  bindPaginationControls(container, "compareRestaurants", () => renderCompareRestaurantRows(container, cards, rows));
  container.querySelectorAll(".cmp-rest-row").forEach((row) => {
    row.addEventListener("click", () => openRestaurantDetail(row.dataset.restaurantKey, "compare"));
  });
}

function paginateItems(items, pageKey, pageSize = PAGINATION_ITEMS_PER_PAGE) {
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(state.pagination[pageKey] || 1, 1), totalPages);
  state.pagination[pageKey] = currentPage;
  const startIndex = (currentPage - 1) * pageSize;

  return {
    items: items.slice(startIndex, startIndex + pageSize),
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex: Math.min(startIndex + pageSize, totalItems),
  };
}

function renderPaginationControls(pageKey, pageData) {
  if (!pageData || pageData.totalPages <= 1) return "";
  const pageListCount = Math.max(0, pageData.endIndex - pageData.startIndex);
  const metaText = pageKey === "results"
    ? `Showing ${pageListCount + (pageData.currentPage === 1 ? 1 : 0)} cards on this page${pageData.currentPage === 1 ? " (plus top pick above)" : ""} · ${pageData.totalItems + 1} total matches`
    : `Showing ${pageData.startIndex + 1}-${pageData.endIndex} of ${pageData.totalItems}`;
  return `
    <div class="pager">
      <div class="pager-meta">${metaText}</div>
      <div class="pager-actions">
        <button class="pager-btn" data-page-key="${escapeAttr(pageKey)}" data-page-action="prev" type="button"${pageData.currentPage === 1 ? " disabled" : ""}>Prev</button>
        <div class="pager-label">Page ${pageData.currentPage} of ${pageData.totalPages}</div>
        <button class="pager-btn" data-page-key="${escapeAttr(pageKey)}" data-page-action="next" type="button"${pageData.currentPage === pageData.totalPages ? " disabled" : ""}>Next</button>
      </div>
    </div>
  `;
}

function bindPaginationControls(container, pageKey, onChange) {
  container.querySelectorAll(`[data-page-key="${pageKey}"]`).forEach((btn) => {
    btn.addEventListener("click", () => {
      const currentPage = state.pagination[pageKey] || 1;
      state.pagination[pageKey] = btn.dataset.pageAction === "prev" ? currentPage - 1 : currentPage + 1;
      onChange();
    });
  });
}

/* ── OUTINGS PILLS ──
   Food: 1–4× per week. Other categories: 1/2/4/8× per month, stored as the
   equivalent outingsPerWeek so every downstream calculation is unchanged. */
function renderOutingsPills() {
  const container = document.getElementById("outings-pills");
  if (!container) return;
  container.innerHTML = "";
  const food = isFoodCategory();
  const options = food ? [1, 2, 3, 4] : MONTHLY_VISIT_OPTIONS;
  const current = food ? state.outingsPerWeek : weeklyToVisitsPerMonth(state.outingsPerWeek);
  options.forEach((n) => {
    const btn = document.createElement("button");
    btn.className = `s-pill${current === n ? " active" : ""}`;
    btn.textContent = food && n === 4 ? "4×+" : `${n}×`;
    btn.type = "button";
    btn.addEventListener("click", () => {
      state.outingsPerWeek = food ? n : visitsPerMonthToWeekly(n);
      render();
    });
    container.appendChild(btn);
  });
}

/* ── TOP PICK REASON ── */
function buildTopPickReason(result) {
  const venueCtx = `covers ${result.coveredVenueCount} of ${result.totalVenueCount} ${venueNoun(result.totalVenueCount)}`;
  const topRest = result.topMatches[0]?.restaurant;
  const savingCtx = topRest ? `best saving at ${topRest}` : `highest savings at your ${billWord()} size`;
  return `Ranked #1: ${venueCtx}, with the ${savingCtx}.`;
}

/* ── MOBILE FILTER BADGE ── */
function getActiveFilterCount() {
  let n = 0;
  if (state.selectedCity !== "all") n++;
  if (state.selectedDays.size > 0) n++;
  if (state.selectedRestaurants.size > 0) n++;
  if (state.selectedBanks.size > 0) n++;
  if (state.selectedCards.size > 0) n++;
  if (state.selectedCardTypes.size > 0) n++;
  if (state.useEligibility) n++;
  return n;
}

function updateMobileFilterBadge() {
  // The badge sits inside the result-header Filters button on mobile.
  // The button itself is hidden on desktop via CSS, so writing to it on
  // every render is a no-op there.
  const badge = document.getElementById("rh-filters-btn-badge");
  if (!badge) return;
  const n = getActiveFilterCount();
  if (n > 0) {
    badge.textContent = String(n);
    badge.removeAttribute("hidden");
  } else {
    badge.setAttribute("hidden", "");
  }
}

/* ── SHAREABLE URL ── */
function encodeStateToUrl() {
  const params = new URLSearchParams();
  if (state.selectedCity !== "all") params.set("city", state.selectedCity);
  if (!isFoodCategory()) params.set("cat", state.category);
  // bill / frequency are relative to the active category's defaults. Food
  // keeps its historical `outings` (per week); other categories use `visits`
  // (per month) so shared links stay readable.
  if (state.orderValue !== getCategoryDefaultBill()) params.set("bill", state.orderValue);
  if (isFoodCategory()) {
    if (state.outingsPerWeek !== 1) params.set("outings", state.outingsPerWeek);
  } else if (Math.abs(state.outingsPerWeek - getCategoryDefaultFrequency()) > 1e-9) {
    params.set("visits", weeklyToVisitsPerMonth(state.outingsPerWeek));
  }
  if (state.selectedDays.size > 0) params.set("days", Array.from(state.selectedDays).join(","));
  if (state.selectedCardTypes.size > 0) params.set("types", Array.from(state.selectedCardTypes).join(","));
  // Use repeated params for multi-select (banks, rests, cards)
  if (state.selectedBanks.size > 0) {
    for (const bank of state.selectedBanks) {
      params.append("banks", bank);
    }
  }
  if (state.selectedRestaurants.size > 0) {
    for (const rest of state.selectedRestaurants) {
      params.append("rests", rest);
    }
  }
  if (state.selectedCards.size > 0) {
    for (const card of state.selectedCards) {
      params.append("cards", card);
    }
  }
  if (state.selectedCuisines.size > 0) {
    for (const cuisine of state.selectedCuisines) {
      params.append("cuisines", cuisine);
    }
  }
  if (state.useEligibility) params.set("elig", "1");
  // Salary and balance are never written to the URL: analytics tools record
  // the page URL with every event, and the privacy policy promises these
  // figures stay on the device. Old links that carry them are still read.
  if (state.viewMode && state.viewMode !== "cards") params.set("view", state.viewMode);
  if (state.viewMode === "overall") {
    const spend = Object.entries(state.monthlySpend).filter(([, v]) => v > 0).map(([k, v]) => `${k}:${v}`).join(",");
    if (spend) params.set("spend", spend);
  }
  if (state.ownedCards.size > 0) {
    for (const key of state.ownedCards) {
      params.append("owned", key);
    }
  }
  if (state.walletSize !== 2) params.set("k", state.walletSize);
  if (state.walletBuildOnOwned) params.set("build", "owned");
  if (state.walletMaxFee !== null) params.set("maxfee", state.walletMaxFee);
  if (state.walletNoSameBank) params.set("nobank", "1");
  if (state.walletMixedTypes) params.set("mix", "1");
  if (state.walletObjective && state.walletObjective !== "savings") params.set("obj", state.walletObjective);
  if (state.walletMustInclude.size > 0) {
    for (const key of state.walletMustInclude) params.append("must", key);
  }
  const qs = params.toString();
  history.replaceState(null, "", qs ? `${location.pathname}?${qs}` : location.pathname);
  // Mirror to localStorage so settings survive across sessions (URL params
  // already cover shared-link state; this layer covers returning users).
  saveStateToLocal();
}

/* ── LOCAL PERSISTENCE ──
   Stores wallet + filter + eligibility state in localStorage so a returning
   user finds the app as they left it. URL params still take precedence on
   init, so shared links keep working. No expiry: localStorage persists until
   the user explicitly clears browser data. Search terms and
   pagination state are intentionally not persisted (they're transient). */
const LS_KEY = "konsacard_state_v1";
// Drop chat history left behind by the removed AI assistant.
try { localStorage.removeItem("konsacard.chat.v1"); } catch { /* ignore */ }
function saveStateToLocal() {
  try {
    rememberCategoryPrefs();
    const foodPrefs = state.categoryPrefs.food || { orderValue: 10000, outingsPerWeek: 1 };
    const payload = {
      v: 1,
      selectedCity: state.selectedCity,
      // Top-level bill/outings are food's (older builds read them as-is);
      // other categories' live in categoryPrefs.
      orderValue: foodPrefs.orderValue,
      outingsPerWeek: foodPrefs.outingsPerWeek,
      category: state.category || "food",
      categoryPrefs: state.categoryPrefs,
      selectedDays: Array.from(state.selectedDays),
      selectedCardTypes: Array.from(state.selectedCardTypes),
      selectedBanks: Array.from(state.selectedBanks),
      selectedRestaurants: Array.from(state.selectedRestaurants),
      selectedCards: Array.from(state.selectedCards),
      useEligibility: state.useEligibility,
      monthlySalary: state.monthlySalary,
      accountBalance: state.accountBalance,
      viewMode: state.viewMode,
      ownedCards: Array.from(state.ownedCards),
      walletSize: state.walletSize,
      walletBuildOnOwned: state.walletBuildOnOwned,
      walletMaxFee: state.walletMaxFee,
      walletNoSameBank: state.walletNoSameBank,
      walletMixedTypes: state.walletMixedTypes,
      walletObjective: state.walletObjective,
      walletMustInclude: Array.from(state.walletMustInclude),
      walletAdvancedOpen: state.walletAdvancedOpen,
      favoriteRestaurants: Array.from(state.favoriteRestaurants),
      selectedCuisines: Array.from(state.selectedCuisines),
      restaurantWalletOnly: state.restaurantWalletOnly,
      monthlySpend: state.monthlySpend,
    };
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  } catch (_) { /* quota or disabled — silently skip */ }
}

function restoreStateFromLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    const p = JSON.parse(raw);
    if (!p || p.v !== 1) return;
    if (typeof p.selectedCity === "string") state.selectedCity = p.selectedCity;
    if (Number.isFinite(p.orderValue)) state.orderValue = p.orderValue;
    if (Number.isFinite(p.outingsPerWeek)) state.outingsPerWeek = Math.max(1, Math.min(4, p.outingsPerWeek));
    if (Array.isArray(p.selectedDays)) state.selectedDays = new Set(p.selectedDays.filter((n) => n >= 0 && n <= 6));
    if (Array.isArray(p.selectedCardTypes)) state.selectedCardTypes = new Set(p.selectedCardTypes.filter(Boolean));
    if (Array.isArray(p.selectedBanks)) state.selectedBanks = new Set(p.selectedBanks.filter(Boolean));
    if (Array.isArray(p.selectedRestaurants)) state.selectedRestaurants = new Set(p.selectedRestaurants.filter(Boolean));
    if (Array.isArray(p.selectedCards)) state.selectedCards = new Set(p.selectedCards.filter(Boolean));
    if (typeof p.useEligibility === "boolean") state.useEligibility = p.useEligibility;
    if (p.monthlySalary === null || Number.isFinite(p.monthlySalary)) state.monthlySalary = p.monthlySalary;
    if (p.accountBalance === null || Number.isFinite(p.accountBalance)) state.accountBalance = p.accountBalance;
    if (typeof p.viewMode === "string" && ["cards", "restaurants", "my-wallet", "wallet", "overall"].includes(p.viewMode)) state.viewMode = p.viewMode;
    if (p.monthlySpend && typeof p.monthlySpend === "object") state.monthlySpend = sanitizeMonthlySpend(p.monthlySpend);
    if (Array.isArray(p.ownedCards)) state.ownedCards = new Set(p.ownedCards.filter(Boolean));
    if (Number.isFinite(p.walletSize) && p.walletSize >= 2 && p.walletSize <= 4) state.walletSize = p.walletSize;
    if (typeof p.walletBuildOnOwned === "boolean") state.walletBuildOnOwned = p.walletBuildOnOwned;
    if (p.walletMaxFee === null || Number.isFinite(p.walletMaxFee)) state.walletMaxFee = p.walletMaxFee;
    if (typeof p.walletNoSameBank === "boolean") state.walletNoSameBank = p.walletNoSameBank;
    if (typeof p.walletMixedTypes === "boolean") state.walletMixedTypes = p.walletMixedTypes;
    if (typeof p.walletObjective === "string" && ["savings", "coverage", "roi"].includes(p.walletObjective)) state.walletObjective = p.walletObjective;
    if (Array.isArray(p.walletMustInclude)) state.walletMustInclude = new Set(p.walletMustInclude.filter(Boolean));
    if (typeof p.restaurantWalletOnly === "boolean") state.restaurantWalletOnly = p.restaurantWalletOnly;
    if (typeof p.walletAdvancedOpen === "boolean") state.walletAdvancedOpen = p.walletAdvancedOpen;
    if (Array.isArray(p.favoriteRestaurants)) state.favoriteRestaurants = new Set(p.favoriteRestaurants.filter(Boolean));
    if (Array.isArray(p.selectedCuisines)) state.selectedCuisines = new Set(p.selectedCuisines.filter(Boolean));
    if (p.categoryPrefs && typeof p.categoryPrefs === "object") {
      const prefs = {};
      for (const [slug, v] of Object.entries(p.categoryPrefs)) {
        if (!v || typeof v !== "object") continue;
        const orderValue = Number(v.orderValue);
        const outingsPerWeek = Number(v.outingsPerWeek);
        if (Number.isFinite(orderValue) && orderValue > 0 && Number.isFinite(outingsPerWeek) && outingsPerWeek > 0) {
          prefs[slug] = { orderValue, outingsPerWeek };
        }
      }
      state.categoryPrefs = prefs;
    }
    state.categoryPrefs.food = { orderValue: state.orderValue, outingsPerWeek: state.outingsPerWeek };
    if (typeof p.category === "string" && p.category) state.category = p.category;
  } catch (_) { /* corrupted payload — ignore */ }
}

function clearLocalState() {
  try { localStorage.removeItem(LS_KEY); } catch (_) {}
}

// Slugify the same way the SEO generator does so a `?restaurant=california-pizza`
// link from /restaurants/california-pizza/ can resolve back to its canonical
// "California Pizza" name in the offers dataset.
function slugifyForLookup(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function resolveSlugToRestaurantName(slug) {
  if (!slug || !state.data?.offers) return null;
  const target = slugifyForLookup(slug);
  for (const o of state.data.offers) {
    if (slugifyForLookup(o.restaurant) === target) return o.restaurant;
  }
  return null;
}

function resolveSlugToBankName(slug) {
  if (!slug || !state.data?.offers) return null;
  const target = slugifyForLookup(slug);
  for (const o of state.data.offers) {
    if (slugifyForLookup(o.bank) === target) return o.bank;
  }
  return null;
}

function restoreStateFromUrl() {
  const params = new URLSearchParams(location.search);
  if (!params.toString()) return;
  // A shared link describes one category; without `cat` it's a food link
  // (every link minted before categories existed), whatever was last used.
  const APP_STATE_KEYS = ["city", "bill", "outings", "days", "types", "banks", "rests", "cards", "cuisines", "view", "owned", "restaurant", "bank", "spend"];
  if (!params.has("cat") && APP_STATE_KEYS.some((k) => params.has(k))) state.category = "food";
  // Human-friendly slug pre-filters from the SEO landing pages. These map
  // back to the canonical restaurant/bank name and slot into the existing
  // multi-select filters so the homepage opens already filtered.
  if (params.has("restaurant")) {
    const name = resolveSlugToRestaurantName(params.get("restaurant"));
    if (name) {
      state.selectedRestaurants = new Set([name]);
      state.selectedCity = "all";
    }
  }
  if (params.has("bank")) {
    const name = resolveSlugToBankName(params.get("bank"));
    if (name) state.selectedBanks = new Set([name]);
  }
  if (params.has("city")) state.selectedCity = normalizeCityValue(params.get("city"));
  if (params.has("cat")) {
    // Visibility is checked in activateRestoredCategory() (manifest-aware).
    state.category = String(params.get("cat") || "food").trim().toLowerCase() || "food";
  }
  if (params.has("bill")) {
    state.orderValue = Number(params.get("bill")) || getCategoryDefaultBill(getCategoryMeta(state.category) || undefined);
    if (!isFoodCategory()) state._urlCategoryBill = true;
  }
  if (params.has("outings")) state.outingsPerWeek = Math.max(1, Math.min(4, Number(params.get("outings")) || 2));
  if (params.has("visits") && !isFoodCategory()) {
    const v = Number(params.get("visits"));
    if (Number.isFinite(v) && v > 0) {
      state.outingsPerWeek = visitsPerMonthToWeekly(Math.min(30, v));
      state._urlCategoryFreq = true;
    }
  }
  if (params.has("days")) state.selectedDays = new Set(params.get("days").split(",").map(Number).filter((n) => n >= 0 && n <= 6));
  if (params.has("types")) state.selectedCardTypes = new Set(params.get("types").split(",").filter(Boolean));
  // Parse repeated params for multi-select (banks, rests, cards)
  // Also support legacy pipe-separated format for backwards compatibility
  if (params.has("banks")) {
    const banks = params.getAll("banks");
    if (banks.length > 0 && banks[0].includes("|")) {
      state.selectedBanks = new Set(banks[0].split("|").filter(Boolean));
    } else {
      state.selectedBanks = new Set(banks.filter(Boolean));
    }
  }
  if (params.has("rests")) {
    const rests = params.getAll("rests");
    if (rests.length > 0 && rests[0].includes("|")) {
      state.selectedRestaurants = new Set(rests[0].split("|").filter(Boolean));
    } else {
      state.selectedRestaurants = new Set(rests.filter(Boolean));
    }
  }
  if (params.has("cards")) {
    const cards = params.getAll("cards");
    if (cards.length > 0 && cards[0].includes("|")) {
      state.selectedCards = new Set(cards[0].split("|").filter(Boolean));
    } else {
      state.selectedCards = new Set(cards.filter(Boolean));
    }
  }
  if (params.has("cuisines")) {
    state.selectedCuisines = new Set(params.getAll("cuisines").filter(Boolean));
  }
  if (params.get("elig") === "1") {
    state.useEligibility = true;
    if (params.has("salary")) state.monthlySalary = Number(params.get("salary"));
    if (params.has("balance")) state.accountBalance = Number(params.get("balance"));
  }
  if (params.has("view")) {
    const v = String(params.get("view") || "").trim();
    // Legacy alias: view=next-card → my-wallet (the feature was renamed)
    if (v === "next-card" || v === "my-wallet") state.viewMode = "my-wallet";
    else if (v === "restaurants" || v === "cards" || v === "wallet" || v === "overall") state.viewMode = v;
    else if (v === "bnpl") { location.replace("/bnpl/"); }
  }
  if (params.has("spend")) {
    // spend=food:40000,health:8000 — the Overall view's spending profile.
    const parsed = {};
    String(params.get("spend") || "").split(",").forEach((pair) => {
      const [slug, amount] = pair.split(":");
      if (slug) parsed[slug.trim()] = Number(amount);
    });
    state.monthlySpend = sanitizeMonthlySpend(parsed);
    state._urlSpend = true;
  }
  if (params.has("owned")) {
    state.ownedCards = new Set(params.getAll("owned").filter(Boolean));
  }
  if (params.has("k")) {
    const k = Number(params.get("k"));
    if (k >= 2 && k <= 4) state.walletSize = k;
  }
  if (params.get("build") === "owned") state.walletBuildOnOwned = true;
  if (params.has("maxfee")) {
    const n = Number(params.get("maxfee"));
    if (Number.isFinite(n) && n >= 0) state.walletMaxFee = n;
  }
  if (params.get("nobank") === "1") state.walletNoSameBank = true;
  if (params.get("mix") === "1") state.walletMixedTypes = true;
  if (params.has("obj")) {
    const o = String(params.get("obj") || "").trim();
    if (o === "coverage" || o === "roi" || o === "savings") state.walletObjective = o;
  }
  if (params.has("must")) {
    state.walletMustInclude = new Set(params.getAll("must").filter(Boolean));
  }
}

function syncDomToState() {
  syncBillControls();
  const bankSearch = document.getElementById("bank-search");
  if (bankSearch) bankSearch.value = "";
  const restSearch = document.getElementById("restaurant-search");
  if (restSearch) restSearch.value = "";
  const cardSearch = document.getElementById("card-search");
  if (cardSearch) cardSearch.value = "";
  const clearEligBtn = document.getElementById("clear-eligibility");
  if (clearEligBtn) clearEligBtn.style.display = state.useEligibility ? "" : "none";
  const monthlySalary = document.getElementById("monthly-salary");
  if (monthlySalary) monthlySalary.value = state.monthlySalary ?? "";
  const accountBalance = document.getElementById("account-balance");
  if (accountBalance) accountBalance.value = state.accountBalance ?? "";
}

/* ── DEBUG HOOK ──
   Expose state + compute fns on window for tests and devtools. No prod risk:
   nothing reads from this in app code paths; classic-script let/const bindings
   already share the same script scope so this is just discoverability sugar. */
if (typeof window !== "undefined") {
  window.__app = {
    get state() { return state; },
    computeRecommendations,
    computeNextCardRecommendations,
    computeWalletRecommendations,
    getOfferSavingValue,
    // Test affordance: wallet / next-card / per-offer computations are not
    // precomputed in the summary and require the lazily-loaded raw offers.
    // Exposing the memoized loader lets the e2e suite force a raw-offers load
    // (it's otherwise only triggered by real UI interactions).
    ensureRawOffers,
  };
}

/* ── ANALYTICS ──
   Fan-out wrapper. Sends each event to both Google Analytics (gtag) and
   PostHog (posthog.capture). Both globals are init'd from their own
   *-init.js scripts in index.html; if either isn't loaded yet, that branch
   is silently skipped. GA gets the "konsa_" prefix it already expects;
   PostHog gets the bare name (cleaner in their UI). Pass small JSON-safe
   values only. */

// Fires a `search_submit` event 600ms after the user stops typing, so a
// query of "hbl plat" produces one event, not eight. getResults is a
// callback so we capture the live filtered result count at fire-time.
function attachSearchTracking(input, surface, getResults, extra) {
  if (!input) return;
  let timer = null;
  input.addEventListener("input", () => {
    if (timer) clearTimeout(timer);
    const value = input.value.trim();
    if (!value) return;
    timer = setTimeout(() => {
      const count = typeof getResults === "function" ? getResults() : null;
      trackEvent("search_submit", {
        surface,
        query: value.toLowerCase(),
        result_count: count,
        ...(extra || {}),
      });
    }, 600);
  });
}

function trackEvent(name, params) {
  const p = params || {};
  try {
    const gtag = /** @type {any} */ (window).gtag;
    if (typeof gtag === "function") gtag("event", `konsa_${name}`, p);
  } catch (_) { /* no-op */ }
  try {
    const ph = /** @type {any} */ (window).posthog;
    if (ph && typeof ph.capture === "function") ph.capture(name, p);
  } catch (_) { /* no-op */ }
}

// Delegated apply-link tracking — covers every .btn-apply rendered anywhere,
// including inside modals and dynamically-rendered cards. Capture before the
// link navigates so the event has time to dispatch.
document.addEventListener("click", (e) => {
  const target = /** @type {HTMLElement | null} */ (e.target);
  const link = target && target.closest && target.closest("a.btn-apply, a.btn-feat-apply");
  if (!link) return;
  const href = /** @type {HTMLAnchorElement} */ (link).href || "";
  try {
    const host = new URL(href).hostname;
    trackEvent("apply_click", { host });
  } catch (_) { /* no-op */ }
}, true);

/* ── SERVICE WORKER ──
   Registered after first paint so it never blocks initial render. Skipped on
   localhost (always-fresh during dev) and when the SW API isn't available. */
function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  const host = location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return;
  // Defer to idle time so registration doesn't compete with the first render.
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
  idle(() => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.warn("[sw] registration failed", err);
    });
  });

  // When a new SW activates and claims this client, the SW posts
  // SW_ACTIVATED. Reload exactly once so the user instantly sees the new
  // shell — without it, Cmd+R serves yesterday's bundle for one more
  // reload because the SWR cache still has the old copy at takeover time.
  // We guard with sessionStorage so a misbehaving SW can't refresh-loop.
  let alreadyReloaded = false;
  try { alreadyReloaded = sessionStorage.getItem("sw-reloaded-once") === "1"; } catch {}
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type !== "SW_ACTIVATED") return;
    if (alreadyReloaded) return;
    try { sessionStorage.setItem("sw-reloaded-once", "1"); } catch {}
    location.reload();
  });
}

function renderCuisinePills() {
  const section = document.getElementById("cuisine-section");
  const container = document.getElementById("cuisine-pills");
  const countEl = document.getElementById("selected-cuisine-count");
  const searchInput = /** @type {HTMLInputElement|null} */ (document.getElementById("cuisine-search"));
  if (!section || !container) return;
  const all = getAllCuisinesFromEnrichment();
  if (!all.length) {
    section.style.display = "none";
    return;
  }
  section.style.display = "";

  // Sync the input value to state (handles initial render + filter changes
  // from other paths) without clobbering the cursor when the user is typing.
  if (searchInput && searchInput.value !== state.cuisineSearchTerm) {
    searchInput.value = state.cuisineSearchTerm;
  }

  const term = (state.cuisineSearchTerm || "").trim().toLowerCase();
  // When the user is searching, show every cuisine that matches — gives
  // access to the long-tail (~65 total) without overwhelming the default
  // view. When not searching, keep the top 12 + already-selected chips.
  let visible;
  if (term) {
    visible = all.filter((c) => c.name.toLowerCase().includes(term));
  } else {
    const topN = 12;
    const top = all.slice(0, topN);
    const selectedExtras = [...state.selectedCuisines].filter((c) => !top.some((t) => t.name === c));
    visible = [
      ...top,
      ...selectedExtras.map((c) => ({ name: c, count: all.find((t) => t.name === c)?.count || 0 })),
    ];
  }

  if (visible.length === 0) {
    container.innerHTML = `<div style="font-size:12px;color:var(--muted);padding:6px 2px">No ${escapeHtml(tagNoun(true))} match "${escapeHtml(term)}".</div>`;
  } else {
    container.innerHTML = visible.map((c) => {
      const active = state.selectedCuisines.has(c.name);
      return `<button type="button" class="s-cuisine-chip${active ? " active" : ""}" data-cuisine="${escapeAttr(c.name)}">${escapeHtml(c.name)}${c.count ? `<span class="s-cuisine-chip-count">${c.count}</span>` : ""}</button>`;
    }).join("");
    container.querySelectorAll(".s-cuisine-chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        const name = btn.dataset.cuisine;
        if (!name) return;
        if (state.selectedCuisines.has(name)) state.selectedCuisines.delete(name);
        else state.selectedCuisines.add(name);
        // Render synchronously so the chip + `?cuisines=` URL param land
        // immediately; the list re-render self-defers until raw offers load.
        render();
      });
    });
  }

  if (searchInput && !searchInput.dataset.bound) {
    // Bind once; subsequent renders only sync the .value above.
    searchInput.dataset.bound = "1";
    searchInput.addEventListener("input", (e) => {
      state.cuisineSearchTerm = /** @type {HTMLInputElement} */ (e.target).value;
      renderCuisinePills();
    });
  }

  if (countEl) {
    if (state.selectedCuisines.size > 0) {
      countEl.style.display = "";
      countEl.textContent = `${state.selectedCuisines.size}`;
    } else {
      countEl.style.display = "none";
    }
  }
}

function renderBranchCard(branch) {
  if (!branch) return "";
  const name = branch.name || branch.address || "Branch";
  const addr = branch.address || "";
  const todayHours = (() => {
    const hours = branch.openingHours;
    if (!Array.isArray(hours) || !hours.length) return null;
    const todayName = _DAY_INDEX_TO_NAME[new Date().getDay()];
    return hours.find((h) => h.day === todayName) || null;
  })();
  const userLoc = getUserLocation();
  const distance = (userLoc && typeof branch.lat === "number" && typeof branch.lng === "number")
    ? haversineKm(userLoc.lat, userLoc.lng, branch.lat, branch.lng)
    : null;
  const mapsHref = (typeof branch.lat === "number" && typeof branch.lng === "number")
    ? `https://www.google.com/maps?q=${branch.lat},${branch.lng}`
    : (branch.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branch.address)}` : null);
  const phoneHref = branch.telephone ? `tel:${String(branch.telephone).replace(/[^0-9+]/g, "")}` : null;
  return `
    <div class="rd-branch">
      <div class="rd-branch-name">${escapeHtml(name)}</div>
      ${addr ? `<div class="rd-branch-addr">${escapeHtml(addr)}</div>` : ""}
      <div class="rd-branch-meta">
        ${todayHours ? `<span class="rd-branch-today">Today ${escapeHtml(todayHours.opens)}–${escapeHtml(todayHours.closes)}</span>` : ""}
        ${distance !== null ? `<span>${escapeHtml(formatDistanceKm(distance))} away</span>` : ""}
        ${mapsHref ? `<a href="${escapeAttr(mapsHref)}" target="_blank" rel="noopener noreferrer">Directions</a>` : ""}
        ${phoneHref ? `<a href="${escapeAttr(phoneHref)}">Call</a>` : ""}
      </div>
    </div>
  `;
}

/* ── RESTAURANT ENRICHMENT HELPERS ──
   Backed by data/offers-restaurants.json, which is loaded in parallel with
   the per-city offer files at boot. Every helper here is defensive: if
   enrichment is missing for a restaurant we degrade gracefully (no logo,
   no "open now", no socials) — never throw or block the UI. */

function getRestaurantEnrichment(name) {
  const map = state.data?.restaurants;
  if (!map || !name) return null;
  return map[name] || null;
}

function renderRestaurantLogo(name, className) {
  // We intentionally don't show restaurant photos — Peekaboo serves them
  // from their CDN and we don't have a license to redistribute them.
  // Show the initial as a stable, license-free fallback.
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  const cls = className || "restaurant-logo";
  return `<div class="${escapeAttr(cls)}"><span class="restaurant-logo-fallback">${escapeHtml(initial)}</span></div>`;
}

function getBranchesForRestaurant(name, city) {
  const enr = getRestaurantEnrichment(name);
  if (!enr || !enr.branchesByCity) return [];
  if (city) return enr.branchesByCity[city] || [];
  // No city filter: return all branches across cities
  return Object.values(enr.branchesByCity).flat();
}

const _DAY_INDEX_TO_NAME = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function _hhmmToMinutes(hhmm) {
  if (typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = +m[1], mm = +m[2];
  if (h < 0 || h > 24 || mm < 0 || mm >= 60) return null;
  return h * 60 + mm;
}

/**
 * Return one of: "open", "closed", null (unknown — no hours data).
 * A branch is "open" if any branch in the city is currently within its
 * opening window for today (handles past-midnight ranges like 16:30-01:00).
 */
function getRestaurantOpenStatus(name, city, now) {
  const branches = getBranchesForRestaurant(name, city);
  if (!branches.length) return null;
  const d = now || new Date();
  const todayName = _DAY_INDEX_TO_NAME[d.getDay()];
  const yesterdayName = _DAY_INDEX_TO_NAME[(d.getDay() + 6) % 7];
  const nowMin = d.getHours() * 60 + d.getMinutes();
  let sawHours = false;
  for (const b of branches) {
    const hours = b.openingHours;
    if (!Array.isArray(hours) || !hours.length) continue;
    sawHours = true;
    for (const h of hours) {
      const opens = _hhmmToMinutes(h.opens);
      const closes = _hhmmToMinutes(h.closes);
      if (opens === null || closes === null) continue;
      if (h.day === todayName) {
        // Same-day window
        if (closes > opens && nowMin >= opens && nowMin < closes) return "open";
        // Past-midnight window: opens today, closes after midnight
        if (closes <= opens && nowMin >= opens) return "open";
      }
      if (h.day === yesterdayName && closes <= opens) {
        // Yesterday's past-midnight window may still be active
        if (nowMin < closes) return "open";
      }
    }
  }
  return sawHours ? "closed" : null;
}

function getAllCuisinesFromEnrichment() {
  const map = state.data?.restaurants;
  if (!map) return [];
  const counts = new Map();
  for (const v of Object.values(map)) {
    const list = v?.servesCuisine;
    if (!Array.isArray(list)) continue;
    for (const c of list) {
      if (typeof c !== "string" || !c.trim()) continue;
      counts.set(c, (counts.get(c) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, count]) => ({ name, count }));
}

function getRestaurantSocialLinks(name) {
  const enr = getRestaurantEnrichment(name);
  const social = enr?.social;
  if (!social || typeof social !== "object") return [];
  const links = [];
  const order = ["website", "facebook", "instagram", "android", "ios"];
  for (const k of order) {
    const url = social[k];
    if (typeof url === "string" && url.startsWith("http")) links.push({ kind: k, url });
  }
  return links;
}

const _SOCIAL_LABELS = {
  website: "Website",
  facebook: "Facebook",
  instagram: "Instagram",
  android: "Android app",
  ios: "iOS app",
};

function renderSocialLinks(name) {
  const links = getRestaurantSocialLinks(name);
  if (!links.length) return "";
  return `
    <div class="rd-social-row">
      ${links.map((l) => `<a class="rd-social-link" href="${escapeAttr(l.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(_SOCIAL_LABELS[l.kind] || l.kind)}</a>`).join("")}
    </div>
  `;
}

/* ── DISTANCE / GEOLOCATION ── */
const USER_LOCATION_KEY = "konsacard_user_location_v1";

function _readSavedLocation() {
  try {
    const raw = localStorage.getItem(USER_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.lat === "number" && typeof parsed?.lng === "number") return parsed;
  } catch {}
  return null;
}

function _saveLocation(loc) {
  try { localStorage.setItem(USER_LOCATION_KEY, JSON.stringify(loc)); } catch {}
}

function getUserLocation() {
  return state.userLocation || _readSavedLocation();
}

async function requestUserLocation() {
  if (!("geolocation" in navigator)) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          ts: Date.now(),
        };
        state.userLocation = loc;
        _saveLocation(loc);
        resolve(loc);
      },
      () => resolve(null),
      { timeout: 10000, maximumAge: 5 * 60 * 1000 },
    );
  });
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function getNearestBranchDistanceKm(name, city, userLoc) {
  if (!userLoc) return null;
  const branches = getBranchesForRestaurant(name, city);
  let best = null;
  for (const b of branches) {
    if (typeof b.lat !== "number" || typeof b.lng !== "number") continue;
    const km = haversineKm(userLoc.lat, userLoc.lng, b.lat, b.lng);
    if (best === null || km < best) best = km;
  }
  return best;
}

function formatDistanceKm(km) {
  if (km === null || km === undefined) return "";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/* ── BOOT ── */
init().then(() => {
  renderNavCityTabs();
  updateCityChip();
  // Check first visit and show quick quiz
  checkFirstVisitAndShowQuickQuiz();
  registerServiceWorker();
}).catch((error) => {
  console.error(error);
  // Report to Sentry if loaded
  try { /** @type {any} */(window).Sentry?.captureException?.(error); } catch {}

  const isSchema = error?.name === "OffersSchemaError";
  const title = isSchema ? "App data is malformed" : "Could not load app data";
  const detail = isSchema
    ? "The offers payload has a shape problem. This usually means the data pipeline emitted an incompatible build. Try refreshing in a minute, or check the browser console for details."
    : "Check that data/offers-index.json (or offers.json) exists and is being served over HTTP.";

  const grid = document.getElementById("results-grid");
  if (grid) grid.innerHTML = `
    <div style="padding:40px 20px;text-align:center;background:var(--surface);border-radius:var(--r);border:1px solid var(--line);">
      <div style="font-weight:800;font-size:18px;color:var(--ink);margin-bottom:8px">${escapeHtml(title)}</div>
      <div style="font-size:13px;color:var(--muted);max-width:520px;margin:0 auto;line-height:1.5">${escapeHtml(detail)}</div>
    </div>
  `;
});
